/*
  MYD: PDP / product-form modules split from global.js for faster non-product pages.
  Requires assets/global.js first (SliderComponent, theme helpers).
*/
    class ProductGallery extends SliderComponent {
      constructor() {
        super();
        this.onDotClick = this.onDotClick.bind(this);
        this.pauseAutoplay = this.pauseAutoplay.bind(this);
        this.advanceAutoplay = this.advanceAutoplay.bind(this);
        this.syncAutoplayForViewport = this.syncAutoplayForViewport.bind(this);
        this.autoplayDelay = 10000;
        this.autoplayPaused = false;
        this._autoplayTimer = null;
      }
  
      init() {
        super.init();
  
        const gallerySliderButtons = this.querySelector('.slider-buttons');
        if (gallerySliderButtons && this.querySelector('[data-gang-option]')) {
          if (this.querySelectorAll('.product__media-item.gang__active').length < 2) {
            gallerySliderButtons.classList.add('hidden');
          } else {
            gallerySliderButtons.classList.remove('hidden');
          }
        }

        this.syncDots();
        this.initAutoplay();
  
        this.cursor = this.querySelector('.gallery-cursor');
        if (!this.cursor) return;
  
        const images = this.querySelectorAll('.product__modal-opener--image');
        images.forEach((image) => image.addEventListener('mousemove', this.onMoveHandler.bind(this)));
        images.forEach((image) => image.addEventListener('mouseenter', this.onEnterHandler.bind(this)));
        images.forEach((image) => image.addEventListener('mouseleave', this.onLeaveHandler.bind(this)));
      }

      initPages() {
        super.initPages();
        this.syncDots();
        this.syncAutoplayForViewport();
      }

      initAutoplay() {
        this._autoplayMq = window.matchMedia('(max-width: 749px)');
        if (this._autoplayMq.addEventListener) {
          this._autoplayMq.addEventListener('change', this.syncAutoplayForViewport);
        } else if (this._autoplayMq.addListener) {
          this._autoplayMq.addListener(this.syncAutoplayForViewport);
        }

        this.addEventListener('touchstart', this.pauseAutoplay, { passive: true });
        this.addEventListener('pointerdown', this.pauseAutoplay);

        document.addEventListener('visibilitychange', this.syncAutoplayForViewport);
        this.syncAutoplayForViewport();
      }

      getAutoplaySlides() {
        if (this.sliderItemsToShow?.length) return this.sliderItemsToShow;
        return Array.from(this.sliderItems || []).filter((el) => el.clientWidth > 0);
      }

      syncAutoplayForViewport() {
        if (this.autoplayPaused || document.hidden || !this._autoplayMq?.matches) {
          this.clearAutoplayTimer();
          return;
        }
        this.scheduleAutoplay();
      }

      scheduleAutoplay() {
        this.clearAutoplayTimer();
        if (this.autoplayPaused || document.hidden || !this._autoplayMq?.matches) return;
        if (this.getAutoplaySlides().length < 2) return;

        this._autoplayTimer = setTimeout(this.advanceAutoplay, this.autoplayDelay);
      }

      clearAutoplayTimer() {
        if (!this._autoplayTimer) return;
        clearTimeout(this._autoplayTimer);
        this._autoplayTimer = null;
      }

      pauseAutoplay() {
        if (this.autoplayPaused) return;
        this.autoplayPaused = true;
        this.clearAutoplayTimer();
      }

      advanceAutoplay() {
        this._autoplayTimer = null;
        if (this.autoplayPaused || document.hidden || !this._autoplayMq?.matches) return;

        const items = this.getAutoplaySlides();
        if (items.length < 2) return;

        const currentIndex = Math.max(0, (this.currentPage || 1) - 1);
        const nextIndex = (currentIndex + 1) % items.length;
        this.goToSlide(nextIndex);
        this.scheduleAutoplay();
      }
  
      onMoveHandler(event) {
        this.cursor.style.left = `${event.clientX - 32}px`;
        this.cursor.style.top = `${event.clientY - 32}px`;
      }
  
      onEnterHandler() {
        this.cursor.classList.add('show');
      }
  
      onLeaveHandler() {
        this.cursor.classList.remove('show');
      }

      syncDots() {
        const buttons = this.querySelector('.slider-buttons');
        if (!buttons) return;

        let dots = buttons.querySelector('.product-gallery__dots');
        const slides = this.sliderItemsToShow?.length
          ? this.sliderItemsToShow
          : Array.from(this.sliderItems || []).filter((el) => el.clientWidth > 0);

        if (slides.length < 2) {
          if (dots) dots.remove();
          buttons.classList.add('small-hide');
          return;
        }

        buttons.classList.remove('small-hide', 'hidden');

        if (!dots) {
          dots = document.createElement('ul');
          dots.className = 'product-gallery__dots';
          dots.setAttribute('role', 'tablist');
          buttons.appendChild(dots);
        }

        const needed = slides.length;
        const existing = dots.querySelectorAll('.product-gallery__dot');

        if (existing.length !== needed) {
          dots.innerHTML = slides
            .map(
              (_, index) => `
              <li class="product-gallery__dots-item" role="presentation">
                <button
                  type="button"
                  class="product-gallery__dot${index === 0 ? ' is-active' : ''}"
                  role="tab"
                  aria-label="Slide ${index + 1}"
                  aria-selected="${index === 0 ? 'true' : 'false'}"
                  data-index="${index}"
                ></button>
              </li>`
            )
            .join('');
        }

        dots.querySelectorAll('.product-gallery__dot').forEach((dot) => {
          dot.removeEventListener('click', this.onDotClick);
          dot.addEventListener('click', this.onDotClick);
        });

        this.updateDots();
      }

      updateDots() {
        const dots = this.querySelectorAll('.product-gallery__dot');
        if (!dots.length) return;
        const activeIndex = Math.max(0, (this.currentPage || 1) - 1);
        dots.forEach((dot, index) => {
          const isActive = index === activeIndex;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
      }

      onDotClick(event) {
        const index = Number(event.currentTarget.dataset.index);
        if (Number.isNaN(index)) return;
        this.pauseAutoplay();
        this.goToSlide(index);
      }

      goToSlide(index) {
        const items = this.sliderItemsToShow?.length
          ? this.sliderItemsToShow
          : Array.from(this.sliderItems || []);
        const item = items[index];
        if (!item || !this.slider) return;
        this.slider.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
      }
  
      update() {
        super.update();
        this.updateDots();
  
        if (this.currentPageElement && this.pageTotalElement) {
          const currentElement = this.sliderItemsToShow[this.currentPage - 1];
          if (currentElement) {
            this.slider.style.setProperty('--force-image-ratio-percent', currentElement.querySelector('.media').style.getPropertyValue('--image-ratio-percent'));
          }
        }
      }

      resetPages() {
        super.resetPages();
        this.syncDots();
        this.syncAutoplayForViewport();
      }
  
      goToFirstSlide() {
        this.slider.scrollTo({
          left: 0
        });
      }
    }
    customElements.define('product-gallery', ProductGallery);

    window.MYDProductLightbox = {
      minZoom: 1,
      maxZoom: 4,
      step: 0.5,
      zoom: 1,
      index: 0,
      images: [],
      dragging: false,
      didDrag: false,
      panX: 0,
      panY: 0,
      startX: 0,
      startY: 0,
      originX: 0,
      originY: 0,
      pointers: new Map(),
      pinchStartDist: 0,
      pinchStartZoom: 1,
      lastTapAt: 0,
      lastTapX: 0,
      lastTapY: 0,

      hiRes(src) {
        if (!src) return '';
        try {
          const url = new URL(src, window.location.origin);
          url.searchParams.set('width', '2000');
          return url.toString();
        } catch (err) {
          if (/([?&])width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, '$1width=2000');
          return src.includes('?') ? `${src}&width=2000` : `${src}?width=2000`;
        }
      },

      collect(gallery) {
        const items = [...gallery.querySelectorAll('.product__media-item')].filter((item) => {
          if (item.classList.contains('product__media-item--variant') && getComputedStyle(item).display === 'none') {
            return false;
          }
          if (item.hasAttribute('data-gang-option') && !item.classList.contains('gang__active') && !item.classList.contains('is-active')) {
            const anyGang = gallery.querySelector('[data-gang-option].gang__active');
            if (anyGang) return false;
          }
          return !!item.querySelector('img');
        });
        return items
          .map((item) => {
            const img = item.querySelector('img');
            const full =
              img?.getAttribute('data-full-src') ||
              img?.currentSrc ||
              img?.getAttribute('src') ||
              '';
            return {
              src: this.hiRes(full),
              alt: img?.getAttribute('alt') || '',
              id: item.getAttribute('data-media-id') || ''
            };
          })
          .filter((entry) => entry.src);
      },

      ensure() {
        let box = document.querySelector('.myd-product-lightbox');
        if (box) return box;
        box = document.createElement('div');
        box.className = 'myd-product-lightbox';
        box.setAttribute('role', 'dialog');
        box.setAttribute('aria-modal', 'true');
        box.setAttribute('aria-label', 'Product image');
        const zoomOutIcon =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false"><path d="M1.5 6h9" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" fill="none"/></svg>';
        const zoomInIcon =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false"><path d="M6 1.5v9M1.5 6h9" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" fill="none"/></svg>';
        const prevIcon =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false"><path d="M7.5 2L3.5 6l4 4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';
        const nextIcon =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false"><path d="M4.5 2l4 4-4 4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';

        box.innerHTML = `
          <div class="myd-product-lightbox__toolbar">
            <button type="button" class="myd-product-lightbox__btn myd-product-lightbox__zoom-out" aria-label="Zoom out">${zoomOutIcon}</button>
            <button type="button" class="myd-product-lightbox__btn myd-product-lightbox__zoom-in" aria-label="Zoom in">${zoomInIcon}</button>
            <button type="button" class="myd-product-lightbox__btn myd-product-lightbox__close myd-popup-close" aria-label="Close">${window.MYD_POPUP_CLOSE_ICON}</button>
          </div>
          <button type="button" class="myd-product-lightbox__nav myd-product-lightbox__nav--prev" aria-label="Previous">${prevIcon}</button>
          <figure class="myd-product-lightbox__stage">
            <div class="myd-product-lightbox__loader" aria-hidden="true"></div>
            <img class="myd-product-lightbox__img" alt="" draggable="false">
          </figure>
          <button type="button" class="myd-product-lightbox__nav myd-product-lightbox__nav--next" aria-label="Next">${nextIcon}</button>
          <div class="myd-product-lightbox__counter" aria-live="polite"></div>
        `;
        document.body.appendChild(box);

        const img = box.querySelector('.myd-product-lightbox__img');
        const stage = box.querySelector('.myd-product-lightbox__stage');

        box.querySelector('.myd-product-lightbox__close').addEventListener('click', (event) => {
          event.stopPropagation();
          this.close();
        });
        box.querySelector('.myd-product-lightbox__zoom-in').addEventListener('click', (event) => {
          event.stopPropagation();
          this.setZoom(this.zoom + this.step);
        });
        box.querySelector('.myd-product-lightbox__zoom-out').addEventListener('click', (event) => {
          event.stopPropagation();
          this.setZoom(this.zoom - this.step);
        });
        box.querySelector('.myd-product-lightbox__nav--prev').addEventListener('click', (event) => {
          event.stopPropagation();
          this.show(this.index - 1);
        });
        box.querySelector('.myd-product-lightbox__nav--next').addEventListener('click', (event) => {
          event.stopPropagation();
          this.show(this.index + 1);
        });

        box.addEventListener('click', (event) => {
          if (this.didDrag || this.zoom > 1) {
            this.didDrag = false;
            return;
          }
          if (event.target === box || event.target === stage) this.close();
        });

        stage.addEventListener(
          'wheel',
          (event) => {
            if (!box.classList.contains('is-open')) return;
            event.preventDefault();
            this.setZoom(this.zoom + (event.deltaY < 0 ? this.step : -this.step));
          },
          { passive: false }
        );

        const pointerDistance = () => {
          const pts = [...this.pointers.values()];
          if (pts.length < 2) return 0;
          return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        };

        const onPointerDown = (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
          stage.setPointerCapture?.(event.pointerId);
          this.didDrag = false;

          if (this.pointers.size === 2) {
            this.dragging = false;
            this.pinchStartDist = pointerDistance() || 1;
            this.pinchStartZoom = this.zoom;
            img.classList.add('is-dragging');
            return;
          }

          const now = Date.now();
          const dt = now - this.lastTapAt;
          const dist = Math.hypot(event.clientX - this.lastTapX, event.clientY - this.lastTapY);
          if (dt > 0 && dt < 280 && dist < 28) {
            event.preventDefault();
            this.setZoom(this.zoom > 1 ? 1 : 2.5);
            this.lastTapAt = 0;
            this.pointers.delete(event.pointerId);
            return;
          }
          this.lastTapAt = now;
          this.lastTapX = event.clientX;
          this.lastTapY = event.clientY;

          this.dragging = true;
          this.startX = event.clientX;
          this.startY = event.clientY;
          this.originX = this.panX;
          this.originY = this.panY;
          if (this.zoom > 1) img.classList.add('is-dragging');
        };

        const onPointerMove = (event) => {
          if (!this.pointers.has(event.pointerId)) return;
          this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

          if (this.pointers.size >= 2) {
            event.preventDefault();
            this.didDrag = true;
            const dist = pointerDistance();
            if (!dist || !this.pinchStartDist) return;
            const next = this.pinchStartZoom * (dist / this.pinchStartDist);
            this.setZoom(next, { snap: false });
            return;
          }

          if (!this.dragging) return;
          const dx = event.clientX - this.startX;
          const dy = event.clientY - this.startY;
          if (Math.hypot(dx, dy) > 8) this.didDrag = true;

          if (this.zoom <= 1) {
            if (Math.abs(dx) > 72 && Math.abs(dx) > Math.abs(dy) * 1.4) {
              this.dragging = false;
              this.didDrag = true;
              this.show(this.index + (dx < 0 ? 1 : -1));
              this.pointers.clear();
            }
            return;
          }

          event.preventDefault();
          this.panX = this.originX + dx;
          this.panY = this.originY + dy;
          this.applyTransform();
        };

        const onPointerUp = (event) => {
          this.pointers.delete(event.pointerId);
          if (this.pointers.size < 2) this.pinchStartDist = 0;
          if (this.pointers.size === 0) {
            this.dragging = false;
            img.classList.remove('is-dragging');
          } else if (this.pointers.size === 1) {
            const remaining = [...this.pointers.values()][0];
            this.dragging = this.zoom > 1;
            this.startX = remaining.x;
            this.startY = remaining.y;
            this.originX = this.panX;
            this.originY = this.panY;
          }
        };

        stage.addEventListener('pointerdown', onPointerDown);
        stage.addEventListener('pointermove', onPointerMove, { passive: false });
        stage.addEventListener('pointerup', onPointerUp);
        stage.addEventListener('pointercancel', onPointerUp);
        img.addEventListener('dragstart', (event) => event.preventDefault());
        stage.addEventListener('gesturestart', (event) => event.preventDefault());
        stage.addEventListener('gesturechange', (event) => event.preventDefault());

        this._onKey = (event) => {
          if (!box.classList.contains('is-open')) return;
          if (event.key === 'Escape') this.close();
          if (event.key === 'ArrowLeft') this.show(this.index - 1);
          if (event.key === 'ArrowRight') this.show(this.index + 1);
          if (event.key === '+' || event.key === '=') this.setZoom(this.zoom + this.step);
          if (event.key === '-') this.setZoom(this.zoom - this.step);
        };
        document.addEventListener('keydown', this._onKey);

        return box;
      },

      applyTransform() {
        const box = document.querySelector('.myd-product-lightbox');
        const img = box?.querySelector('.myd-product-lightbox__img');
        if (!img) return;
        img.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
        img.classList.toggle('is-zoomed', this.zoom > 1);
        box?.classList.toggle('is-zoomed', this.zoom > 1);
        box?.querySelector('.myd-product-lightbox__zoom-out')?.toggleAttribute('disabled', this.zoom <= this.minZoom + 0.01);
        box?.querySelector('.myd-product-lightbox__zoom-in')?.toggleAttribute('disabled', this.zoom >= this.maxZoom - 0.01);
      },

      setZoom(next, { snap = true } = {}) {
        const value = snap ? Math.round(next * 10) / 10 : next;
        this.zoom = Math.min(this.maxZoom, Math.max(this.minZoom, value));
        if (this.zoom <= this.minZoom + 0.01) {
          this.zoom = this.minZoom;
          this.panX = 0;
          this.panY = 0;
        }
        this.applyTransform();
      },

      show(index) {
        if (!this.images.length) return;
        this.index = (index + this.images.length) % this.images.length;
        const entry = this.images[this.index];
        const box = this.ensure();
        const img = box.querySelector('.myd-product-lightbox__img');
        const loader = box.querySelector('.myd-product-lightbox__loader');
        const counter = box.querySelector('.myd-product-lightbox__counter');
        this.zoom = 1;
        this.panX = 0;
        this.panY = 0;
        this.pointers.clear();
        box.classList.add('is-loading');
        img.style.opacity = '0';

        const finish = () => {
          box.classList.remove('is-loading');
          img.style.opacity = '1';
          this.applyTransform();
          this.prefetchNeighbors();
        };

        img.onload = finish;
        img.onerror = finish;
        img.alt = entry.alt || '';
        // Force reload even if browser serves from cache with same src
        if (img.src === entry.src) {
          if (img.complete && img.naturalWidth) finish();
          else img.src = entry.src;
        } else {
          img.src = entry.src;
        }
        if (loader) loader.setAttribute('aria-hidden', 'false');
        counter.textContent = `${this.index + 1} / ${this.images.length}`;
        box.querySelector('.myd-product-lightbox__nav--prev').hidden = this.images.length < 2;
        box.querySelector('.myd-product-lightbox__nav--next').hidden = this.images.length < 2;
        this.applyTransform();
      },

      prefetchNeighbors() {
        if (this.images.length < 2) return;
        [this.index - 1, this.index + 1].forEach((i) => {
          const entry = this.images[(i + this.images.length) % this.images.length];
          if (!entry?.src) return;
          const pre = new Image();
          pre.src = entry.src;
        });
      },

      open(images, startIndex) {
        if (!images?.length) return;
        this.images = images;
        const box = this.ensure();
        this.show(startIndex || 0);
        box.classList.add('is-open');
        document.documentElement.classList.add('myd-product-lightbox-open');
      },

      close() {
        const box = document.querySelector('.myd-product-lightbox');
        if (!box) return;
        box.classList.remove('is-open', 'is-zoomed');
        document.documentElement.classList.remove('myd-product-lightbox-open');
        this.zoom = 1;
        this.panX = 0;
        this.panY = 0;
        this.dragging = false;
        this.didDrag = false;
        this.pointers.clear();
        this.pinchStartDist = 0;
      },

      openFromTarget(target) {
        const item = target.closest?.('.product__media-item');
        const gallery =
          target.closest?.('product-gallery') ||
          target.closest?.('.product__media-list')?.closest('product-gallery') ||
          document.querySelector('product-gallery');
        if (!item || !gallery) return false;
        if (target.closest('thumbnail-slider, .thumbnail-list, .thumbnail')) return false;
        const images = this.collect(gallery);
        if (!images.length) return false;
        const mediaId = item.getAttribute('data-media-id');
        let index = images.findIndex((entry) => entry.id === mediaId);
        if (index < 0) {
          const img = item.querySelector('img');
          const src = this.hiRes(img?.getAttribute('data-full-src') || img?.currentSrc || img?.src || '');
          index = Math.max(0, images.findIndex((entry) => entry.src === src));
        }
        this.open(images, index);
        return true;
      },

      isCartDrawerOpen() {
        return (
          document.body.classList.contains('mini-cart--open') ||
          document.body.classList.contains('mini-cart--opening') ||
          document.body.classList.contains('mini-cart--closing') ||
          !!document.querySelector('cart-drawer details[open]')
        );
      },

      bind() {
        if (this._bound) return;
        this._bound = true;
        document.addEventListener(
          'click',
          (event) => {
            // Cart dimmer / drawer must win — never open lightbox through the overlay
            if (this.isCartDrawerOpen()) return;
            if (event.target?.closest?.('.cart-drawer__backdrop, cart-drawer, .cart-drawer')) return;
            const media = event.target.closest?.(
              'product-gallery .product__media-item .product__media, product-gallery .product__media-item img, product-gallery .product__modal-opener--image, product-gallery .product__media-toggle'
            );
            if (!media) return;
            if (event.target.closest('a[href], button:not(.product__media-toggle), .slider-button, .product-gallery__dot')) {
              return;
            }
            event.preventDefault();
            event.stopPropagation();
            this.openFromTarget(media);
          },
          true
        );
        document.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          const media = event.target.closest?.(
            'product-gallery .product__media[role="button"], product-gallery .product__modal-opener--image'
          );
          if (!media) return;
          event.preventDefault();
          this.openFromTarget(media);
        });
      }
    };

    const bootProductLightbox = () => window.MYDProductLightbox?.bind();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bootProductLightbox, { once: true });
    } else {
      bootProductLightbox();
    }

    // Wishlist confetti + guest login prompt live in myd-wishlist-celebrate.js
    // so collection / search / cart hearts work without loading this PDP file.


    class VariantSelects extends HTMLElement {
      constructor() {
        super();
  
        this.addEventListener('change', this.onVariantChange);
  
        this.updateOptions();
        this.updateMasterId();
        this.setAvailability();
        this.enableLookOptionGroups();
        this.ensureFirstCombinedListingSelected();
        this.lockCombinedListingParentUrl();
        // Hide Notify Me immediately — don't wait for child product fetch (prevents flash)
        this.primeCombinedListingNotifyHide();
        // Hydrate selected look first (fast path), then fill any missing thumbs idle
        this.hydrateCombinedListingInPlace();
        this.scheduleCombinedListingThumbnails();
        // Skip clearing when URL deep-links a specific variant (e.g. OOS → Notify Me)
        this.clearSizePreselection();
        // Hengam Notify Me must wait for an explicit OOS size selection (or URL variant)
        this.primeNotifyMeGate();
      }

      getUrlVariantId() {
        try {
          const id = new URLSearchParams(window.location.search).get('variant');
          return id ? String(id) : '';
        } catch (e) {
          return '';
        }
      }

      selectVariantFromUrl() {
        const variantId = this.getUrlVariantId();
        if (!variantId) return false;

        const variants = this.getVariantData() || [];
        const match = variants.find((v) => String(v.id) === variantId);
        if (!match) return false;

        // Apply option values from the URL variant onto size/color radios
        const optionValues = [match.option1, match.option2, match.option3].filter(Boolean);
        this.querySelectorAll('.variant-input-wrapper').forEach((group) => {
          if (this.isLookOptionGroup(group)) return;
          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            const value = input.dataset.optionValue || input.value;
            input.checked = optionValues.some((opt) => String(opt) === String(value));
          });
        });

        this.updateOptions();
        this.updateMasterId();
        this.updateVariantInput();
        this.toggleAddButton(
          !match.available,
          match.available ? '' : window.variantStrings.soldOut,
          true,
          true
        );
        return true;
      }

      clearSizePreselection() {
        // Keep deep-linked ?variant=… (including OOS) so Notify Me can show
        if (this.selectVariantFromUrl()) return;

        let cleared = false;
        this.querySelectorAll('.variant-input-wrapper').forEach((group) => {
          if (this.isLookOptionGroup(group)) return;
          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            if (input.checked) {
              input.checked = false;
              cleared = true;
            }
          });
        });

        this.updateOptions();
        this.currentVariant = undefined;
        this.updateVariantInput();

        const anyAvailable = (this.getVariantData() || []).some((variant) => variant.available);
        this.toggleAddButton(!anyAvailable, anyAvailable ? '' : window.variantStrings.soldOut, true, true);

        if (cleared && window.MYDSizeGate?.clearSizeWarning) {
          window.MYDSizeGate.clearSizeWarning();
        }
      }

      hasSizeSelection() {
        return Array.from(this.querySelectorAll('.variant-input-wrapper')).some((group) => {
          if (this.isLookOptionGroup(group)) return false;
          return Boolean(group.querySelector('input[type="radio"]:checked'));
        });
      }

      shouldShowNotifyMe() {
        // URL deep-link to an OOS variant counts as an explicit selection
        if (this.currentVariant && this.currentVariant.available === false) {
          const urlId = this.getUrlVariantId();
          if (urlId && String(this.currentVariant.id) === urlId) return true;
        }
        return Boolean(
          this.hasSizeSelection() && this.currentVariant && this.currentVariant.available === false
        );
      }

      primeNotifyMeGate() {
        this.ensureNotifyMeHideStyle();
        this.syncNotifyMeGate();
        this.startNotifyMeGateWatcher();
        this.startNotifyFloatObserver();
        this.bindStickyNotifyProxy();
      }

      bindStickyNotifyProxy() {
        if (this._stickyNotifyBound) return;
        this._stickyNotifyBound = true;
        document.addEventListener('click', (event) => {
          const btn = event.target.closest('[data-myd-sticky-notify]');
          if (!btn) return;
          event.preventDefault();
          const inline = this.findInlineNotifyEl?.();
          if (inline) {
            inline.click();
            return;
          }
          document
            .querySelector(
              '.product-form__buttons .restock-alerts-notify-button, .product__info-container .PRODUCT_PAGE-notify-button:not([data-myd-notify-float="true"])'
            )
            ?.click();
        });
      }

      ensureNotifyMeHideStyle() {
        let styleEl = document.getElementById('myd-hide-notify-style');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'myd-hide-notify-style';
          document.head.appendChild(styleEl);
        }
        styleEl.textContent = `
          body:not(.myd-notify-me-active) .product-form__buttons > button[type="submit"].product-form__submit ~ button[type="submit"],
          body:not(.myd-notify-me-active) .product-form__buttons > button[type="submit"].product-form__submit ~ a.product-form__submit,
          body:not(.myd-notify-me-active) [data-myd-notify-hidden="true"] {
            display: none !important;
            visibility: hidden !important;
            pointer-events: none !important;
            height: 0 !important;
            max-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            border: 0 !important;
            opacity: 0 !important;
            position: absolute !important;
            left: -9999px !important;
          }
          /* Right sticky Notify Me: permanently hidden */
          [data-myd-notify-float="true"],
          .floatButtonStyle,
          .restock-alerts-float-button,
          [class*="-float-button"],
          .restock-alerts-notify-button[class*="float"],
          .restock-alerts-notify-button[class*="Float"],
          [class*="notify"][class*="float"],
          [class*="Notify"][class*="float"],
          body > .restock-alerts-notify-button,
          body > .PRODUCT_PAGE-notify-button,
          body > [class*="notify-button"],
          .restock-alerts-notify-button:not(.product-form__buttons *):not(.product__info-container *):not(.sticky-cart-wrapper *),
          .PRODUCT_PAGE-notify-button:not(.product-form__buttons *):not(.product__info-container *):not(.sticky-cart-wrapper *) {
            display: none !important;
            visibility: hidden !important;
            pointer-events: none !important;
            opacity: 0 !important;
          }
          html body [data-myd-notify-cta="true"],
          html body [data-myd-notify-cta="true"].product-form__submit.button,
          html body.myd-notify-me-active .product-form__buttons > button[type="submit"].product-form__submit ~ button,
          html body.myd-notify-me-active .product-form__buttons > button[type="submit"].product-form__submit ~ a {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            box-sizing: border-box !important;
            width: 100% !important;
            height: 4.6rem !important;
            min-height: 4.6rem !important;
            max-height: 4.6rem !important;
            padding: 0 3rem !important;
            margin-top: 1rem !important;
            border: none !important;
            border-radius: 1rem !important;
            box-shadow: none !important;
            font-size: var(--font-button-size, 1.5rem) !important;
            font-weight: 700 !important;
            letter-spacing: 0.01em !important;
            line-height: 1.2 !important;
            text-transform: none !important;
            background: rgb(235 91 12 / 87%) !important;
            background-color: rgb(235 91 12 / 87%) !important;
            background-image: none !important;
            color: #fff !important;
          }
        `;
      }

      isNotifyMeEl(el) {
        if (!(el instanceof HTMLElement)) return false;
        if (el.id === 'gokwik-buy-now' || el.closest?.('#gokwik-buy-now')) return false;
        if (
          el.closest?.(
            '.gbbExtBundleUpsellWrapper, .gbbProductPageAppBlock, [class*="gbbext"], [class*="gbbExt"], [class*="gbboffer"], [class*="unloq"], [class*="fealtyx"], [class*="fx-offer"], [class*="fxui"]'
          )
        ) {
          return false;
        }
        if (el.matches?.('[name="add"]') && el.closest?.('.product-form__buttons')) {
          const submits = Array.from(
            el.closest('.product-form__buttons').querySelectorAll('button[type="submit"].product-form__submit, [name="add"]')
          );
          if (submits[0] === el) return false;
        }

        if (
          el.matches?.(
            '.restock-alerts-notify-button, .PRODUCT_PAGE-notify-button, [class*="restock-alerts"][class*="notify"]'
          )
        ) {
          return true;
        }

        const text = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
        if (text.includes('notify me')) return true;

        if (
          el.matches?.('button[type="submit"].product-form__submit') &&
          el.closest?.('.product-form__buttons')
        ) {
          const submits = Array.from(
            el.closest('.product-form__buttons').querySelectorAll('button[type="submit"].product-form__submit')
          );
          return submits.indexOf(el) > 0;
        }
        return false;
      }

      isNotifyFloatEl(el) {
        if (!(el instanceof HTMLElement)) return false;
        if (
          el.matches?.('.floatButtonStyle, .restock-alerts-float-button, [class*="-float-button"]') ||
          el.closest?.('.floatButtonStyle, .restock-alerts-float-button, [class*="-float-button"]')
        ) {
          return !el.closest(
            '.product-form__buttons, .product__info-container, .sticky-cart-wrapper, [class*="restock-alerts-modal"], .myd-sticky-notify-btn'
          );
        }
        if (
          el.closest(
            '.product-form__buttons, .product__info-container, .sticky-cart-wrapper, [class*="restock-alerts-modal"], .myd-sticky-notify-btn'
          )
        ) {
          return false;
        }
        const text = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
        const looksNotify =
          this.isNotifyMeEl(el) ||
          text === 'notify me' ||
          text === 'notify me!' ||
          el.matches?.('.restock-alerts-notify-button, .PRODUCT_PAGE-notify-button, [class*="notify-button"]');
        if (looksNotify && text.length <= 24) {
          // Hengam side tab lives on document.body, not in the PDP column.
          return true;
        }
        let node = el;
        for (let i = 0; i < 6 && node && node !== document.body; i++) {
          const cls = String(node.className || '').toLowerCase();
          if (
            cls.includes('float') ||
            cls.includes('side-tab') ||
            cls.includes('sticky-tab') ||
            cls.includes('floating')
          ) {
            return true;
          }
          try {
            const style = window.getComputedStyle(node);
            if (style.position === 'fixed') {
              const rect = node.getBoundingClientRect();
              if (rect.width >= window.innerWidth * 0.45) {
                node = node.parentElement;
                continue;
              }
              const nearRight = rect.right >= window.innerWidth - 80;
              if (nearRight) return true;
            }
          } catch (e) {}
          node = node.parentElement;
        }
        return false;
      }

      getNotifyFloatRoot(el) {
        if (!(el instanceof HTMLElement)) return el;
        let node = el;
        let fixedRoot = el;
        for (let i = 0; i < 6 && node && node !== document.body; i++) {
          const cls = String(node.className || '').toLowerCase();
          if (cls.includes('float') || cls.includes('side-tab') || cls.includes('floating')) {
            return node;
          }
          try {
            if (window.getComputedStyle(node).position === 'fixed') fixedRoot = node;
          } catch (e) {}
          node = node.parentElement;
        }
        return fixedRoot;
      }

      tagNotifyFloatElements() {
        const nodes = document.querySelectorAll(
          '.floatButtonStyle, .restock-alerts-float-button, [class*="-float-button"], .restock-alerts-notify-button, .PRODUCT_PAGE-notify-button, [class*="notify-button"], button, a, [role="button"], div'
        );
        nodes.forEach((el) => {
          const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
          if (text.length > 48) return;
          if (!this.isNotifyMeEl(el) && !/^notify me!?$/i.test(text) && !el.matches?.('.restock-alerts-notify-button, .PRODUCT_PAGE-notify-button, [class*="notify-button"]')) {
            return;
          }
          if (!this.isNotifyFloatEl(el)) return;
          const root = this.getNotifyFloatRoot(el);
          [root, el].forEach((node) => {
            if (!(node instanceof HTMLElement)) return;
            node.setAttribute('data-myd-notify-float', 'true');
            node.style.setProperty('display', 'none', 'important');
            node.style.setProperty('visibility', 'hidden', 'important');
            node.style.setProperty('pointer-events', 'none', 'important');
          });
        });
      }

      findInlineNotifyEl() {
        const wrap =
          document.querySelector(`#product-form-${this.dataset.section} .product-form__buttons`) ||
          document.querySelector('.product-form__buttons');
        const pool = [];
        if (wrap) {
          pool.push(
            ...wrap.querySelectorAll(
              '.restock-alerts-notify-button, .PRODUCT_PAGE-notify-button, button, a, [role="button"]'
            )
          );
        }
        pool.push(
          ...document.querySelectorAll(
            '.product__info-container .restock-alerts-notify-button, .product__info-container .PRODUCT_PAGE-notify-button'
          )
        );
        for (const el of pool) {
          if (!this.isNotifyMeEl(el)) continue;
          if (this.isNotifyFloatEl(el)) continue;
          const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
          if (text.length > 48) continue;
          return el;
        }
        return null;
      }

      hideNotifyMeEl(el) {
        if (!el) return;
        el.setAttribute('data-myd-notify-hidden', 'true');
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
      }

      revealNotifyMeEl(el) {
        if (!el) return;
        // Never "reveal" the float tab via the general gate — float has its own scroll rule
        if (el.getAttribute('data-myd-notify-float') === 'true' || this.isNotifyFloatEl(el)) {
          el.removeAttribute('data-myd-notify-hidden');
          return;
        }
        el.removeAttribute('data-myd-notify-hidden');
        el.style.removeProperty('display');
        el.style.removeProperty('visibility');
        this.styleNotifyMeButton(el);
      }

      collapseNotifyMeWrapper(el) {
        if (!(el instanceof HTMLElement)) return;
        el.removeAttribute('data-myd-notify-cta');
        el.style.setProperty('background', 'transparent', 'important');
        el.style.setProperty('background-color', 'transparent', 'important');
        el.style.setProperty('background-image', 'none', 'important');
        el.style.setProperty('border', '0', 'important');
        el.style.setProperty('padding', '0', 'important');
        el.style.setProperty('min-height', '0', 'important');
        el.style.setProperty('height', 'auto', 'important');
        el.style.setProperty('box-shadow', 'none', 'important');
        el.style.setProperty('width', '100%', 'important');
      }

      styleNotifyMeButton(el) {
        if (!(el instanceof HTMLElement)) return;
        if (el.classList.contains('myd-sticky-notify-btn') || el.closest('.sticky-cart-wrapper')) return;
        if (el.getAttribute('data-myd-notify-float') === 'true' || this.isNotifyFloatEl(el)) return;

        const clickable = el.matches('button, a, [role="button"]')
          ? el
          : el.querySelector('button, a, [role="button"]') || el;

        if (clickable !== el) this.collapseNotifyMeWrapper(el);
        clickable.querySelectorAll('button, a, [role="button"]').forEach((child) => {
          this.collapseNotifyMeWrapper(child);
        });

        clickable.setAttribute('data-myd-notify-cta', 'true');
        const styles = {
          background: 'rgb(235 91 12 / 87%)',
          'background-color': 'rgb(235 91 12 / 87%)',
          'background-image': 'none',
          border: 'none',
          color: '#fff',
          display: 'inline-flex',
          'align-items': 'center',
          'justify-content': 'center',
          gap: '0.8rem',
          'box-sizing': 'border-box',
          width: '100%',
          'min-width': '0',
          'max-width': '100%',
          height: '4.6rem',
          'min-height': '4.6rem',
          'max-height': '4.6rem',
          padding: '0 3rem',
          'border-radius': '1rem',
          'box-shadow': 'none',
          'font-size': 'var(--font-button-size, 1.5rem)',
          'font-weight': '700',
          'letter-spacing': '0.01em',
          'line-height': '1.2',
          'text-transform': 'none',
          overflow: 'hidden',
        };
        Object.entries(styles).forEach(([prop, value]) => {
          clickable.style.setProperty(prop, value, 'important');
        });
      }

      styleInlineNotifyMeButtons() {
        const wrap =
          document.querySelector(`#product-form-${this.dataset.section} .product-form__buttons`) ||
          document.querySelector('.product-form__buttons');
        const pool = [];
        if (wrap) {
          pool.push(
            ...wrap.querySelectorAll(
              'button, a, [role="button"], [class*="notify"], [class*="Notify"]'
            )
          );
        }
        pool.push(
          ...document.querySelectorAll(
            '.product__info-container .restock-alerts-notify-button, .product__info-container .PRODUCT_PAGE-notify-button, .product__info-container [class*="notify-button"]'
          )
        );
        pool.forEach((el) => {
          if (!this.isNotifyMeEl(el)) return;
          if (el.closest('[data-myd-notify-cta="true"]') && el.getAttribute('data-myd-notify-cta') !== 'true') return;
          const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
          if (text.length > 48) return;
          this.styleNotifyMeButton(el);
        });
      }

      hideNotifyMeElements() {
        const wrap =
          document.querySelector(`#product-form-${this.dataset.section} .product-form__buttons`) ||
          document.querySelector('.product-form__buttons');
        if (wrap) {
          const submits = Array.from(
            wrap.querySelectorAll(':scope > button[type="submit"].product-form__submit, :scope > a.product-form__submit')
          );
          submits.slice(1).forEach((btn) => {
            if (this.isNotifyFloatEl(btn)) return;
            this.hideNotifyMeEl(btn);
          });
        }

        // Inline / injected CTAs — float tab is CSS-gated by scroll-past
        document.querySelectorAll('button, a, [role="button"], div').forEach((el) => {
          if (!this.isNotifyMeEl(el)) return;
          const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
          if (text.length > 40) return;
          if (this.isNotifyFloatEl(el)) {
            const root = this.getNotifyFloatRoot(el);
            [root, el].forEach((node) => {
              if (!(node instanceof HTMLElement)) return;
              node.setAttribute('data-myd-notify-float', 'true');
              node.style.setProperty('display', 'none', 'important');
              node.style.setProperty('visibility', 'hidden', 'important');
              node.style.setProperty('pointer-events', 'none', 'important');
            });
            return;
          }
          this.hideNotifyMeEl(el);
        });
        this._mydScrolledPastInline = false;
        document.body.classList.remove('myd-notify-float-visible');
      }

      hasScrolledPastInline(inline) {
        if (!inline) return false;
        try {
          return inline.getBoundingClientRect().bottom < 0;
        } catch (e) {
          return false;
        }
      }

      syncNotifyFloatVisibility() {
        this.tagNotifyFloatElements();
        document.body.classList.remove('myd-notify-float-visible');
      }

      bindNotifyFloatObserver() {
        const inline = this.findInlineNotifyEl();
        if (inline === this._mydInlineNotifyEl && this._mydInlineIO) {
          this.syncNotifyFloatVisibility();
          return;
        }
        if (this._mydInlineIO) {
          this._mydInlineIO.disconnect();
          this._mydInlineIO = null;
        }
        if (this._mydInlineScrollBound) {
          window.removeEventListener('scroll', this._mydInlineScrollBound, { passive: true });
          this._mydInlineScrollBound = null;
        }
        this._mydInlineNotifyEl = inline;
        this._mydScrolledPastInline = false;
        if (!inline) {
          this.syncNotifyFloatVisibility();
          return;
        }
        const updatePast = () => {
          this._mydScrolledPastInline = this.hasScrolledPastInline(inline);
          this.syncNotifyFloatVisibility();
        };
        this._mydInlineIO = new IntersectionObserver(
          () => updatePast(),
          { threshold: [0, 0.01, 0.1, 1], rootMargin: '0px' }
        );
        this._mydInlineIO.observe(inline);
        this._mydInlineScrollBound = updatePast;
        window.addEventListener('scroll', this._mydInlineScrollBound, { passive: true });
        updatePast();
      }

      startNotifyFloatObserver() {
        this._mydScrolledPastInline = false;
        document.body.classList.remove('myd-notify-float-visible');
        this.tagNotifyFloatElements();
        this.bindNotifyFloatObserver();
        if (!this._mydFloatDomObserver) {
          let scheduled = false;
          this._mydFloatDomObserver = new MutationObserver(() => {
            if (scheduled) return;
            scheduled = true;
            requestAnimationFrame(() => {
              scheduled = false;
              this.tagNotifyFloatElements();
              this.bindNotifyFloatObserver();
            });
          });
          this._mydFloatDomObserver.observe(document.body, { childList: true, subtree: true });
        }
        [300, 1000, 2500, 5000].forEach((ms) => {
          setTimeout(() => {
            this.tagNotifyFloatElements();
            this.bindNotifyFloatObserver();
          }, ms);
        });
      }

      syncNotifyMeGate() {
        const show = this.shouldShowNotifyMe();
        document.body.classList.toggle('myd-notify-me-active', show);

        if (this.querySelector('[data-combined-listing-option="true"]')) {
          this.setCombinedListingNotifyVisibility(show);
        }

        if (show) {
          document.querySelectorAll('[data-myd-notify-hidden="true"]').forEach((el) => {
            this.revealNotifyMeEl(el);
          });
          this.styleInlineNotifyMeButtons();
        } else {
          this.hideNotifyMeElements();
        }
        // Always kill Hengam's right-edge tab — even when inline Notify Me is on.
        this.syncNotifyFloatVisibility();
      }

      startNotifyMeGateWatcher() {
        if (this._mydNotifyObserver) return;
        const wrap =
          document.querySelector(`#product-form-${this.dataset.section} .product-form__buttons`) ||
          document.querySelector('.product-form__buttons');
        if (wrap) {
          this._mydNotifyObserver = new MutationObserver(() => {
            if (this._mydNotifyStyleBusy) return;
            this._mydNotifyStyleBusy = true;
            try {
              if (!this.shouldShowNotifyMe()) {
                this.hideNotifyMeElements();
                return;
              }
              document.querySelectorAll('[data-myd-notify-hidden="true"], [data-cl-notify-hidden="true"]').forEach((el) => {
                this.revealNotifyMeEl(el);
                this.revealCombinedListingNotifyEl?.(el);
              });
              this.styleInlineNotifyMeButtons();
              this.syncNotifyFloatVisibility();
            } finally {
              requestAnimationFrame(() => {
                this._mydNotifyStyleBusy = false;
              });
            }
          });
          this._mydNotifyObserver.observe(wrap, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
        }
        // Hengam injects late
        [300, 1000, 2500].forEach((ms) => {
          setTimeout(() => {
            if (!this.shouldShowNotifyMe()) {
              this.hideNotifyMeElements();
              return;
            }
            document.querySelectorAll('[data-myd-notify-hidden="true"], [data-cl-notify-hidden="true"]').forEach((el) => {
              this.revealNotifyMeEl(el);
              this.revealCombinedListingNotifyEl?.(el);
            });
            this.styleInlineNotifyMeButtons();
            this.syncNotifyFloatVisibility();
          }, ms);
        });
      }

      lockCombinedListingParentUrl() {
        if (!this.querySelector('[data-combined-listing-option="true"]')) return;

        // Bidirectional (Snitch-style): each sibling URL is a valid entry point.
        // Keep the entry path, but allow history to move to /products/{sibling}.
        if (!this.dataset.clEntryUrl) {
          this.dataset.clEntryUrl = window.location.pathname;
        }
        if (!this.dataset.clParentUrl) {
          this.dataset.clParentUrl = this.dataset.url || window.location.pathname;
        }
        this.dataset.combinedListing = 'true';
        this.dataset.clBidirectional = this.dataset.clBidirectional || 'true';
        this._clHistoryBase = window.location.pathname + window.location.search;
      }

      updateCombinedListingHistory(productUrl, variantId) {
        // Snitch-style AB/BA: URL becomes the selected look's product path.
        // Look picker stays on the page because sibling PDPs also render the group.
        try {
          const childPath = this.normalizeProductPath(productUrl) || window.location.pathname;
          const url = new URL(childPath, window.location.origin);
          const current = new URL(window.location.href);

          // Keep preview / storefront params when switching looks
          ['preview_theme_id', 'preview_key', '_ab', '_fd', '_sc'].forEach((key) => {
            if (current.searchParams.has(key)) {
              url.searchParams.set(key, current.searchParams.get(key));
            }
          });

          if (variantId) url.searchParams.set('variant', String(variantId));
          url.searchParams.delete('look');

          const next = `${url.pathname}${url.search}${url.hash}`;
          if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
            window.history.replaceState({}, '', next);
          }

          this.dataset.url = childPath;
          this.dataset.clChildUrl = childPath;
        } catch (e) {}
      }

      selectCombinedListingLookFromQuery() {
        const params = new URLSearchParams(window.location.search);
        const look = (params.get('look') || '').trim();
        if (!look) return null;

        const lookGroup = Array.from(this.querySelectorAll('.variant-input-wrapper')).find((group) =>
          this.isLookOptionGroup(group)
        );
        if (!lookGroup) return null;

        const inputs = Array.from(lookGroup.querySelectorAll('input[type="radio"]'));
        const match =
          inputs.find((input) => (input.dataset.productHandle || '') === look) ||
          inputs.find((input) => (input.dataset.productUrl || '').includes(`/products/${look}`)) ||
          inputs.find((input) =>
            (input.dataset.optionValue || input.value || '').toLowerCase().includes(look.replace(/-/g, ' '))
          );

        if (match) {
          match.checked = true;
          return match;
        }
        return null;
      }

      primeCombinedListingNotifyHide() {
        if (!this.querySelector('[data-combined-listing-option="true"]')) return;

        document.body.classList.add('combined-listing-pdp', 'combined-listing--in-stock');
        document.body.classList.remove('combined-listing--sold-out');
        this.dataset.clInStock = 'true';
        this.ensureCombinedListingNotifyStyle(true);
        this.removeCombinedListingNotifyButtons();
        this.startCombinedListingNotifyWatcher(true);
      }

      getCombinedListingCache() {
        if (!window.__clProductCache) window.__clProductCache = new Map();
        return window.__clProductCache;
      }

      fetchCombinedListingProduct(productUrl) {
        const path = this.normalizeProductPath(productUrl);
        if (!path) return Promise.resolve(null);

        const cache = this.getCombinedListingCache();
        if (cache.has(path)) return Promise.resolve(cache.get(path));

        if (!this._clFetchInflight) this._clFetchInflight = new Map();
        if (this._clFetchInflight.has(path)) return this._clFetchInflight.get(path);

        const request = fetch(`${path}.js`)
          .then((response) => {
            if (!response.ok) throw new Error(`Product JSON failed: ${response.status}`);
            return response.json();
          })
          .then((product) => {
            // Ajax product JSON uses product_type; Liquid/PDP UI expects type
            if (product && !product.type && product.product_type) {
              product.type = product.product_type;
            }
            cache.set(path, product);
            this._clFetchInflight.delete(path);
            return product;
          })
          .catch((error) => {
            this._clFetchInflight.delete(path);
            throw error;
          });

        this._clFetchInflight.set(path, request);
        return request;
      }
  
      onVariantChange(event) {
        // Combined listings: switching "Shop by Variant/Look" loads a sibling product
        if (this.switchCombinedListingProduct(event?.target)) {
          return;
        }

        this.updateOptions();
        this.updateMasterId();
        this.updatePickupAvailability();
        this.removeErrorMessage();
        this.validateGang();
        this.updateVarMetaData();
  
        if (!this.currentVariant) {
          const fieldsets = Array.from(this.querySelectorAll('fieldset')).filter((fs) => !this.isLookOptionGroup(fs));
          const selectedCount = fieldsets.filter((fs) => fs.querySelector('input:checked')).length;
          const isPartialSelection = fieldsets.length > 1 && selectedCount > 0 && selectedCount < fieldsets.length;

          if (isPartialSelection) {
            // Still choosing options — leave buttons alone so they don't flicker
            this.setAvailabilityPartial();
          } else {
            // No size yet — keep ATC / Buy Now clickable so they can show the inline warning
            this.updateVariantInput();
            const anyAvailable = (this.getVariantData() || []).some((variant) => variant.available);
            this.toggleAddButton(!anyAvailable, anyAvailable ? '' : window.variantStrings.soldOut, true, true);
            if (this.querySelector('[data-combined-listing-option="true"]')) {
              this.syncCombinedListingSizeAvailability();
              this.enableLookOptionGroups();
            }
          }
          this.syncNotifyMeGate();
        } else {
          if(this.gangOption) {
            this.updateGangMedia();
          } else {
            this.updateMedia();
          }
          this.updateURL();
          this.updateVariantInput();
          // Optimistic availability sync — no temporary disable, so ATC / Buy Now
          // stay steady when switching between in-stock sizes.
          this.toggleAddButton(
            !this.currentVariant.available,
            this.currentVariant.available ? '' : window.variantStrings.soldOut,
            true,
            true
          );
          if (window.MYDSizeGate?.clearSizeWarning) window.MYDSizeGate.clearSizeWarning();
          this.renderProductInfo();
          this.setAvailability();
          this.handleFreeShippingInfo();
          this.syncNotifyMeGate();

          if (this.querySelector('[data-combined-listing-option="true"]')) {
            this.syncCombinedListingPurchaseUI(
              Boolean(this.currentVariant.available),
              this.currentVariant
            );
          }
  
          document.dispatchEvent(new CustomEvent('variant:change', {
            detail: {
              variant: this.currentVariant
            }
          }));
        }
  
        this.handleStickyCart(event.target);
      
  
      }

      normalizeProductPath(url) {
        if (!url) return '';
        try {
          return new URL(url, window.location.origin).pathname.replace(/\/$/, '');
        } catch (e) {
          return String(url).split('?')[0].replace(/\/$/, '');
        }
      }

      isLookOptionGroup(group) {
        if (!group) return false;
        if (group.dataset.combinedListingOption === 'true') return true;
        if (group.querySelector('input[data-combined-listing-sibling="true"]')) return true;
        const slug = (group.dataset.optionSlug || '').toLowerCase();
        const legend = (group.querySelector('legend')?.textContent || '').toLowerCase();
        return (
          slug.includes('look') ||
          slug.includes('shop-by') ||
          legend.includes('look') ||
          legend.includes('shop by')
        );
      }

      enableLookOptionGroups() {
        this.querySelectorAll('.variant-input-wrapper').forEach((group) => {
          if (!this.isLookOptionGroup(group)) return;
          group.dataset.combinedListingOption = 'true';
          group.querySelectorAll('input').forEach((input) => {
            input.removeAttribute('disabled');
            input.classList.remove('disabled');
          });
        });
      }

      enableCombinedListingSiblings() {
        this.enableLookOptionGroups();
      }

      ensureFirstCombinedListingSelected() {
        const lookGroup = Array.from(this.querySelectorAll('.variant-input-wrapper')).find((group) =>
          this.isLookOptionGroup(group)
        );
        if (!lookGroup) return;

        const inputs = Array.from(lookGroup.querySelectorAll('input[type="radio"]'));
        if (!inputs.length) return;

        if (!inputs.some((input) => input.checked)) {
          inputs[0].checked = true;
        }
      }

      scheduleCombinedListingThumbnails() {
        const run = () => this.loadCombinedListingThumbnails();
        // After first paint / look hydrate — thumbs are secondary
        if ('requestIdleCallback' in window) {
          requestIdleCallback(run, { timeout: 4000 });
        } else {
          setTimeout(run, 1200);
        }
      }

      loadCombinedListingThumbnails() {
        const lookGroup = Array.from(this.querySelectorAll('.variant-input-wrapper')).find((group) =>
          this.isLookOptionGroup(group)
        );
        if (!lookGroup) return;

        lookGroup.querySelectorAll('input[data-combined-listing-sibling="true"]').forEach((input) => {
          const label = lookGroup.querySelector(`label[for="${input.id}"]`);
          const placeholder = label?.querySelector('[data-look-thumb-placeholder]');
          const existingImg = label?.querySelector('img.look__swatch-image');
          if (!label || existingImg || !placeholder) return;

          this.resolveCombinedListingProductUrl(input).then((productUrl) => {
            if (!productUrl) return;
            return this.fetchCombinedListingProduct(productUrl).then((product) => {
              if (!product) return;
              let src = product.featured_image || product.images?.[0];
              if (!src) return;
              if (src.startsWith('//')) src = `https:${src}`;
              const thumbSrc = src.includes('?') ? `${src}&width=240` : `${src}?width=240`;

              const img = document.createElement('img');
              img.className = 'look__swatch-image';
              img.src = thumbSrc;
              img.alt = product.title || input.value;
              img.loading = 'lazy';
              img.width = 120;
              img.height = 160;
              placeholder.replaceWith(img);
            });
          }).catch(() => {});
        });
      }

      reorderCombinedListingLookOptions(activeInput) {
        const lookGroup = Array.from(this.querySelectorAll('.variant-input-wrapper')).find((group) =>
          this.isLookOptionGroup(group)
        );
        if (!lookGroup || !activeInput) return;

        // Only reorder the currently selected look so the first card is always the active one.
        const selected = activeInput;
        if (selected.type !== 'radio') return;
        if (!selected.checked) return;

        const legend = lookGroup.querySelector('legend');
        const handle = (selected.dataset.productHandle || '').trim();

        const label = lookGroup.querySelector(`label[for="${selected.id}"]`);
        const template = handle ? lookGroup.querySelector(`template[data-cl-look-accordions="${handle}"]`) : null;

        // Build the “block” in the same order as the Liquid snippet: input -> label -> template
        const block = [selected];
        if (label) block.push(label);
        if (template) block.push(template);

        // Move as a single fragment to keep DOM structure stable.
        const frag = document.createDocumentFragment();
        block.forEach((n) => frag.appendChild(n));

        // Insert after legend if present (legend stays on top).
        const ref = legend ? legend.nextSibling : lookGroup.firstChild;
        lookGroup.insertBefore(frag, ref);
      }

      hydrateCombinedListingInPlace() {
        if (this.dataset.clHydrated === 'true') return;

        const lookGroup = Array.from(this.querySelectorAll('.variant-input-wrapper')).find((group) =>
          this.isLookOptionGroup(group)
        );
        if (!lookGroup) return;

        this.ensureFirstCombinedListingSelected();
        const fromQuery = this.selectCombinedListingLookFromQuery();
        const selected =
          fromQuery ||
          lookGroup.querySelector('input[type="radio"]:checked') ||
          lookGroup.querySelector('input[type="radio"]');
        if (!selected) return;

        this.dataset.clHydrated = 'true';

        // Apply look-specific reviews/accordions from Liquid templates immediately (no network).
        // Full product.js hydrate still runs for price/images/sizes, but UI is not dimmed.
        const handle = (selected.dataset.productHandle || '').trim();
        if (handle) {
          try {
            this.syncCombinedListingAccordions(`/products/${handle}`);
          } catch (e) {}
          try {
            this.applyCombinedListingReviewsFromHandle(handle);
          } catch (e) {}
        }

        this.loadCombinedListingInPlace(selected, {
          updateHistory: false,
          showLoading: false,
          refreshSection: false,
          refreshAccordions: false,
          deferReviews: true,
          lightImages: false
        });
      }

      getSelectedOptionValueIds() {
        return Array.from(this.querySelectorAll('fieldset input[type="radio"]:checked'))
          .map((el) => el.dataset.optionValueId)
          .filter(Boolean);
      }

      hasNonLookOptionGroup() {
        return Array.from(this.querySelectorAll('.variant-input-wrapper')).some(
          (group) => !this.isLookOptionGroup(group)
        );
      }

      // Amazon-style: same page update (title, price, images, sizes) — no full redirect
      switchCombinedListingProduct(input) {
        if (!input || input.type !== 'radio') return false;

        const group = input.closest('.variant-input-wrapper');
        if (!this.isLookOptionGroup(group)) return false;

        this.enableLookOptionGroups();
        this.reorderCombinedListingLookOptions(input);
        // Instant template updates while product.js loads
        const handle = (input.dataset.productHandle || '').trim();
        if (handle) {
          try {
            this.applyCombinedListingReviewsFromHandle(handle);
          } catch (e) {}
        }

        this.loadCombinedListingInPlace(input, {
          updateHistory: true,
          showLoading: true,
          refreshSection: false,
          refreshAccordions: true,
          deferReviews: false
        });
        return true;
      }

      loadCombinedListingInPlace(input, {
        updateHistory = true,
        showLoading = true,
        refreshSection = false,
        refreshAccordions = true,
        deferReviews = false
      } = {}) {
        // Allow replacing an in-flight look swap (don't drop clicks while loading)
        const loadId = (this._clLoadId = (this._clLoadId || 0) + 1);
        this._clLoading = true;

        const sectionEl =
          document.getElementById(`shopify-section-${this.dataset.section}`) ||
          this.closest('.shopify-section');
        if (showLoading) sectionEl?.classList.add('combined-listing--loading');

        const finishLoading = () => {
          if (loadId !== this._clLoadId) return;
          this._clLoading = false;
          sectionEl?.classList.remove('combined-listing--loading');
        };

        // Hard stop: never leave the page dimmed if a network call hangs
        const loadingWatchdog = setTimeout(finishLoading, 1800);

        this.resolveCombinedListingProductUrl(input)
          .then((productUrl) => {
            if (loadId !== this._clLoadId) return null;
            if (!productUrl) {
              console.warn('[CombinedListing] Could not resolve product URL for', input.value);
              return null;
            }

            return this.fetchCombinedListingProduct(productUrl).then((product) => {
              if (loadId !== this._clLoadId) return;
              this.applyCombinedListingProduct(product, productUrl, updateHistory, {
                skipReviews: deferReviews,
                eagerKiwi: !deferReviews
              });
              // Unblock UI immediately — secondary work must not dim the page
              clearTimeout(loadingWatchdog);
              finishLoading();

              if (deferReviews) {
                const applyReviews = () => {
                  if (loadId !== this._clLoadId) return;
                  try {
                    this.applyCombinedListingReviews(product, productUrl);
                  } catch (e) {}
                };
                if ('requestIdleCallback' in window) {
                  requestIdleCallback(applyReviews, { timeout: 1200 });
                } else {
                  setTimeout(applyReviews, 0);
                }
              }

              if (refreshAccordions) {
                try {
                  this.syncCombinedListingAccordions(productUrl);
                } catch (e) {}
              }

              if (refreshSection) {
                const run = () => {
                  if (loadId !== this._clLoadId) return;
                  this.refreshCombinedListingSection(productUrl);
                };
                if ('requestIdleCallback' in window) {
                  requestIdleCallback(run, { timeout: 2000 });
                } else {
                  setTimeout(run, 120);
                }
              }

              this.prefetchCombinedListingNeighbors(input);
            });
          })
          .catch((error) => console.error('[CombinedListing]', error))
          .finally(() => {
            clearTimeout(loadingWatchdog);
            finishLoading();
          });
      }

      prefetchCombinedListingNeighbors(activeInput) {
        const lookGroup = activeInput?.closest('.variant-input-wrapper');
        if (!lookGroup) return;

        const run = () => {
          // Prefetch at most 2 neighbors to avoid flooding the network on load
          const siblings = Array.from(
            lookGroup.querySelectorAll('input[data-combined-listing-sibling="true"]')
          ).filter((input) => input !== activeInput && !input.checked);
          siblings.slice(0, 2).forEach((input) => {
            this.resolveCombinedListingProductUrl(input)
              .then((productUrl) => (productUrl ? this.fetchCombinedListingProduct(productUrl) : null))
              .catch(() => {});
          });
        };

        if ('requestIdleCallback' in window) {
          requestIdleCallback(run, { timeout: 5000 });
        } else {
          setTimeout(run, 1200);
        }
      }

      getCombinedListingAccordionMap(root) {
        const map = new Map();
        if (!root) return map;
        root.querySelectorAll('accordion-tab.product__accordion').forEach((tab) => {
          const title = this.normalizeCombinedListingAccordionTitle(
            tab.querySelector('.accordion__title')?.textContent || ''
          );
          if (title) map.set(title, tab);
          const key = (tab.getAttribute('data-cl-accordion') || '').trim();
          if (key) map.set(`key:${key}`, tab);
        });
        return map;
      }

      normalizeCombinedListingAccordionTitle(title) {
        return String(title || '')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase()
          .replace(/^reviews\s+[\d.,]+\/\s*[\d.,]+/, 'reviews');
      }

      findCombinedListingAccordion(map, candidates) {
        if (!map?.size) return null;
        for (const candidate of candidates) {
          if (candidate.startsWith('key:') && map.has(candidate)) return map.get(candidate);
          const key = this.normalizeCombinedListingAccordionTitle(candidate);
          if (map.has(key)) return map.get(key);
        }
        for (const [key, tab] of map.entries()) {
          if (key.startsWith('key:')) continue;
          for (const candidate of candidates) {
            if (candidate.startsWith('key:')) continue;
            const needle = this.normalizeCombinedListingAccordionTitle(candidate);
            if (key.includes(needle) || needle.includes(key)) return tab;
          }
        }
        return null;
      }

      getCombinedListingLookHandle(productUrl) {
        const path = this.normalizeProductPath(productUrl);
        return path ? path.split('/').pop() : '';
      }

      applyCombinedListingAccordionHtml(targetTab, html) {
        if (!targetTab || !html) return false;
        const target = targetTab.querySelector('.accordion__content');
        const trimmed = String(html).trim();
        if (!target || !trimmed) return false;
        // Ignore empty shell lists (parent metafields with labels but no values)
        const probe = document.createElement('div');
        probe.innerHTML = trimmed;
        const text = (probe.textContent || '').replace(/\s+/g, ' ').trim();
        const meaningful = text
          .replace(/FIT:?/gi, '')
          .replace(/MATERIAL:?/gi, '')
          .replace(/MRP:?/gi, '')
          .replace(/COLOR:?/gi, '')
          .replace(/[/\-\s]/g, '')
          .trim();
        if (meaningful.length < 2) return false;
        target.innerHTML = trimmed;
        return true;
      }

      syncCombinedListingAccordionsFromLookTemplates(productUrl) {
        const handle = this.getCombinedListingLookHandle(productUrl);
        if (!handle) return false;

        const template =
          this.querySelector(`template[data-cl-look-accordions="${handle}"]`) ||
          document.querySelector(`template[data-cl-look-accordions="${handle}"]`);
        if (!template) return false;

        const sectionId = this.dataset.section;
        const currentInfo =
          document.querySelector(`#ProductInfo-${sectionId}`) ||
          document.querySelector('.product__info-container');
        if (!currentInfo) return false;

        const currentMap = this.getCombinedListingAccordionMap(currentInfo);
        const pairs = [
          [['key:product-info', 'product info'], 'product-info'],
          [['key:design-story', 'design story'], 'design-story'],
          [['key:wash-care', 'wash care'], 'wash-care']
        ];

        let applied = false;
        pairs.forEach(([candidates, part]) => {
          const target = this.findCombinedListingAccordion(currentMap, candidates);
          const source = template.content.querySelector(`[data-cl-part="${part}"]`);
          if (this.applyCombinedListingAccordionHtml(target, source?.innerHTML)) applied = true;
        });
        return applied;
      }

      stripCombinedListingLegacyAccordions(currentInfo) {
        // No-op: Product Info / Design Story / Wash Care / Return Policy are the intended rows.
        return;
      }

      syncCombinedListingAccordions(productUrl) {
        // Prefer Liquid-rendered look templates (no network).
        if (this.syncCombinedListingAccordionsFromLookTemplates(productUrl)) {
          document
            .querySelectorAll('[id*="collapsible_content"]')
            .forEach((el) => el.style.setProperty('display', 'none', 'important'));
          return Promise.resolve();
        }

        // Slow fallback: never block look-switch UI on full HTML parse
        const path = this.normalizeProductPath(productUrl);
        if (!path) return Promise.resolve();

        const sectionId = this.dataset.section;
        const runFetch = () => {
          const urls = [];
          if (sectionId) urls.push(`${path}?section_id=${encodeURIComponent(sectionId)}`);
          urls.push(path);

          const fetchFirstHtml = (index = 0) => {
            if (index >= urls.length) return Promise.resolve('');
            return fetch(urls[index])
              .then((response) => (response.ok ? response.text() : ''))
              .then((text) => (text ? text : fetchFirstHtml(index + 1)));
          };

          return fetchFirstHtml()
            .then((responseText) => {
              if (!responseText) return;
              // If templates appeared later / look changed, skip
              if (this.syncCombinedListingAccordionsFromLookTemplates(productUrl)) return;

              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const currentInfo =
                document.querySelector(`#ProductInfo-${sectionId}`) ||
                document.querySelector('.product__info-container');
              if (!currentInfo) return;

              const currentMap = this.getCombinedListingAccordionMap(currentInfo);
              const incomingMap = this.getCombinedListingAccordionMap(html);
              if (!incomingMap.size) return;

              const copyPairs = [
                [
                  ['key:product-info', 'product info'],
                  ['key:product-info', 'product info', 'product description']
                ],
                [
                  ['key:design-story', 'design story'],
                  ['key:design-story', 'design story']
                ],
                [
                  ['key:wash-care', 'wash care'],
                  ['key:wash-care', 'wash care']
                ],
                [
                  ['key:return-policy', 'return policy'],
                  ['key:return-policy', 'return policy', '7 days returns & exchange']
                ]
              ];

              copyPairs.forEach(([currentKeys, incomingKeys]) => {
                const target = this.findCombinedListingAccordion(currentMap, currentKeys);
                const source = this.findCombinedListingAccordion(incomingMap, incomingKeys);
                if (target && source) {
                  this.applyCombinedListingAccordionHtml(
                    target,
                    source.querySelector('.accordion__content')?.innerHTML
                  );
                }
              });

              document
                .querySelectorAll('[id*="collapsible_content"]')
                .forEach((el) => el.style.setProperty('display', 'none', 'important'));
            })
            .catch(() => {});
        };

        if ('requestIdleCallback' in window) {
          requestIdleCallback(runFetch, { timeout: 2500 });
        } else {
          setTimeout(runFetch, 200);
        }
        return Promise.resolve();
      }

      refreshCombinedListingSection(productUrl) {
        const params = new URLSearchParams();
        params.set('section_id', this.dataset.section);
        return fetch(`${productUrl}?${params}`)
          .then((response) => (response.ok ? response.text() : ''))
          .then((responseText) => {
            if (!responseText) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sectionId = this.dataset.section;

            const currentGallery = document.querySelector(`#MediaGallery-${sectionId}`);
            const newGallery = html.querySelector(`#MediaGallery-${sectionId}`);
            if (currentGallery && newGallery?.innerHTML.trim()) {
              currentGallery.innerHTML = newGallery.innerHTML;
            }

            // Replace buy buttons from in-stock child product (drops parent Notify Me markup)
            const currentButtons = document.querySelector(
              `#product-form-${sectionId} .product-form__buttons`
            );
            const newButtons = html.querySelector(`#product-form-${sectionId} .product-form__buttons`);
            if (currentButtons && newButtons?.innerHTML.trim()) {
              const quantity = currentButtons.querySelector('.product-form__quantity')?.outerHTML || '';
              currentButtons.innerHTML = newButtons.innerHTML;
              // Keep existing quantity UI if child section omitted it
              if (quantity && !currentButtons.querySelector('.product-form__quantity')) {
                currentButtons.insertAdjacentHTML('afterbegin', quantity);
              }
              // Buttons node was replaced — rebind notify watcher
              this._clNotifyObserver?.disconnect();
              this._clNotifyObserver = null;
            }

            this.updatePrice(responseText);
            this.updatePriceAlt(responseText);
            this.updateSKU(responseText);
            this.updateInventoryStatus(responseText);

            if (this.currentVariant) {
              this.syncCombinedListingPurchaseUI(
                Boolean(this.currentVariant.available),
                this.currentVariant
              );
            }
          })
          .catch(() => {});
      }

      resolveCombinedListingProductUrl(input) {
        const handle = (input.dataset.productHandle || '').trim();
        if (handle && handle !== 'oversized-tees') {
          return Promise.resolve(`/products/${handle}`);
        }

        const fromData = this.normalizeProductPath(input.dataset.productUrl);
        const currentPath = this.normalizeProductPath(this.dataset.url || window.location.pathname);
        // Ignore product_url when it points at the parent listing shell
        if (
          fromData &&
          fromData !== currentPath &&
          !fromData.endsWith('/oversized-tees') &&
          !fromData.includes('/oversized-tees')
        ) {
          return Promise.resolve(fromData);
        }

        const label = (input.dataset.optionValue || input.value || '').trim();
        if (!label) return Promise.resolve('');

        return this.findProductUrlByLabel(label);
      }

      findProductUrlByLabel(label) {
        if (!this._clUrlByLabel) this._clUrlByLabel = new Map();
        const cacheKey = label.toLowerCase().trim();
        if (this._clUrlByLabel.has(cacheKey)) {
          return Promise.resolve(this._clUrlByLabel.get(cacheKey));
        }

        const guesses = this.guessProductHandles(label);
        // Resolve on first successful handle — don't wait for failed guesses
        const tryHandle = (handle) =>
          fetch(`/products/${handle}.js`)
            .then((response) => (response.ok ? `/products/${handle}` : Promise.reject()))
            .catch(() => Promise.reject());

        const raceFirst = (handles) =>
          new Promise((resolve) => {
            if (!handles.length) {
              resolve('');
              return;
            }
            let remaining = handles.length;
            let settled = false;
            handles.forEach((handle) => {
              tryHandle(handle)
                .then((url) => {
                  if (settled) return;
                  settled = true;
                  resolve(url);
                })
                .catch(() => {
                  remaining -= 1;
                  if (!settled && remaining <= 0) resolve('');
                });
            });
          });

        return raceFirst(guesses.slice(0, 3))
          .then((found) => {
            if (found) {
              this._clUrlByLabel.set(cacheKey, found);
              return found;
            }

            const suggestUrl =
              `/search/suggest.json?q=${encodeURIComponent(label)}` +
              '&resources[type]=product&resources[limit]=6';

            return fetch(suggestUrl)
              .then((response) => response.json())
              .then((data) => {
                const products = data?.resources?.results?.products || [];
                const normalizedLabel = label.toLowerCase().trim();
                const match =
                  products.find((product) => (product.title || '').toLowerCase() === normalizedLabel) ||
                  products.find((product) => (product.title || '').toLowerCase().includes(normalizedLabel)) ||
                  products.find((product) =>
                    normalizedLabel
                      .split(/\s+/)
                      .every((part) => (product.title || '').toLowerCase().includes(part))
                  );

                const url = match?.handle ? `/products/${match.handle}` : '';
                if (url) this._clUrlByLabel.set(cacheKey, url);
                return url;
              })
              .catch(() => '');
          });
      }

      guessProductHandles(label) {
        const base = label
          .toLowerCase()
          .replace(/&/g, 'and')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');

        const guesses = [
          base,
          `${base}-oversized-tshirt`,
          `${base}-oversized-t-shirt`,
          `${base}-oversized-graphic-tshirt`,
          `${base}-oversized-graphic-t-shirt`,
        ];

        // Common shortcuts for this catalog
        if (base.includes('defender')) {
          guesses.unshift('defender-oversized-graphic-tshirt');
        }
        if (base.includes('endangered')) {
          guesses.unshift('endangered-heritage-oversized-tshirt');
        }
        if (base.includes('tribal') || base.includes('leopard')) {
          guesses.unshift('tribal-leopard-oversized-graphic-tshirt');
        }
        if (base.includes('green') && base.includes('panel')) {
          guesses.unshift('green-panel');
        }

        return [...new Set(guesses)];
      }

      applyCombinedListingProduct(product, productUrl, updateHistory, { skipReviews = false, eagerKiwi = true } = {}) {
        const sectionId = this.dataset.section;

        // Title
        const currentTitle = document.querySelector(`#ProductInfo-${sectionId} .product__heading`);
        if (currentTitle && product.title) {
          currentTitle.textContent = product.title;
          document.title = product.title;
        }

        // Product type above title → links to same-type products
        let typeEl = document.querySelector(`#ProductInfo-${sectionId} [data-product-type]`);
        const typeWrap =
          currentTitle?.closest('.product__title-main') ||
          currentTitle?.parentElement;
        const productType = product.type || product.product_type || '';
        if (!typeEl && typeWrap && productType) {
          typeEl = document.createElement('a');
          typeEl.className = 'product__type product__text';
          typeEl.setAttribute('data-product-type', '');
          const titleRow = typeWrap.querySelector('.product__title-row');
          if (titleRow) typeWrap.insertBefore(typeEl, titleRow);
          else typeWrap.prepend(typeEl);
        }
        if (typeEl) {
          if (productType) {
            // Keep an <a> even if Liquid rendered a legacy <div>
            if (typeEl.tagName !== 'A') {
              const link = document.createElement('a');
              link.className = typeEl.className || 'product__type product__text';
              link.setAttribute('data-product-type', '');
              typeEl.replaceWith(link);
              typeEl = link;
            }
            typeEl.textContent = productType;
            typeEl.href =
              '/collections/all?filter.p.product_type=' + encodeURIComponent(productType);
            typeEl.hidden = false;
          } else {
            typeEl.textContent = '';
            if (typeEl.tagName === 'A') typeEl.removeAttribute('href');
            typeEl.hidden = true;
          }
        }

        // Price
        this.applyCombinedListingPrice(product, sectionId);

        // Images
        this.applyCombinedListingImages(product, sectionId);

        // Judge.me rating badge + review widget (can be deferred on first paint)
        if (!skipReviews) {
          this.applyCombinedListingReviews(product, productUrl);
        }

        // Size options + variant JSON
        this.applyCombinedListingSizes(product, { eagerKiwi });

        // Buy button / variant id — leave unset until shopper picks a size
        const idInput = document.querySelector(`#product-form-${sectionId} [name="id"]`);
        if (idInput) {
          idInput.value = '';
          idInput.disabled = true;
        }

        this.dataset.clChildUrl = productUrl;
        // Point dataset.url at the active look so size / fetch helpers stay on this product
        const childPath = this.normalizeProductPath(productUrl);
        if (childPath) {
          this.dataset.url = childPath;
        } else if (this.dataset.clParentUrl) {
          this.dataset.url = this.dataset.clParentUrl;
        }

        if (updateHistory) {
          this.updateCombinedListingHistory(productUrl, null);
        }

        this.variantData = product.variants;
        const jsonEl = this.querySelector(':scope > [type="application/json"]');
        if (jsonEl) jsonEl.textContent = JSON.stringify(product.variants);

        this.updateOptions();
        this.currentVariant = undefined;
        this.updateMasterId();
        this.setAvailability();
        this.enableLookOptionGroups();

        // Keep ATC / Buy Now enabled so click can show the inline size warning
        const anyAvailable = product.variants?.some((variant) => variant.available);
        this.syncCombinedListingPurchaseUI(Boolean(anyAvailable), null, product);
        this.clearStuckProductLoaders();

        document.dispatchEvent(
          new CustomEvent('productInfo:loaded', {
            detail: { productUrl, combinedListing: true, product }
          })
        );

        document.dispatchEvent(
          new CustomEvent('variant:change', {
            detail: { variant: null, product, combinedListing: true }
          })
        );
      }

      syncCombinedListingPurchaseUI(isAvailable, variant, product) {
        const sectionId = this.dataset.section;
        const form = document.querySelector(`#product-form-${sectionId}`);
        const sectionEl =
          document.getElementById(`shopify-section-${sectionId}`) ||
          this.closest('.shopify-section');

        this.dataset.clInStock = isAvailable ? 'true' : 'false';
        document.body.classList.toggle('combined-listing--in-stock', Boolean(isAvailable));
        document.body.classList.toggle('combined-listing--sold-out', !isAvailable);
        sectionEl?.classList.toggle('combined-listing--in-stock', Boolean(isAvailable));
        sectionEl?.classList.toggle('combined-listing--sold-out', !isAvailable);

        this.ensureCombinedListingNotifyStyle(isAvailable);
        this.toggleAddButton(!isAvailable, isAvailable ? '' : window.variantStrings.soldOut);

        if (isAvailable) {
          this.removeCombinedListingNotifyButtons();
          this.startCombinedListingNotifyWatcher(true);
        } else {
          this.stopCombinedListingNotifyWatcher();
          // OOS size selected — surface Notify Me (do not leave buttons deleted/hidden)
          if (this.shouldShowNotifyMe()) {
            this.setCombinedListingNotifyVisibility(true);
          }
        }

        // Buy It Now / GoKwik / dynamic checkout
        if (form) {
          form.querySelectorAll('#gokwik-buy-now, .shopify-payment-button, [name="buy-now"]').forEach((el) => {
            el.style.display = isAvailable ? '' : 'none';
            if (isAvailable) {
              el.classList.remove('disabled', 'hidden', 'gokwik-disabled');
              el.removeAttribute('disabled');
              el.querySelectorAll('.addloadr, #btn-loader').forEach((loader) => {
                loader.style.display = 'none';
              });
            } else {
              el.classList.add('disabled');
              el.setAttribute('disabled', 'disabled');
            }
          });
        }
        this.syncGoKwikBuyNowState(Boolean(isAvailable));

        if (product && window.ShopifyAnalytics?.meta?.product) {
          window.ShopifyAnalytics.meta.product.id = product.id;
          window.ShopifyAnalytics.meta.product.gid = `gid://shopify/Product/${product.id}`;
          if (variant?.id) {
            window.ShopifyAnalytics.meta.selectedVariantId = variant.id;
          }
        }

        if (product && window.meta?.product) {
          window.meta.product.id = product.id;
        }
      }

      ensureCombinedListingNotifyStyle(isAvailable) {
        let styleEl = document.getElementById('cl-hide-notify-style');
        if (!isAvailable) {
          styleEl?.remove();
          return;
        }
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'cl-hide-notify-style';
          document.head.appendChild(styleEl);
        }
        // Hengam Notify Me clones ATC (type=submit + product-form__submit, often name="add").
        // Hide every submit after the real ATC; keep #gokwik-buy-now (type=button).
        styleEl.textContent = `
          body.combined-listing-pdp:not(.combined-listing--sold-out):not(.myd-notify-me-active) .product-form__buttons > button[type="submit"].product-form__submit ~ button[type="submit"],
          body.combined-listing-pdp:not(.combined-listing--sold-out):not(.myd-notify-me-active) .product-form__buttons > button[type="submit"].product-form__submit ~ a.product-form__submit,
          body.combined-listing--in-stock:not(.combined-listing--sold-out):not(.myd-notify-me-active) .product-form__buttons > button[type="submit"].product-form__submit ~ button[type="submit"],
          body.combined-listing--in-stock:not(.combined-listing--sold-out):not(.myd-notify-me-active) .product-form__buttons > button[type="submit"].product-form__submit ~ a.product-form__submit,
          body.combined-listing--in-stock:not(.combined-listing--sold-out):not(.myd-notify-me-active) [data-cl-notify-hidden="true"],
          body:not(.myd-notify-me-active):not(.combined-listing--sold-out) [data-cl-notify-hidden="true"] {
            display: none !important;
            visibility: hidden !important;
            pointer-events: none !important;
            height: 0 !important;
            max-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            border: 0 !important;
            opacity: 0 !important;
            position: absolute !important;
            left: -9999px !important;
          }
        `;
      }

      startCombinedListingNotifyWatcher(force = false) {
        if (force) this.stopCombinedListingNotifyWatcher();

        document.body.classList.add('combined-listing--in-stock');
        this.removeCombinedListingNotifyButtons();

        // Light touch: a few delayed passes + buttons observer. CSS does the rest.
        // Avoid long setInterval + body-wide observers (main lag source).
        if (!this._clNotifyTimers) {
          this._clNotifyTimers = [0, 400, 1200, 3000].map((ms) =>
            setTimeout(() => {
              if (this.dataset.clInStock === 'true') this.removeCombinedListingNotifyButtons();
            }, ms)
          );
        }

        const buttonsWrap = document.querySelector(
          `#product-form-${this.dataset.section} .product-form__buttons`
        );
        if (buttonsWrap && !this._clNotifyObserver) {
          this._clNotifyObserver = new MutationObserver(() => {
            if (this.dataset.clInStock === 'true') this.removeCombinedListingNotifyButtons();
          });
          this._clNotifyObserver.observe(buttonsWrap, { childList: true });
        }
      }

      stopCombinedListingNotifyWatcher() {
        this._clNotifyObserver?.disconnect();
        this._clNotifyObserver = null;
        this._clNotifyBodyObserver?.disconnect();
        this._clNotifyBodyObserver = null;
        if (this._clNotifyInterval) {
          clearInterval(this._clNotifyInterval);
          this._clNotifyInterval = null;
        }
        if (this._clNotifyTimers) {
          this._clNotifyTimers.forEach((id) => clearTimeout(id));
          this._clNotifyTimers = null;
        }
      }

      isCombinedListingNotifyEl(el) {
        if (!(el instanceof HTMLElement)) return false;
        if (el.id === 'gokwik-buy-now' || el.closest?.('#gokwik-buy-now')) return false;
        if (
          el.closest?.(
            '.gbbExtBundleUpsellWrapper, .gbbProductPageAppBlock, [class*="gbbext"], [class*="gbbExt"], [class*="gbboffer"], [class*="unloq"], [class*="fealtyx"], [class*="fx-offer"], [class*="fxui"]'
          )
        ) {
          return false;
        }
        if (el.closest?.('.product-form__quantity, .shopify-payment-button')) return false;

        const text = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
        if (/^notify\s*me$/i.test(text) || text.includes('notify me')) return true;

        // Hengam clones ATC classes; second (and later) submit CTAs are Notify Me
        if (
          el.matches?.('button[type="submit"].product-form__submit') &&
          el.closest?.('.product-form__buttons')
        ) {
          const submits = Array.from(
            el.closest('.product-form__buttons').querySelectorAll('button[type="submit"].product-form__submit')
          );
          return submits.indexOf(el) > 0;
        }
        return false;
      }

      hideCombinedListingNotifyEl(el) {
        if (!el) return;
        // Hide only — never remove. Hengam injects once; deleting the node
        // permanently kills Notify Me when an OOS size is later selected.
        el.setAttribute('data-cl-notify-hidden', 'true');
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
      }

      revealCombinedListingNotifyEl(el) {
        if (!el) return;
        el.removeAttribute('data-cl-notify-hidden');
        el.style.removeProperty('display');
        el.style.removeProperty('visibility');
        this.styleNotifyMeButton?.(el);
      }

      removeCombinedListingNotifyButtons() {
        if (this._clNotifyBusy) return;
        // Never strip while Notify Me should be visible for an OOS size
        if (this.shouldShowNotifyMe()) return;
        this._clNotifyBusy = true;
        try {
          const wrap = document.querySelector(
            `#product-form-${this.dataset.section} .product-form__buttons`
          ) || document.querySelector('.product-form__buttons');

          if (wrap) {
            const submits = Array.from(
              wrap.querySelectorAll(':scope > button[type="submit"].product-form__submit')
            );
            submits.slice(1).forEach((btn) => this.hideCombinedListingNotifyEl(btn));
          }
          document
            .querySelectorAll(
              '.restock-alerts-notify-button, .PRODUCT_PAGE-notify-button, [data-cl-notify-hidden="true"]'
            )
            .forEach((el) => {
              if (!this.isCombinedListingNotifyEl(el) && !el.hasAttribute('data-cl-notify-hidden')) return;
              if (this.isNotifyFloatEl?.(el)) return;
              this.hideCombinedListingNotifyEl(el);
            });
        } finally {
          this._clNotifyBusy = false;
        }
      }

      revealCombinedListingNotifyButtons() {
        const wrap = document.querySelector(
          `#product-form-${this.dataset.section} .product-form__buttons`
        ) || document.querySelector('.product-form__buttons');
        if (wrap) {
          const submits = Array.from(
            wrap.querySelectorAll(':scope > button[type="submit"].product-form__submit, :scope > a.product-form__submit')
          );
          submits.slice(1).forEach((btn) => this.revealCombinedListingNotifyEl(btn));
        }
        document
          .querySelectorAll(
            '.restock-alerts-notify-button, .PRODUCT_PAGE-notify-button, [data-cl-notify-hidden="true"]'
          )
          .forEach((el) => {
            if (this.isNotifyFloatEl?.(el)) return;
            this.revealCombinedListingNotifyEl(el);
            this.revealNotifyMeEl?.(el);
          });
      }

      setCombinedListingNotifyVisibility(showNotify) {
        if (showNotify) {
          document.body.classList.remove('combined-listing--in-stock');
          document.body.classList.add('combined-listing--sold-out');
          this.dataset.clInStock = 'false';
          document.getElementById('cl-hide-notify-style')?.remove();
          this.stopCombinedListingNotifyWatcher();
          // Disconnect early boot stripper so Hengam can keep / reinject Notify Me
          try {
            window.__clNotifyEarlyObserver?.disconnect();
            window.__clNotifyEarlyObserver = null;
          } catch (e) {}
          this.revealCombinedListingNotifyButtons();
          // Hengam often injects a beat late after OOS size selection
          [200, 800, 2000].forEach((ms) => {
            setTimeout(() => {
              if (this.shouldShowNotifyMe()) this.revealCombinedListingNotifyButtons();
            }, ms);
          });
          return;
        }
        document.body.classList.add('combined-listing--in-stock');
        document.body.classList.remove('combined-listing--sold-out');
        this.dataset.clInStock = 'true';
        this.ensureCombinedListingNotifyStyle(true);
        this.removeCombinedListingNotifyButtons();
      }

      applyCombinedListingReviewsFromHandle(handle) {
        if (!handle) return;
        const template =
          this.querySelector(`template[data-cl-look-accordions="${handle}"]`) ||
          document.querySelector(`template[data-cl-look-accordions="${handle}"]`);
        if (!template) return;

        const meta = template.content.querySelector('[data-cl-part="jdgm-meta"]');
        const productId = meta?.getAttribute('data-product-id') || '';
        const title = meta?.getAttribute('data-product-title') || '';
        if (!productId) return;

        this.applyCombinedListingReviews({ id: productId, title }, `/products/${handle}`);
      }

      applyCombinedListingReviews(product, productUrl) {
        if (!product?.id) return;

        const productId = String(product.id);
        // Avoid thrashing if we're already showing this look's reviews
        if (this._clReviewsProductId === productId) {
          window.MYDJudgeMe?.placeBadgeUnderTitle();
          return;
        }
        this._clReviewsProductId = productId;

        const title = product.title || '';
        const handle = this.getCombinedListingLookHandle(productUrl);
        const template =
          (handle &&
            (this.querySelector(`template[data-cl-look-accordions="${handle}"]`) ||
              document.querySelector(`template[data-cl-look-accordions="${handle}"]`))) ||
          null;
        const badgeHtml = template?.content
          ?.querySelector('[data-cl-part="jdgm-badge"]')
          ?.innerHTML?.trim();
        const widgetHtml = template?.content
          ?.querySelector('[data-cl-part="jdgm-widget"]')
          ?.innerHTML?.trim();

        document.querySelectorAll('.jdgm-preview-badge').forEach((badge) => {
          this.setJudgeMeProductId(badge, productId);
          badge.classList.remove('jdgm--done-setup-widget', 'jdgm--done-setup');
          if (badgeHtml) {
            badge.innerHTML = badgeHtml;
            badge.dataset.clFromTemplate = 'true';
          } else {
            delete badge.dataset.clFromTemplate;
          }
          this.syncJudgeMePreviewBadge(badge);
        });
        this.placeJudgeMeBadgeUnderTitle();

        document.querySelectorAll('#judgeme_product_reviews, .jdgm-review-widget').forEach((widget) => {
          this.setJudgeMeProductId(widget, productId);
          widget.setAttribute('data-product-title', title);
          widget.classList.remove('jdgm--done-setup-widget', 'jdgm--done-setup');
          if (widgetHtml) {
            widget.innerHTML = widgetHtml;
            widget.dataset.clFromTemplate = 'true';
          } else {
            delete widget.dataset.clFromTemplate;
          }
          widget.querySelectorAll('.jdgm-temp-hiding-style').forEach((styleEl) => styleEl.remove());
          widget
            .querySelectorAll('.jdgm-rev-widg__paginate-spinner-wrapper')
            .forEach((el) => {
              el.style.display = 'none';
            });
          this.setupJudgeMeReviewPagination(widget);
        });

        this.clearStuckProductLoaders();

        document.querySelectorAll('.product__info-container .rating').forEach((el) => {
          el.dataset.clProductId = productId;
        });

        // Lightweight rebind only — do NOT fire jdgm.doneSetup (causes expensive re-init loops)
        window.MYDJudgeMe?.enhancePaginationOnly?.();

        // CDN refresh only when Liquid template didn't provide a full widget
        if (!widgetHtml || !badgeHtml) {
          this.fetchCombinedListingJudgeMeReviews(productId, title);
        }
      }

      setJudgeMeProductId(el, productId) {
        window.MYDJudgeMe?.setProductId(el, productId);
      }

      placeJudgeMeBadgeUnderTitle() {
        window.MYDJudgeMe?.placeBadgeUnderTitle();
      }

      syncJudgeMePreviewBadge(badge) {
        window.MYDJudgeMe?.syncPreviewBadge(badge);
      }

      setupJudgeMeReviewPagination(widget) {
        window.MYDJudgeMe?.setupPagination(widget);
      }

      clearStuckProductLoaders() {
        // GoKwik leaves "Loading.." nodes in the Buy It Now button; keep them hidden when enabled
        document
          .querySelectorAll(
            '#gokwik-buy-now:not(.disabled):not([disabled]) .addloadr, #gokwik-buy-now:not(.disabled):not([disabled]) #btn-loader, #sticky-gokwik-buy-now:not(.disabled):not([disabled]) .addloadr'
          )
          .forEach((el) => {
            el.style.setProperty('display', 'none', 'important');
          });

        document
          .querySelectorAll('.jdgm-rev-widg__paginate-spinner-wrapper')
          .forEach((el) => {
            el.style.display = 'none';
          });

        // Never leave the CL dimmer on after content is applied
        document.querySelectorAll('.combined-listing--loading').forEach((el) => {
          el.classList.remove('combined-listing--loading');
        });
        this._clLoading = false;
      }

      reinitJudgeMeWidgets() {
        document.querySelectorAll('.jdgm-preview-badge').forEach((badge) => {
          this.syncJudgeMePreviewBadge(badge);
        });
        this.placeJudgeMeBadgeUnderTitle();
        document
          .querySelectorAll('#judgeme_product_reviews, .jdgm-review-widget')
          .forEach((widget) => this.setupJudgeMeReviewPagination(widget));
      }

      fetchCombinedListingJudgeMeReviews(productId, title) {
        const shopDomain =
          window.jdgm?.SHOP_DOMAIN ||
          window.Shopify?.shop ||
          window.location.hostname.replace(/^www\./, '');
        if (!shopDomain || !productId) return;

        const params = new URLSearchParams({
          url: shopDomain,
          shop_domain: shopDomain,
          platform: 'shopify',
          product_id: String(productId)
        });

        fetch(`https://cdn.judge.me/widgets/preview_badge?${params}`, { credentials: 'omit' })
          .then((res) => (res.ok ? res.text() : ''))
          .then((svg) => {
            if (!svg || svg.includes('<!DOCTYPE')) return;
            document.querySelectorAll('.jdgm-preview-badge').forEach((badge) => {
              if (!badge.querySelector('.jdgm-prev-badge') && svg.trim().startsWith('<svg')) {
                badge.innerHTML = `<div class="jdgm-prev-badge">${svg}</div>`;
              }
              this.setJudgeMeProductId(badge, productId);
              this.syncJudgeMePreviewBadge(badge);
            });
            this.placeJudgeMeBadgeUnderTitle();
          })
          .catch(() => {});

        fetch(`https://cdn.judge.me/reviews/reviews_for_widget?${params}`, { credentials: 'omit' })
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (!data) return;
            const count = Number(data.total_count) || 0;
            const reviewsHtml = data.html || '';

            document.querySelectorAll('#judgeme_product_reviews, .jdgm-review-widget').forEach((widget) => {
              this.setJudgeMeProductId(widget, productId);
              const reviewsHost = widget.querySelector('.jdgm-rev-widg__reviews');
              if (reviewsHost && reviewsHtml) {
                const probe = document.createElement('div');
                probe.innerHTML = reviewsHtml;
                const incoming = probe.querySelector('.jdgm-rev-widg__reviews') || probe;
                reviewsHost.innerHTML = incoming.innerHTML;
              }

              const revWidg = widget.querySelector('.jdgm-rev-widg');
              if (revWidg) {
                revWidg.setAttribute('data-number-of-reviews', String(count));
              }

              const summaryText = widget.querySelector('.jdgm-rev-widg__summary-text');
              if (summaryText && !summaryText.dataset.clLocked) {
                if (count > 0) {
                  if (
                    !/\d/.test(summaryText.textContent || '') ||
                    /be the first/i.test(summaryText.textContent || '')
                  ) {
                    summaryText.textContent = `${count} review${count === 1 ? '' : 's'}`;
                  }
                } else {
                  summaryText.textContent = 'Be the first to write a review';
                }
              }

              widget.setAttribute('data-product-title', title || '');
              widget.querySelectorAll('.jdgm-temp-hiding-style').forEach((styleEl) => styleEl.remove());
              this.setupJudgeMeReviewPagination(widget);
            });

            document.querySelectorAll('.jdgm-preview-badge').forEach((badge) => {
              if (badge.dataset.clFromTemplate === 'true') {
                this.syncJudgeMePreviewBadge(badge);
                return;
              }
              const prev = badge.querySelector('.jdgm-prev-badge');
              if (!prev) return;
              prev.setAttribute('data-number-of-reviews', String(count));
              if (count <= 0) prev.setAttribute('data-average-rating', '0.00');
              this.syncJudgeMePreviewBadge(badge);
            });
            this.placeJudgeMeBadgeUnderTitle();
          })
          .catch(() => {});
      }

      applyCombinedListingPrice(product, sectionId) {
        const priceContainer = document.getElementById(`price-${sectionId}`);
        if (!priceContainer || !product) return;

        const format =
          window.theme?.Currency?.formatMoney ||
          ((cents) =>
            `₹ ${((cents || 0) / 100).toLocaleString('en-IN', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2
            })}`);
        const moneyFormat = window.shopSettings?.moneyFormat;
        const money = (cents) =>
          moneyFormat ? window.theme.Currency.formatMoney(cents, moneyFormat) : format(cents);

        const priceHtml = money(product.price);
        const sale = product.compare_at_price > product.price;
        const compareHtml = sale ? money(product.compare_at_price) : '';
        const discountHtml = sale ? money(product.compare_at_price - product.price) : '';
        const existingPrice = priceContainer.querySelector('.price');
        const priceSizeClass = ['price--large', 'price--medium', 'price--small'].find((c) =>
          existingPrice?.classList.contains(c)
        ) || '';

        priceContainer.classList.remove('visibility-hidden');
        priceContainer.innerHTML = sale
          ? `
          <div class="price price--mrp-layout ${priceSizeClass} price--on-sale">
            <div class="price__sale-row">
              <span class="price-item price-item--sale"><price-money><bdi>${priceHtml}</bdi></price-money></span>
              <span class="price__discount-off" data-discount-off>
                <price-money><bdi>${discountHtml}</bdi></price-money>&nbsp;OFF
              </span>
            </div>
            <div class="price__mrp-row">
              <span class="price__mrp-label">MRP:</span>
              <s class="price-item price-item--regular">
                <price-money><bdi>${compareHtml}</bdi></price-money>
              </s>
              <span class="price__tax-note">Inclusive of all Taxes</span>
            </div>
          </div>
        `
          : `
          <div class="price price--mrp-layout ${priceSizeClass}">
            <div class="price__sale-row">
              <span class="price-item price-item--regular"><price-money><bdi>${priceHtml}</bdi></price-money></span>
            </div>
            <div class="price__mrp-row">
              <span class="price__tax-note">Inclusive of all Taxes</span>
            </div>
          </div>
        `;
      }

      applyCombinedListingImages(product, sectionId) {
        const imageUrls = (product.images || [])
          .map((image) => {
            let src = typeof image === 'string' ? image : image?.src;
            if (!src) return '';
            if (src.startsWith('//')) src = `https:${src}`;
            return src;
          })
          .filter(Boolean);
        if (!imageUrls.length) return;

        const productRoot =
          document.getElementById(`shopify-section-${sectionId}`)?.querySelector('.product') ||
          document.querySelector('.product');
        const slider = document.querySelector(`#Slider-Gallery-${sectionId}`);
        if (!slider) return;

        const sizedUrls = imageUrls.map((src) =>
          src.includes('?') ? `${src}&width=900` : `${src}?width=900`
        );
        const fullUrls = imageUrls.map((src) => {
          try {
            const url = new URL(src, window.location.origin);
            url.searchParams.set('width', '2000');
            return url.toString();
          } catch (err) {
            return src.includes('?') ? `${src}&width=2000` : `${src}?width=2000`;
          }
        });
        const thumbUrls = imageUrls.map((src) =>
          src.includes('?') ? `${src}&width=208` : `${src}?width=208`
        );
        const alt = (product.title || '').replace(/"/g, '&quot;');
        const isDesktop = window.matchMedia('(min-width: 750px)').matches;

        // Per-look aspect ratio (e.g. Green Panel 1:1 vs Defender 3:4) — never reuse the parent listing's fixed height
        const firstMedia = Array.isArray(product.media) ? product.media[0] : null;
        const aspectRatio =
          Number(firstMedia?.aspect_ratio) ||
          Number(firstMedia?.preview_image?.aspect_ratio) ||
          (firstMedia?.width && firstMedia?.height
            ? Number(firstMedia.width) / Number(firstMedia.height)
            : 0);
        const fallbackRatio =
          getComputedStyle(slider).getPropertyValue('--force-image-ratio-percent').trim() ||
          '133.33333333333334%';
        const ratioPercent = aspectRatio > 0 ? `${(1 / aspectRatio) * 100}%` : fallbackRatio;
        const imgWidth = Number(firstMedia?.width) || Number(firstMedia?.preview_image?.width) || 900;
        const imgHeight =
          Number(firstMedia?.height) ||
          Number(firstMedia?.preview_image?.height) ||
          Math.round(imgWidth / (aspectRatio || 0.75));

        const applyRatioToGallery = (percent) => {
          slider.style.setProperty('--force-image-ratio-percent', percent);
          productRoot?.style.setProperty('--force-image-ratio-percent', percent);
          slider.querySelectorAll('.media').forEach((mediaEl) => {
            mediaEl.style.setProperty('--image-ratio-percent', percent);
          });
        };

        const buildSlides = (urls) =>
          urls
            .map(
              (safeSrc, index) => `
                <li
                  id="Slide-${sectionId}-cl-${index}"
                  class="product__media-item grid__item slider__slide${index === 0 ? ' is-active' : ''}"
                  data-media-id="${sectionId}-cl-${index}"
                  data-media-position="${index + 1}"
                >
                  <div
                    class="product__media media media--transparent media--adapt media-mobile--adapt"
                    style="--image-ratio-percent: ${ratioPercent}; cursor: zoom-in;"
                    role="button"
                    tabindex="0"
                    aria-label="Open image ${index + 1} fullscreen"
                  >
                    <img
                      src="${safeSrc}"
                      data-full-src="${fullUrls[index] || safeSrc}"
                      alt="${alt}"
                      width="${imgWidth}"
                      height="${imgHeight}"
                      loading="${index < 1 ? 'eager' : 'lazy'}"
                      decoding="async"
                      fetchpriority="${index === 0 ? 'high' : 'auto'}"
                      sizes="(min-width: 750px) calc(100vw - 12rem), 100vw"
                    >
                  </div>
                </li>
              `
            )
            .join('');

        const refreshGalleryBindings = () => {
          const mediaGallery = document.getElementById(`MediaGallery-${sectionId}`);
          const viewer = document.getElementById(`GalleryViewer-${sectionId}`);
          const thumbs = document.getElementById(`GalleryThumbnails-${sectionId}`);

          if (viewer?.resetPages) viewer.resetPages();
          else if (viewer?.syncDots) viewer.syncDots();
          if (thumbs?.resetPages) thumbs.resetPages();
          if (typeof mediaGallery?.bindThumbnailClicks === 'function') {
            mediaGallery.bindThumbnailClicks();
          }

          // Keep main gallery scrolled to first image
          if (slider) slider.scrollTo({ left: 0 });
          if (viewer?.goToFirstSlide) viewer.goToFirstSlide();
        };

        const renderMobileThumbnailSlider = () => {
          if (productRoot) {
            productRoot.classList.remove(
              'product--media-grid-pending',
              'product--columns',
              'product--media-grid',
              'product--media-grid-ready',
              'product--stacked'
            );
            productRoot.classList.add('product--thumbnail_slider', 'product--cl-mobile-gallery');
          }
          const gallery = document.getElementById(`MediaGallery-${sectionId}`);
          if (gallery) gallery.dataset.desktopLayout = 'thumbnail_slider';

          applyRatioToGallery(ratioPercent);
          slider.innerHTML = buildSlides(sizedUrls);

          let thumbSlider = document.getElementById(`GalleryThumbnails-${sectionId}`);
          if (!thumbSlider) {
            const mediaGallery = document.getElementById(`MediaGallery-${sectionId}`);
            if (!mediaGallery) return;
            thumbSlider = document.createElement('thumbnail-slider');
            thumbSlider.id = `GalleryThumbnails-${sectionId}`;
            thumbSlider.className = `thumbnail-slider slider-mobile-gutter${
              sizedUrls.length <= 3 ? ' thumbnail-slider--no-slide' : ''
            }`;
            mediaGallery.appendChild(thumbSlider);
          }

          // Keep thumbs in DOM for desktop rebind / a11y; mobile CSS hides them for dots swiper
          thumbSlider.style.display = '';
          thumbSlider.classList.remove('small-hide', 'hidden');
          thumbSlider.innerHTML = `
            <ul
              id="Slider-Thumbnails-${sectionId}"
              class="thumbnail-list list-unstyled slider slider--mobile slider--tablet-up"
              data-media-count="${sizedUrls.length}"
            >
              ${thumbUrls
                .map(
                  (safeSrc, index) => `
                    <li
                      id="Slide-Thumbnails-${sectionId}-${index}"
                      class="thumbnail-list__item slider__slide media-wrapper media-wrapper--small${
                        index === 0 ? ' is-active' : ''
                      }"
                      data-target="${sectionId}-cl-${index}"
                      data-media-position="${index + 1}"
                    >
                      <button
                        type="button"
                        class="thumbnail media media--square"
                        aria-label="Load image ${index + 1}"
                        ${index === 0 ? 'aria-current="true"' : ''}
                        aria-controls="GalleryViewer-${sectionId}"
                      >
                        <img
                          src="${safeSrc}"
                          alt="${alt}"
                          width="104"
                          height="104"
                          loading="lazy"
                          decoding="async"
                        >
                      </button>
                    </li>
                  `
                )
                .join('')}
            </ul>
            <div class="slider-buttons${sizedUrls.length <= 3 ? ' small-hide' : ''}${
              sizedUrls.length <= 4 ? ' medium-hide large-up-hide' : ''
            }">
              <button
                type="button"
                class="slider-button slider-button--prev"
                name="previous"
                aria-label="Previous"
                aria-controls="GalleryThumbnails-${sectionId}"
                data-step="3"
              >
                <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
                </svg>
              </button>
              <button
                type="button"
                class="slider-button slider-button--next"
                name="next"
                aria-label="Next"
                aria-controls="GalleryThumbnails-${sectionId}"
                data-step="3"
              >
                <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
                </svg>
              </button>
            </div>
          `;

          const galleryButtons = document.querySelector(`#GalleryViewer-${sectionId} .slider-buttons`);
          if (galleryButtons) {
            galleryButtons.classList.toggle('small-hide', sizedUrls.length < 2);
            galleryButtons.classList.remove('hidden');
            galleryButtons.querySelectorAll('.slider-button .icon').forEach((icon) => {
              icon.outerHTML = `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-chevron" fill="none" viewBox="0 0 32 32"><path fill-rule="evenodd" clip-rule="evenodd" d="M12.943 24.943l8-8c0.521-0.521 0.521-1.365 0-1.885l-8-8c-0.521-0.521-1.365-0.521-1.885 0s-0.521 1.365 0 1.885l7.057 7.057-7.057 7.057c-0.521 0.521-0.521 1.365 0 1.885s1.365 0.521 1.885 0z" fill="currentColor"></path></svg>`;
            });
          }

          // Wait a frame so custom elements see the new DOM, then rebind clicks/pages
          requestAnimationFrame(() => refreshGalleryBindings());
        };

        const renderDesktopGrid = () => {
          if (productRoot) {
            productRoot.classList.remove(
              'product--media-grid-pending',
              'product--thumbnail_slider',
              'product--cl-mobile-gallery',
              'product--stacked'
            );
            productRoot.classList.add('product--columns', 'product--media-grid', 'product--media-grid-ready');
          }
          const gallery = document.getElementById(`MediaGallery-${sectionId}`);
          if (gallery) gallery.dataset.desktopLayout = 'columns';

          applyRatioToGallery(ratioPercent);
          slider.innerHTML = buildSlides(sizedUrls);

          const thumbs = document.querySelector(
            `#GalleryThumbnails-${sectionId}, #Slider-Thumbnails-${sectionId}`
          );
          if (thumbs) {
            const wrap = thumbs.closest('thumbnail-slider') || thumbs;
            wrap.style.display = 'none';
          }
          document
            .querySelector(`#shopify-section-${sectionId} .product__thumbnails-wrapper`)
            ?.style.setProperty('display', 'none');
        };

        const renderGallery = () => {
          if (isDesktop) {
            renderDesktopGrid();
          } else {
            renderMobileThumbnailSlider();
          }
        };

        // Clear pending state immediately so the page never looks "stuck loading"
        if (productRoot) {
          productRoot.classList.remove('product--media-grid-pending');
        }

        // Render right away — don't block UI on image decode
        renderGallery();

        // Soft-preload next image only (avoid background decode storms)
        sizedUrls.slice(1, 2).forEach((src) => {
          const img = new Image();
          const timer = setTimeout(() => {}, 1200);
          img.onload = () => clearTimeout(timer);
          img.onerror = () => clearTimeout(timer);
          img.src = src;
        });
      }

      applyCombinedListingSizes(product, { eagerKiwi = true } = {}) {
        const lookGroup = Array.from(this.querySelectorAll('.variant-input-wrapper')).find((group) =>
          this.isLookOptionGroup(group)
        );
        if (!lookGroup) return;

        // Remove old size / non-look groups (also drops any prior Kiwi inject inside them)
        Array.from(this.querySelectorAll('.variant-input-wrapper')).forEach((group) => {
          if (!this.isLookOptionGroup(group)) group.remove();
        });

        const sizeOptionIndex = (product.options || []).findIndex((option) =>
          String(option.name || option)
            .toLowerCase()
            .includes('size')
        );
        const optionIndex = sizeOptionIndex >= 0 ? sizeOptionIndex : 0;
        const optionName =
          typeof product.options?.[optionIndex] === 'string'
            ? product.options[optionIndex]
            : product.options?.[optionIndex]?.name || 'Size';
        const optionKey = `option${optionIndex + 1}`;
        const values = product.options?.[optionIndex]?.values || [
          ...new Set(product.variants.map((variant) => variant[optionKey] || variant.options?.[optionIndex]))
        ];

        if (!values.length) return;

        const fieldset = document.createElement('fieldset');
        fieldset.className = 'js product-form__input variant-input-wrapper';
        fieldset.dataset.optionIndex = 'option2';
        fieldset.dataset.variantId = 'option2';
        fieldset.dataset.optionSlug = 'size';
        fieldset.innerHTML = `<legend class="form__label">${optionName}</legend>`;

        values.forEach((value, index) => {
          if (!value) return;
          const id = `${this.dataset.section}-cl-size-${index}`;
          const available = product.variants.some(
            (variant) =>
              (variant[optionKey] || variant.options?.[optionIndex]) === value && variant.available
          );

          const input = document.createElement('input');
          input.type = 'radio';
          input.id = id;
          input.name = optionName;
          input.value = value;
          input.dataset.optionValue = value;
          if (!available) {
            input.classList.add('disabled');
          } else {
            input.removeAttribute('disabled');
            input.classList.remove('disabled');
          }

          const label = document.createElement('label');
          label.className = `size__swatch${available ? '' : ' disabled'}`;
          label.htmlFor = id;
          label.textContent = value;

          fieldset.appendChild(input);
          fieldset.appendChild(label);
        });

        // Do not preselect a size — ATC / Buy Now require an explicit size tap
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = false;
        });

        lookGroup.after(fieldset);
        if (window.MYDSizeGate?.clearSizeWarning) window.MYDSizeGate.clearSizeWarning();

        // Yellow Kiwi SIZE CHART sits beside the Size label
        this.syncKiwiSizingForCombinedListing(product, { eagerLoad: eagerKiwi });
      }

      moveKiwiSizeChart() {
        const root =
          this.closest?.('variant-radios, variant-selects') ||
          document.querySelector('variant-radios, variant-selects') ||
          this;
        const sizeGroup =
          Array.from(root.querySelectorAll('.variant-input-wrapper')).find((group) => {
            const slug = (group.dataset.optionSlug || '').toLowerCase();
            const legend = (group.querySelector('legend')?.textContent || '').toLowerCase();
            return slug.includes('size') || legend.includes('size');
          }) || null;
        const lookGroup = Array.from(root.querySelectorAll('.variant-input-wrapper')).find((group) =>
          this.isLookOptionGroup(group)
        );

        let host = root.querySelector('[data-cl-size-chart-host]');
        if (!host) {
          host = document.createElement('span');
          host.className = 'cl-size-chart-host';
          host.setAttribute('data-cl-size-chart-host', 'true');
          host.innerHTML = `
            <button type="button" class="cl-size-chart-btn" data-cl-size-chart-btn aria-label="Size chart" aria-haspopup="dialog">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M4.5 15.5c0-1.2.7-2.2 1.8-2.7l8.4-3.5c.4-.2.8 0 1 .4l3.3 7.9c.2.4 0 .8-.4 1l-8.4 3.5c-1.1.5-2.4 0-2.9-1.1L4.8 16c-.2-.2-.3-.4-.3-.5z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                <path d="M8 14.2l.8.3M10.2 13.3l.8.3M12.4 12.4l.8.3M14.6 11.5l.8.3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
              <span>Size Chart</span>
            </button>`;
        }

        // Prefer inside Size legend, immediately after the label text
        let legend = sizeGroup?.querySelector('legend.form__label');
        if (!legend && sizeGroup) {
          legend = document.createElement('legend');
          legend.className = 'form__label';
          legend.innerHTML = '<span class="form__label-text">Size</span>';
          sizeGroup.prepend(legend);
        }

        if (legend) {
          if (!legend.querySelector('.form__label-text')) {
            const text = document.createElement('span');
            text.className = 'form__label-text';
            text.textContent = (legend.textContent || 'Size').trim();
            legend.textContent = '';
            legend.appendChild(text);
          }
          legend.appendChild(host);
        } else if (sizeGroup) {
          sizeGroup.prepend(host);
        } else if (lookGroup) {
          lookGroup.after(host);
        } else {
          root.appendChild(host);
        }

        this.bindClSizeChartButton(host);
        return true;
      }

      bindClSizeChartButton(host) {
        const btn = host?.querySelector('[data-cl-size-chart-btn]');
        if (!btn) return;
        if (btn.dataset.clBound === 'radios') return;
        // Liquid binder already delegates to openClSizeChartForCurrentLook when ready
        if (btn.dataset.clBound === 'true') {
          btn.dataset.clBound = 'radios';
          return;
        }
        btn.dataset.clBound = 'radios';
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.openClSizeChartForCurrentLook();
        });
      }

      openClSizeChartForCurrentLook() {
        const product = this._clKiwiProduct;
        const btn = this.querySelector('[data-cl-size-chart-btn]');
        if (typeof window.MYDSizeChart?.open === 'function') {
          window.MYDSizeChart.open(product, { button: btn });
          return;
        }
        // Legacy fallback if MYD sheet JS missing
        if (btn) btn.setAttribute('aria-busy', 'true');
        setTimeout(() => {
          const ensure =
            typeof window.ensureKiwiSizingPlugin === 'function'
              ? window.ensureKiwiSizingPlugin()
              : Promise.resolve();
          ensure
            .then(() => {
              const chart = document.querySelector(
                '.ks-chart-container, .kiwi-sizing-chart-button-container, #kiwi-sizing-chart-button'
              );
              const trigger =
                chart?.querySelector('a, button, [role="button"], ._ks_text') || chart;
              trigger?.click?.();
            })
            .finally(() => btn?.removeAttribute('aria-busy'));
        }, 0);
      }

      reloadKiwiSizingForProduct(product) {
        if (!product?.id) return Promise.resolve();

        window.KiwiSizing = window.KiwiSizing || {};
        const data = window.KiwiSizing.data || (window.KiwiSizing.data = {});
        data.product = String(product.id);
        data.title = product.title || data.title;
        data.vendor = product.vendor || data.vendor;
        data.type = product.type || data.type;
        data.tags = Array.isArray(product.tags) ? product.tags.join(', ') : product.tags || data.tags;
        data.options = product.options || data.options;
        data.variants = product.variants || data.variants;
        if (Array.isArray(product.images)) {
          data.images = product.images
            .map((img) => (typeof img === 'string' ? img : img?.src))
            .filter(Boolean);
        }

        // Keep Shopify analytics meta in sync so Kiwi doesn't keep the previous product
        try {
          if (window.ShopifyAnalytics?.meta?.product) {
            window.ShopifyAnalytics.meta.product.id = Number(product.id);
            window.ShopifyAnalytics.meta.product.gid = `gid://shopify/Product/${product.id}`;
            if (product.title) window.ShopifyAnalytics.meta.product.type = product.type || window.ShopifyAnalytics.meta.product.type;
          }
          if (window.meta?.product) {
            window.meta.product.id = Number(product.id);
          }
        } catch (e) {}

        this._clKiwiProduct = product;
        this._clKiwiProductId = String(product.id);

        // Drop previous look's injected chart/modal hooks so Kiwi rebuilds for the new product
        document
          .querySelectorAll(
            '.ks-chart-container, .ks-calculator-container, .kiwi-sizing-chart-button-container, #kiwi-sizing-chart-button'
          )
          .forEach((el) => {
            try {
              el.remove();
            } catch (err) {}
          });

        window._ks_sizingLoaded = false;

        return new Promise((resolve) => {
          let settled = false;
          const done = () => {
            if (settled) return;
            settled = true;
            resolve();
          };

          try {
            if (typeof window.ks?.on === 'function') {
              window.ks.on('sizing_loaded', done);
            }
          } catch (e) {}

          const tryLoad = (attempt = 0) => {
            const canLoad = typeof window.loadKiwiSizingChart === 'function';
            if (!canLoad) {
              if (attempt < 16) setTimeout(() => tryLoad(attempt + 1), 50);
              else done();
              return;
            }
            try {
              // Prefer Kiwi's public loader only — ks.loadSizing throws when internals aren't ready
              window.loadKiwiSizingChart({});
            } catch (e) {
              // Chart may still inject on next click; avoid console noise from third-party invariant
            }
            // Resolve as soon as chart mounts (don't wait a full 1.2s)
            let ticks = 0;
            const waitMount = setInterval(() => {
              ticks += 1;
              if (
                document.querySelector(
                  '.ks-chart-container, .kiwi-sizing-chart-button-container, #kiwi-sizing-chart-button'
                ) ||
                ticks > 20
              ) {
                clearInterval(waitMount);
                done();
              }
            }, 40);
            setTimeout(done, 800);
          };

          tryLoad();
        });
      }

      syncKiwiSizingForCombinedListing(product, { eagerLoad = true } = {}) {
        if (!product?.id) return;
        this.moveKiwiSizeChart();
        this._clKiwiProduct = product;
        this._clKiwiProductId = String(product.id);

        // Warm Kiwi product data for the selected look (cheap)
        window.KiwiSizing = window.KiwiSizing || {};
        const data = window.KiwiSizing.data || (window.KiwiSizing.data = {});
        data.product = String(product.id);
        data.title = product.title || data.title;
        data.vendor = product.vendor || data.vendor;
        data.type = product.type || data.type;
        data.tags = Array.isArray(product.tags) ? product.tags.join(', ') : product.tags || data.tags;
        data.options = product.options || data.options;
        data.variants = product.variants || data.variants;
        if (Array.isArray(product.images)) {
          data.images = product.images
            .map((img) => (typeof img === 'string' ? img : img?.src))
            .filter(Boolean);
        }

        // On first paint, skip Kiwi script/chart reload — it keeps the page "loading".
        // Chart still loads when the user taps SIZE CHART or switches looks.
        if (!eagerLoad) return;

        const ensure =
          typeof window.ensureKiwiSizingPlugin === 'function'
            ? window.ensureKiwiSizingPlugin()
            : Promise.resolve();

        // Debounce look switches so we don't hammer Kiwi mid-bootstrap
        clearTimeout(this._clKiwiReloadTimer);
        this._clKiwiReloadTimer = setTimeout(() => {
          ensure
            .then(() => new Promise((r) => setTimeout(r, 100)))
            .then(() => this.reloadKiwiSizingForProduct(product))
            .catch(() => {});
        }, 200);
      }
      handleFreeShippingInfo() {
        const infoContainer = this.closest('.product__info-container');
        if(infoContainer) {
          const limitEl = infoContainer.querySelector('.free_shipping_info[data-free-shipping-limit]')
          const limit = limitEl?.getAttribute('data-free-shipping-limit');
          if(limitEl && this.currentVariant.price/100 >= limit) {
            limitEl.classList.remove('hidden')
          } else if(limitEl) {
            limitEl.classList.add('hidden')
          }
        }
      }
      handleStickyCart(target) {
        const stickyCart = document.getElementById(`sticky-cart-${this.dataset.section}`);
        if (!stickyCart) return;
  
        const attribute = target.getAttribute('name');
  
        document.querySelectorAll(`variant-radios[data-section="${this.dataset.section}"] [name="${attribute}"], variant-selects[data-section="${this.dataset.section}"] [name="${attribute}"]`)
        .forEach((input) => {
          if (input.tagName === 'INPUT') {
            input.checked = input.value === target.value;
          }
          else {
            input.value = target.value;
          }
        });
  
        if (!this.currentVariant) return;
        if (!this.currentVariant.featured_media) return;
  
        const image = document.querySelector('.sticky-cart__content-image img');
        if (image) {
          image.srcset = `${this.currentVariant.featured_media.preview_image.src}&width=70 1x, ${this.currentVariant.featured_media.preview_image.src}&width=140 2x`;
          image.src = `${this.currentVariant.featured_media.preview_image.src}&width=70`;
          image.width = 70;
          image.height = Math.ceil(70 / this.currentVariant.featured_media.preview_image.aspect_ratio);
        }
      }
  
      updateOptions() {
        this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
      }
  
      updateMasterId() {
        const variantData = this.getVariantData();
        if (!variantData?.length) {
          this.currentVariant = undefined;
          return;
        }

        const lookGroupIndexes = Array.from(this.querySelectorAll('.variant-input-wrapper'))
          .map((group, index) => (this.isLookOptionGroup(group) ? index : -1))
          .filter((index) => index >= 0);

        this.currentVariant = variantData.find((variant) => {
          // Child product JSON often only has Size — match against non-look options
          if (lookGroupIndexes.length && variant.options.length < this.options.length) {
            const nonLookSelected = this.options.filter((_, index) => !lookGroupIndexes.includes(index));
            return variant.options.every((option, index) => option === nonLookSelected[index]);
          }

          return !variant.options.map((option, index) => {
            return this.options[index] === option;
          }).includes(false);
        });
      }
      updateVarMetaData(){
        const variantInputs = document.querySelectorAll('.variant-input-wrapper input[name="Size"]');
        variantInputs.forEach(input => {
          if (input.checked) {
            const selectedIndex = Array.from(variantInputs).findIndex(input => input.checked);
            const metafieldValues = document.querySelectorAll('.metafield-value');
            metafieldValues.forEach((value, index) => {
              value.classList.toggle('active-metafield', index === selectedIndex);
            });
          }
        });
      }

      handleMetafieldToggle = (selectedIndex, wrapper) => {
        const metafieldValues = wrapper.querySelectorAll('.metafield-value');
        metafieldValues.forEach((value, index) => {
          value.classList.toggle('active-metafield', index === selectedIndex);
        });
      }
      


      validateGang() {
        const mediaItemWithGang = document.querySelector(`#shopify-section-${this.dataset.section} [data-gang-option], quick-view-drawer details[open] [data-gang-option]`);
        if (mediaItemWithGang) {
          this.gangOption = mediaItemWithGang.dataset.gangOption;
        }
      }
      
      updateGangMedia() {
        if (!this.gangOption) return;
  
        const variantInputWrapper = this.querySelector('.variant-input-wrapper[data-option-slug="'+this.gangOption+'"]');
        if (variantInputWrapper) {
          this.gangIndex = variantInputWrapper.dataset.optionIndex;
        } else {
          this.gangOption = null;
        }
  
        if (!this.currentVariant) return;
        //if (!this.currentVariant.featured_media) return;
  
        const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
        const gangValue = this.getSlug(this.currentVariant[this.gangIndex]);
        if (this.currentGangValue !== gangValue) {
          document.querySelectorAll(
            `#shopify-section-${this.dataset.section} [data-gang-connect], quick-view-drawer details[open] [data-gang-connect]`
          ).forEach(function(media){
            media.classList.remove('gang__active');
          });
  
          this.currentGangValue = gangValue;
          var gangConnect = this.gangOption + '_' + gangValue;
          const newMedias = document.querySelectorAll(
            `#shopify-section-${this.dataset.section} [data-gang-connect="${gangConnect}"], quick-view-drawer details[open] [data-gang-connect="${gangConnect}"]`
          );
  
          if (!newMedias.length) return;
          Array.from(newMedias).reverse().forEach(function(newMedia) {
            newMedia.classList.add('gang__active');
            let parent = newMedia.parentElement;
            if (parent.firstChild != newMedia) {
              parent.prepend(newMedia);
              parent.classList.add('product__media-no-animate');
            }
          });
  
          const thumbnailSlider = document.getElementById(`GalleryThumbnails-${this.dataset.section}`);
          if (thumbnailSlider) {
            const inactiveItems = thumbnailSlider.querySelectorAll('.thumbnail-list__item:not(.gang__active)');
            thumbnailSlider.slider.dataset.mediaCount = thumbnailSlider.sliderItems.length - inactiveItems.length;
            if (thumbnailSlider.slider.dataset.mediaCount < 2) {
              thumbnailSlider.classList.add('hidden');
            } else {
              thumbnailSlider.classList.remove('hidden');
            }
            thumbnailSlider.resetPages();
          }
  
          if (!mediaGallery) {
            const productGallery = document.querySelector(`#shopify-section-${this.dataset.section} product-gallery, quick-view-drawer details[open] product-gallery`)
            if (productGallery) {
              productGallery.resetPages();
              productGallery.goToFirstSlide();
            }
          }
  
          const gallerySliderButtons = document.querySelector(`#shopify-section-${this.dataset.section} product-gallery .slider-buttons, quick-view-drawer details[open] product-gallery .slider-buttons`);
          if (gallerySliderButtons) {
            if (document.querySelectorAll(`#shopify-section-${this.dataset.section} product-gallery .product__media-item.gang__active, quick-view-drawer details[open] product-gallery .product__media-item.gang__active`).length < 2) {
              gallerySliderButtons.classList.add('hidden');
            } else {
              gallerySliderButtons.classList.remove('hidden');
            }
          }
        }
  
        if (!this.currentVariant.featured_media) {
          if(!document.querySelectorAll(`#shopify-section-${this.dataset.section} product-gallery .product__media-item.gang__active.is-active, quick-view-drawer details[open] product-gallery .product__media-item.gang__active.is-active`).length) {
            document.querySelector(`#shopify-section-${this.dataset.section} product-gallery .product__media-item.gang__active, quick-view-drawer details[open] product-gallery .product__media-item.gang__active`).classList.add('is-active');
          }
          return;
        }
        
        if (mediaGallery) {
          const scrollIntoView = typeof this.dataset.noScroll === 'undefined';
          mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true, scrollIntoView);
        }
        
        const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
        if (modalContent) {
          const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
          modalContent.prepend(newMediaModal);
        }
        
        const thumbnailContent = document.querySelector(`#ProductThumbnails-${this.dataset.section}`);
        if (thumbnailContent) {
          const newMediaThumbnail = thumbnailContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
          thumbnailContent.prepend(newMediaThumbnail);
        }
      }
  
      getSlug(someString) {
        return someString.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, "-");
      }
  
      updateMedia() {
        if (!this.currentVariant) return;
        if (!this.currentVariant.featured_media) return;
        const newMedia = document.querySelector(
          `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
        );
  
        if (!newMedia) return;
        
        const parent = newMedia.parentElement;
        if (parent.firstChild == newMedia) return;
        parent.prepend(newMedia);
        parent.classList.add('product__media-no-animate');
  
        const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
        if (mediaGallery) {
          const scrollIntoView = typeof this.dataset.noScroll === 'undefined';
          mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true, scrollIntoView);
        }
  
        const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
        if (modalContent) {
          const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
          modalContent.prepend(newMediaModal);
        }
        
        const thumbnailContent = document.querySelector(`#ProductThumbnails-${this.dataset.section}`);
        if (thumbnailContent) {
          const newMediaThumbnail = thumbnailContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
          thumbnailContent.prepend(newMediaThumbnail);
        }
      }
  
      updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === 'false') return;

        // Combined listing: move to selected look's product URL (AB/BA)
        if (
          this.dataset.combinedListing === 'true' ||
          this.querySelector('[data-combined-listing-option="true"]')
        ) {
          this.updateCombinedListingHistory(this.dataset.clChildUrl || this.dataset.url, this.currentVariant.id);
          return;
        }

        window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
      }
  
      updateVariantInput() {
        const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-${this.dataset.section}--alt`);
        productForms.forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          if (this.currentVariant?.id) {
            input.value = this.currentVariant.id;
            input.disabled = false;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            input.value = '';
            input.disabled = true;
          }
        });
      }
  
      updatePickupAvailability() {
        const pickUpAvailability = document.querySelector('pickup-availability');
        if (!pickUpAvailability) return;
  
        if (this.currentVariant && this.currentVariant.available) {
          pickUpAvailability.fetchAvailability(this.currentVariant.id);
        } else {
          pickUpAvailability.removeAttribute('available');
          pickUpAvailability.innerHTML = '';
        }
      }
  
      removeErrorMessage() {
        const section = this.closest('section');
        if (!section) return;
  
        const productForm = section.querySelector('product-form');
        if (productForm) productForm.handleErrorMessage();
      }
  
      renderProductInfo() {
        fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            this.updateSKU(responseText);
            this.updatePrice(responseText);
            this.updatePriceAlt(responseText);
            this.updateColorName(responseText);
            this.updateInventoryStatus(responseText);
            if (this.currentVariant) this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
            this.updateCustomLiquid(responseText);
  
            document.dispatchEvent(new CustomEvent('productInfo:loaded'));
          })
          .catch((e) => {
            console.error(e);
          });
      }
  
      updateSKU(responseText) {
        const id = `sku-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(id);
        const source = html.getElementById(id);
  
        if (source && destination) destination.innerHTML = source.innerHTML;
        if (destination) destination.classList.remove('visibility-hidden');
      }
  
      updateCustomLiquid(responseText) {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destinations = document.querySelectorAll('.custom__liquid');
        const sources = html.querySelectorAll('.custom__liquid');
  
        for (let i = 0, clLength = destinations.length ; i < clLength ; i++) {
          destinations[i].innerHTML = sources[i].innerHTML;
        }
      }
  
      updatePrice(responseText) {
        const id = `price-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(id);
        const source = html.getElementById(id);
  
        if (source && destination) destination.innerHTML = source.innerHTML;
        if (destination) destination.classList.remove('visibility-hidden');
      }
  
      updatePriceAlt(responseText) {
        const id = `price-${this.dataset.section}--alt`;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(id);
        const source = html.getElementById(id);
  
        if (source && destination) destination.innerHTML = source.innerHTML;
        if (destination) destination.classList.remove('visibility-hidden');
      }
  
      updateColorName(responseText) {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        
        for (let index = 1; index <= 3; index++) {
          const id = index > 1 ? `color-${this.dataset.section}-${index}` : `color-${this.dataset.section}`;
          const destination = document.getElementById(id);
          const source = html.getElementById(id);
  
          if (source && destination) destination.innerHTML = source.innerHTML;
          if (destination) destination.classList.remove('visibility-hidden');
        }
      }
  
      updateInventoryStatus(responseText) {
        const id = `inventory-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(id);
        const source = html.getElementById(id);
  
        if (source && destination) destination.innerHTML = source.innerHTML;
        if (destination) destination.classList.remove('visibility-hidden');
      }
  
      setAddButtonLabel(addButton, label) {
        if (!addButton || label == null) return;
        const labelEl = addButton.querySelector('.product-form__cta-label');
        if (labelEl) {
          if (labelEl.textContent !== label) labelEl.textContent = label;
          return;
        }
        if (addButton.textContent !== label) addButton.textContent = label;
      }

      getAddButtonLabel(addButton) {
        if (!addButton) return '';
        const labelEl = addButton.querySelector('.product-form__cta-label');
        return (labelEl ? labelEl.textContent : addButton.textContent) || '';
      }

      toggleAddButton(disable = true, text, modifyClass = true, syncBuyNow = true) {
        const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-${this.dataset.section}--alt`);
        productForms.forEach((productForm) => {
          const addButton = productForm.querySelector('[name="add"]');
          if (!addButton) return;

          const nextLabel = disable
            ? (text || this.getAddButtonLabel(addButton))
            : (addButton.dataset.preOrder === 'true' ? window.variantStrings.preOrder : window.variantStrings.addToCart);
          const isDisabled = addButton.hasAttribute('disabled');

          // Skip DOM writes when state is already correct — avoids visible button refresh
          if (disable) {
            if (!isDisabled) addButton.setAttribute('disabled', 'disabled');
            if (text) this.setAddButtonLabel(addButton, text);
          } else {
            if (isDisabled) addButton.removeAttribute('disabled');
            this.setAddButtonLabel(addButton, nextLabel);
          }
  
          if (!modifyClass) return;
        });

        if (syncBuyNow) this.syncGoKwikBuyNowState(!disable);
      }

      syncGoKwikBuyNowState(enable) {
        document
          .querySelectorAll('#gokwik-buy-now, #sticky-gokwik-buy-now, .gokwik-checkout button, .sticky-gokwik-buy-now button')
          .forEach((el) => {
            el.querySelectorAll('.addloadr, #btn-loader').forEach((loader) => {
              loader.style.display = 'none';
            });
            const isDisabled = el.hasAttribute('disabled') || el.classList.contains('disabled');
            if (enable) {
              if (!isDisabled) return;
              el.classList.remove('disabled', 'hidden', 'gokwik-disabled');
              el.removeAttribute('disabled');
            } else if (!isDisabled) {
              el.classList.add('disabled');
              el.setAttribute('disabled', 'disabled');
            }
          });
      }
  
      setUnavailable() {
        const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-${this.dataset.section}--alt`);
        productForms.forEach((productForm) => {
          const addButton = productForm.querySelector('[name="add"]');
          if (!addButton) return;
          
          this.setAddButtonLabel(addButton, window.variantStrings?.addToCart || 'Add to cart');
      
          // You can choose to keep or modify the following lines based on your requirements
          const price = document.getElementById(`price-${this.dataset.section}`);
          if (price) price.classList.add('visibility-hidden');
      
          const priceAlt = document.getElementById(`price-${this.dataset.section}--alt`);
          if (priceAlt) priceAlt.classList.add('visibility-hidden');
      
          const inventory = document.getElementById(`inventory-${this.dataset.section}`);
          if (inventory) inventory.classList.add('visibility-hidden');
        });
      }
      
      
      getVariantData() {
        this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }
  
      setAvailability() {
        // Combined listing: child variants are Size-only, while the page also has Look.
        // Normal option-tree logic would disable every size — sync from variant JSON instead.
        if (this.querySelector('.variant-input-wrapper[data-combined-listing-option="true"]')) {
          this.syncCombinedListingSizeAvailability();
          this.enableLookOptionGroups();
          return;
        }

        if (!this.currentVariant) {
          this.enableLookOptionGroups();
          return;
        }
  
        this.querySelectorAll('.variant-input-wrapper').forEach(group => {
          if (this.isLookOptionGroup(group)) return;
          this.disableVariantGroup(group);
        });
  
        const currentlySelectedValues = this.currentVariant.options.map((value,index) => {return {value, index: `option${index+1}`}})
        const initialOptions = this.createAvailableOptionsTree(this.variantData, currentlySelectedValues, this.currentVariant);
  
        for (var [option, values] of Object.entries(initialOptions)) {
          this.manageOptionState(option, values);
        }

        this.enableLookOptionGroups();
      }
  
      setAvailabilityInUnavailable() {
        if (this.querySelector('.variant-input-wrapper[data-combined-listing-option="true"]')) {
          this.syncCombinedListingSizeAvailability();
          this.enableLookOptionGroups();
          return;
        }

        this.querySelectorAll('.variant-input-wrapper').forEach(group => {
          if (this.isLookOptionGroup(group)) return;
          this.disableVariantGroup(group);
        });

        const currentlySelectedValues = this.options.map((value,index) => {return {value, index: `option${index+1}`}})
        const initialOptions = this.createAvailableOptionsTree(this.variantData, currentlySelectedValues, this.currentVariant);

        for (var [option, values] of Object.entries(initialOptions)) {
          this.manageOptionState(option, values);
        }

        this.enableLookOptionGroups();
      }

      syncCombinedListingSizeAvailability() {
        const variants = this.getVariantData() || [];
        const sizeGroup = Array.from(this.querySelectorAll('.variant-input-wrapper')).find(
          (group) => !this.isLookOptionGroup(group)
        );
        if (!sizeGroup) return;

        sizeGroup.querySelectorAll('input[type="radio"]').forEach((input) => {
          const value = input.dataset.optionValue || input.value;
          const available = variants.some((variant) => {
            const variantSize = variant.option1 || variant.options?.[0];
            return variantSize === value && variant.available;
          });

          if (available) {
            input.removeAttribute('disabled');
            input.classList.remove('disabled');
          } else {
            // Keep OOS sizes selectable so Notify Me can show for that size
            input.removeAttribute('disabled');
            input.classList.add('disabled');
          }
        });
      }

      setAvailabilityPartial() {
        this.querySelectorAll('.variant-input-wrapper').forEach((group) => {
          group.querySelectorAll('input').forEach((input) => {
            input.removeAttribute('disabled');
            input.classList.remove('disabled');
          });
        });
        this.enableLookOptionGroups();
      }
  
      enableVariantOption(group, obj) {
        const value = obj.value.replace(/([ #;&,.+*~\':"!^$[\]()=>|\/@])/g,'\\$1');
        group.querySelector('option[value="'+ value +'"]').disabled = false;
      }
  
      disableVariantGroup(group) {
        group.querySelectorAll('option').forEach((option) => {
          option.disabled = true;
        });
      }
  
      manageOptionState(option, values) {
        const group = this.querySelector('.variant-input-wrapper[data-option-index="'+ option +'"]');
  
        // Loop through each option value
        values.forEach(obj => {
          this.enableVariantOption(group, obj);
        });
      }
  
      createAvailableOptionsTree(variants, currentlySelectedValues) {
        // Reduce variant array into option availability tree
        return variants.reduce((options, variant) => {
  
          // Check each option group (e.g. option1, option2, option3) of the variant
          Object.keys(options).forEach(index => {
  
            if (variant[index] === null) return;
  
            let entry = options[index].find(option => option.value === variant[index]);
  
            if (typeof entry === 'undefined') {
              // If option has yet to be added to the options tree, add it
              entry = {value: variant[index], soldOut: true}
              options[index].push(entry);
            }
  
            const currentOption1 = currentlySelectedValues.find(({value, index}) => index === 'option1')
            const currentOption2 = currentlySelectedValues.find(({value, index}) => index === 'option2')
  
            switch (index) {
              case 'option1':
                // Option1 inputs should always remain enabled based on all available variants
                entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut;
                // When only option2 is selected, still show option1 availability
                if (!currentOption1?.value && currentOption2?.value && variant['option2'] === currentOption2.value) {
                  entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut;
                }
                break;
              case 'option2':
                if (!currentOption1?.value) {
                  // Only second option group selected — keep all values selectable
                  entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut;
                } else if (variant['option1'] === currentOption1.value) {
                  entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut;
                }
                break;
              case 'option3':
                // Option 3 inputs should remain enabled based on available variants that match first and second option group
                if (
                  currentOption1 && variant['option1'] === currentOption1.value
                  && currentOption2 && variant['option2'] === currentOption2.value
                ) {
                  entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut;
                }
            }
          })
  
          return options;
        }, { option1: [], option2: [], option3: []})
      }
    }

    // Judge.me helpers must exist before variant-selects upgrades / hydrates.
    window.MYDJudgeMe = {
      _paginationReady: false,
      _enhanceScheduled: false,
      _mediaCache: Object.create(null),
      _mediaFetchPromises: Object.create(null),
      _photosByReviewId: Object.create(null),
      _reviewExcerpts: Object.create(null),
      _reviewBodies: Object.create(null),
      _lbPrefetch: null,
      placeBadgeUnderTitle() {
        const slot = document.querySelector('[data-title-rating]');
        if (!slot) return;
        const badge =
          document.querySelector('.product__info-container .jdgm-preview-badge') ||
          document.querySelector('.jdgm-preview-badge');
        if (!badge) {
          slot.classList.remove('is-visible');
          return;
        }
        const moveEl = badge.closest('.shopify-block') || badge;
        if (!slot.contains(moveEl)) slot.appendChild(moveEl);
        this.syncPreviewBadge(badge);
      },
      syncTitleRatingSlot(badge) {
        const slot = document.querySelector('[data-title-rating]');
        if (!slot) return;
        const prev =
          (badge && badge.querySelector('.jdgm-prev-badge')) ||
          slot.querySelector('.jdgm-prev-badge');
        const avg = Number.parseFloat(prev?.getAttribute('data-average-rating') || '0');
        const count = Number.parseInt(prev?.getAttribute('data-number-of-reviews') || '0', 10);
        if (avg > 0 && count > 0) slot.classList.add('is-visible');
        else slot.classList.remove('is-visible');
      },
      syncPreviewBadge(badge) {
        if (!badge) {
          this.syncTitleRatingSlot(null);
          return;
        }
        const prev = badge.querySelector('.jdgm-prev-badge');
        if (!prev) {
          badge.style.display = 'none';
          this.syncTitleRatingSlot(badge);
          return;
        }
        const avg = Number.parseFloat(prev.getAttribute('data-average-rating') || '0');
        const count = Number.parseInt(prev.getAttribute('data-number-of-reviews') || '0', 10);
        if (!(avg > 0) || !(count > 0)) {
          badge.style.display = 'none';
          this.syncTitleRatingSlot(badge);
          return;
        }
        badge.style.display = '';
        badge.style.removeProperty('display');
        prev.removeAttribute('style');

        let score = prev.querySelector('.jdgm-prev-badge__score');
        if (!score) {
          score = document.createElement('span');
          score.className = 'jdgm-prev-badge__score';
          const text = prev.querySelector('.jdgm-prev-badge__text');
          if (text) prev.insertBefore(score, text);
          else prev.appendChild(score);
        }
        score.textContent = (Math.round(avg * 10) / 10).toFixed(1);

        const text = prev.querySelector('.jdgm-prev-badge__text');
        if (text) {
          text.style.removeProperty('visibility');
          text.textContent = `${count} review${count === 1 ? '' : 's'}`;
        }
        this.syncTitleRatingSlot(badge);
      },
      setProductId(el, productId) {
        if (!el || productId == null) return;
        const id = String(productId);
        el.setAttribute('data-id', id);
        el.setAttribute('data-product-id', id);
        const $ = window.jdgm?.$;
        if (!$) return;
        try {
          const $el = $(el);
          $el.attr({ 'data-id': id, 'data-product-id': id });
          $el.removeData('id');
          $el.removeData('productId');
          $el.data('id', id);
          $el.data('productId', id);
        } catch (e) {
          /* optional */
        }
      },
      setupPagination(widget) {
        const jdgm = window.jdgm;
        if (!widget || !jdgm?._setupLoadReviewsEventsFor || !jdgm.$) return false;
        const $w = jdgm.$(widget);
        if (!$w.length) return false;
        let $body = $w.find('.jdgm-rev-widg__body');
        if (!$body.length) {
          $body = jdgm.$('#myd-jdgm-reviews-drawer .jdgm-rev-widg__body.jdgm-myd-in-drawer');
        }
        if ($body.length) {
          const existing = jdgm.$._data?.($body[0], 'events')?.click || [];
          const hasPaginate = existing.some((h) =>
            String(h.selector || '').includes('jdgm-paginate__load-more')
          );
          if (hasPaginate) {
            this._paginationReady = true;
            return true;
          }
          $body.off(
            'click',
            '.jdgm-paginate__page:not(.jdgm-curt), .jdgm-paginate__load-more'
          );
        }
        try {
          // Prefer widget; if body was teleported into the drawer, bind on body host
          if ($body.length && !$w.find('.jdgm-rev-widg__body').length) {
            jdgm._setupLoadReviewsEventsFor($body);
          } else {
            jdgm._setupLoadReviewsEventsFor($w);
          }
          this._paginationReady = true;
          return true;
        } catch (e) {
          return false;
        }
      },
      authorInitials(name) {
        const parts = String(name || '')
          .replace(/[^a-zA-Z0-9\u00C0-\u024F\s.]/g, ' ')
          .trim()
          .split(/\s+/)
          .filter(Boolean);
        if (!parts.length) return '';
        const first = parts[0][0] || '';
        const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
        return (first + last).toUpperCase();
      },
      formatReviewDate(raw) {
        if (!raw) return '';
        const d = this.parseReviewDate(raw);
        if (!d) return '';
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
      },
      parseReviewDate(raw) {
        const s = String(raw || '').trim();
        if (!s) return null;
        let d = new Date(s);
        if (!Number.isNaN(d.getTime())) return d;
        // Safari often rejects "YYYY-MM-DD HH:MM:SS UTC"
        const m = s.match(
          /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?(?:\s*(?:UTC|Z|GMT))?$/i
        );
        if (m) {
          d = new Date(
            Date.UTC(
              Number(m[1]),
              Number(m[2]) - 1,
              Number(m[3]),
              Number(m[4] || 0),
              Number(m[5] || 0),
              Number(m[6] || 0)
            )
          );
          if (!Number.isNaN(d.getTime())) return d;
        }
        const m2 = s.match(/^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})/);
        if (m2) {
          d = new Date(`${m2[1]} ${m2[2]} ${m2[3]} UTC`);
          if (!Number.isNaN(d.getTime())) return d;
        }
        return null;
      },
      formatRelativeDate(raw) {
        if (!raw) return '';
        const d = this.parseReviewDate(raw);
        if (!d) return this.formatReviewDate(raw) || '';
        const diffMs = Date.now() - d.getTime();
        if (diffMs < 0) return this.formatReviewDate(raw);
        const days = Math.floor(diffMs / 86400000);
        if (days < 1) return 'Today';
        if (days === 1) return '1 day ago';
        if (days < 30) return `${days} days ago`;
        const months = Math.floor(days / 30);
        if (months === 1) return '1 month ago';
        if (months < 12) return `${months} months ago`;
        const years = Math.floor(days / 365);
        if (years === 1) return '1 year ago';
        return `${years} years ago`;
      },
      fixReviewDate(rev, timestamp, footer) {
        const el = timestamp || rev?.querySelector('.jdgm-rev__timestamp, .jdgm-myd-rev-date');
        if (!el) return;
        const host =
          footer ||
          rev?.querySelector('.jdgm-myd-rev-footer') ||
          el.closest('.jdgm-myd-rev-footer');
        const raw =
          el.getAttribute('data-content') ||
          el.getAttribute('datetime') ||
          el.textContent ||
          '';
        const relative = this.formatRelativeDate(raw);
        // CDN pages ship empty <time class="jdgm-spinner"> — always fill from data-content
        if (relative) {
          el.textContent = relative;
        } else if (!(el.textContent || '').trim() && raw) {
          el.textContent = this.formatReviewDate(raw) || raw;
        }
        // Judge.me leaves .jdgm-spinner on timestamps — that CSS clips the date to a tiny box
        el.classList.remove('jdgm-spinner', 'jdgm-spinner--sm', 'jdgm-curt');
        el.classList.add('jdgm-myd-rev-date');
        el.removeAttribute('style');
        el.style.setProperty('display', 'block', 'important');
        el.style.setProperty('transform', 'none', 'important');
        el.style.setProperty('writing-mode', 'horizontal-tb', 'important');
        el.style.setProperty('rotate', 'none', 'important');
        el.style.setProperty('width', 'auto', 'important');
        el.style.setProperty('height', 'auto', 'important');
        el.style.setProperty('overflow', 'visible', 'important');
        el.style.setProperty('max-width', 'none', 'important');
        el.style.setProperty('animation', 'none', 'important');
        if (host) {
          if (el.parentElement !== host) host.insertBefore(el, host.firstChild);
          else host.insertBefore(el, host.firstChild);
        }
      },
      sentimentLabel(avg) {
        const n = Number(avg) || 0;
        if (n >= 4.5) return 'Excellent';
        if (n >= 4.0) return 'Very Good';
        if (n >= 3.0) return 'Good';
        if (n > 0) return 'Average';
        return '';
      },
      reviewScore(rev) {
        if (!rev) return 0;
        const ratingEl = rev.querySelector('.jdgm-rev__rating');
        const fromAttr =
          Number.parseInt(ratingEl?.getAttribute('data-score') || '', 10) ||
          Number.parseInt(rev.getAttribute('data-score') || '', 10);
        if (fromAttr > 0) return fromAttr;
        const onStars = rev.querySelectorAll('.jdgm-rev__rating .jdgm-star.jdgm--on').length;
        return onStars || 0;
      },
      setLoadMoreLabel(root) {
        const scope = this.reviewsScope(root) || document;
        scope.querySelectorAll?.('.jdgm-paginate__load-more')?.forEach((btn) => {
          if (!/show all reviews|view all/i.test(btn.textContent || '')) {
            btn.textContent = 'View all';
          }
        });
        this.setupReviewCarousel(root);
        this.bindShowAllReviews(root);
      },
      setupReviewCarousel(root) {
        if (!root) return;
        const list = this.reviewsScope(root)?.querySelector?.('.jdgm-rev-widg__reviews');
        if (!list) return;
        if (!this._mydCarouselMqBound) {
          this._mydCarouselMqBound = true;
          const mq = window.matchMedia('(max-width: 749px)');
          const onChange = () => {
            const live = document.querySelector(
              '.jdgm-rev-widg:not(.jdgm-myd-reviews-in-drawer)'
            );
            if (live) this.setupReviewCarousel(live);
          };
          if (mq.addEventListener) mq.addEventListener('change', onChange);
          else mq.addListener?.(onChange);
        }
        if (!root.classList.contains('jdgm-myd-reviews-in-drawer')) {
          list.classList.add('jdgm-myd-rev-carousel');
          this.ensureCarouselDots(root, list);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => this.ensureCarouselReadMore(root, list));
          });
          this.ensureCarouselHasFive(root, list);
        } else {
          list.classList.remove('jdgm-myd-rev-carousel');
          this.removeCarouselDots(list);
          this.clearCarouselReadMore(list);
        }
        this.ensureViewAllButton(root);
      },
      ensureCarouselHasFive(root, list) {
        if (!root || !list || root.classList.contains('jdgm-myd-reviews-in-drawer')) return;
        if (this._carouselPrefetching || root.dataset.mydCarouselPrefetch === 'done') return;

        const count = () => list.querySelectorAll(':scope > .jdgm-rev').length;
        if (count() >= 5) {
          root.dataset.mydCarouselPrefetch = 'done';
          return;
        }

        const body =
          root.querySelector('.jdgm-rev-widg__body') ||
          document.querySelector('#myd-jdgm-reviews-drawer .jdgm-rev-widg__body') ||
          list.parentElement;
        const btn = body?.querySelector?.('.jdgm-paginate__load-more');
        if (!btn) {
          root.dataset.mydCarouselPrefetch = 'done';
          return;
        }

        this._carouselPrefetching = true;
        root.dataset.mydCarouselPrefetch = '1';
        let rounds = 0;

        const finish = () => {
          this._carouselPrefetching = false;
          root.dataset.mydCarouselPrefetch = 'done';
          this.setupReviewCarousel(root);
        };

        const step = () => {
          if (root.classList.contains('jdgm-myd-reviews-in-drawer')) {
            finish();
            return;
          }
          if (count() >= 5 || rounds >= 4) {
            finish();
            return;
          }
          const loadBtn = body?.querySelector?.('.jdgm-paginate__load-more');
          if (!loadBtn) {
            finish();
            return;
          }
          const before = count();
          rounds += 1;
          try {
            loadBtn.click();
          } catch (e) {
            finish();
            return;
          }

          let tries = 0;
          const poll = () => {
            tries += 1;
            const after = count();
            if (after > before || tries > 35) {
              if (after >= 5 || !body?.querySelector?.('.jdgm-paginate__load-more')) {
                finish();
                return;
              }
              setTimeout(step, 80);
              return;
            }
            setTimeout(poll, 100);
          };
          setTimeout(poll, 160);
        };

        setTimeout(step, 50);
      },
      clearCarouselReadMore(list) {
        (list || document)
          .querySelectorAll?.('.jdgm-myd-read-more')
          ?.forEach((el) => el.remove());
      },
      ensureCarouselReadMore(root, list) {
        if (!root || !list || root.classList.contains('jdgm-myd-reviews-in-drawer')) {
          this.clearCarouselReadMore(list);
          return;
        }
        if (!list.classList.contains('jdgm-myd-rev-carousel')) {
          this.clearCarouselReadMore(list);
          return;
        }

        [...list.querySelectorAll(':scope > .jdgm-rev')].slice(0, 5).forEach((rev) => {
          const body = rev.querySelector('.jdgm-rev__body');
          if (!body) return;
          const textEl = body.querySelector('p') || body;
          const text = (textEl.textContent || '').replace(/\s+/g, ' ').trim();
          // line-clamp is 3; show Read more when text is long or visually clipped
          const clipped =
            textEl.scrollHeight > textEl.clientHeight + 1 ||
            text.length > 85 ||
            text.endsWith('…') ||
            text.endsWith('...');

          let link = rev.querySelector(':scope > .jdgm-myd-read-more, .jdgm-rev__content > .jdgm-myd-read-more');
          if (!clipped) {
            link?.remove();
            return;
          }
          if (!link) {
            link = document.createElement('button');
            link.type = 'button';
            link.className = 'jdgm-myd-read-more';
            link.textContent = 'Read more';
            link.setAttribute('aria-expanded', 'false');
            const content = rev.querySelector('.jdgm-rev__content');
            if (content && body.parentElement === content) {
              body.after(link);
            } else {
              body.after(link);
            }
          } else if (!rev.classList.contains('is-expanded')) {
            link.textContent = 'Read more';
            link.setAttribute('aria-expanded', 'false');
          }
        });
      },
      removeCarouselDots(list) {
        this.stopCarouselAutoplay(list);
        const host = list?.parentElement;
        host?.querySelector?.('.jdgm-myd-rev-dots')?.remove();
        if (list) {
          list.removeEventListener('scroll', list._mydDotsScroll);
          delete list._mydDotsScroll;
        }
      },
      stopCarouselAutoplay(list) {
        if (!list) return;
        if (list._mydCarouselTimer) {
          clearInterval(list._mydCarouselTimer);
          list._mydCarouselTimer = null;
        }
        if (list._mydCarouselResume) {
          clearTimeout(list._mydCarouselResume);
          list._mydCarouselResume = null;
        }
      },
      startCarouselAutoplay(root, list) {
        this.stopCarouselAutoplay(list);
        if (!root || !list) return;
        if (root.classList.contains('jdgm-myd-reviews-in-drawer')) return;
        if (!list.classList.contains('jdgm-myd-rev-carousel')) return;
        // Carousel / autoplay is mobile-only — desktop shows a stacked list
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;

        const cards = () => [...list.querySelectorAll(':scope > .jdgm-rev')].slice(0, 5);
        if (cards().length < 2) return;

        const activeIndex = () => {
          const items = cards();
          const left = list.scrollLeft;
          let best = 0;
          let bestDist = Infinity;
          items.forEach((card, i) => {
            const dist = Math.abs(card.offsetLeft - left);
            if (dist < bestDist) {
              bestDist = dist;
              best = i;
            }
          });
          return best;
        };

        const goNext = () => {
          if (document.hidden) return;
          if (!window.matchMedia('(max-width: 749px)').matches) {
            this.stopCarouselAutoplay(list);
            return;
          }
          if (root.classList.contains('jdgm-myd-reviews-in-drawer')) {
            this.stopCarouselAutoplay(list);
            return;
          }
          // Don't steal focus while someone is reading an expanded card
          if (list.querySelector(':scope > .jdgm-rev.is-expanded')) return;
          const items = cards();
          if (items.length < 2) return;
          const next = (activeIndex() + 1) % items.length;
          const card = items[next];
          if (!card) return;
          list.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
        };

        list._mydCarouselTimer = setInterval(goNext, 5000);

        if (!list.dataset.mydCarouselAutoBound) {
          list.dataset.mydCarouselAutoBound = '1';
          const pauseThenResume = () => {
            this.stopCarouselAutoplay(list);
            list._mydCarouselResume = setTimeout(() => {
              this.startCarouselAutoplay(root, list);
            }, 8000);
          };
          list.addEventListener('pointerdown', pauseThenResume, { passive: true });
          list.addEventListener(
            'keydown',
            (e) => {
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') pauseThenResume();
            },
            true
          );
          const host = list.parentElement;
          host?.querySelector?.('.jdgm-myd-rev-dots')?.addEventListener(
            'click',
            pauseThenResume,
            true
          );
        }
      },
      ensureCarouselDots(root, list) {
        if (!root || !list || root.classList.contains('jdgm-myd-reviews-in-drawer')) {
          this.removeCarouselDots(list);
          return;
        }
        // Dots + autoplay only on mobile; desktop stacks all cards
        if (!window.matchMedia('(max-width: 749px)').matches) {
          this.removeCarouselDots(list);
          return;
        }
        const cards = [...list.querySelectorAll(':scope > .jdgm-rev')].slice(0, 5);
        const host = list.parentElement;
        if (!host) return;

        if (cards.length < 2) {
          this.removeCarouselDots(list);
          return;
        }

        let dots = host.querySelector(':scope > .jdgm-myd-rev-dots');
        if (!dots) {
          dots = document.createElement('div');
          dots.className = 'jdgm-myd-rev-dots';
          dots.setAttribute('role', 'tablist');
          dots.setAttribute('aria-label', 'Review slides');
          list.after(dots);
        }

        if (dots.childElementCount !== cards.length) {
          dots.innerHTML = cards
            .map(
              (_, i) =>
                `<button type="button" class="jdgm-myd-rev-dot" role="tab" aria-label="Review ${i + 1}" data-myd-dot="${i}"></button>`
            )
            .join('');
        }

        const setActive = (index) => {
          dots.querySelectorAll('.jdgm-myd-rev-dot').forEach((dot, i) => {
            if (i === index) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
          });
        };

        const activeIndex = () => {
          const left = list.scrollLeft;
          let best = 0;
          let bestDist = Infinity;
          cards.forEach((card, i) => {
            const dist = Math.abs(card.offsetLeft - left);
            if (dist < bestDist) {
              bestDist = dist;
              best = i;
            }
          });
          return best;
        };

        if (!dots.dataset.mydDotsBound) {
          dots.dataset.mydDotsBound = '1';
          dots.addEventListener('click', (event) => {
            const dot = event.target.closest('.jdgm-myd-rev-dot');
            if (!dot || !dots.contains(dot)) return;
            const index = Number.parseInt(dot.getAttribute('data-myd-dot') || '0', 10);
            const card = cards[index];
            if (!card) return;
            list.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
            setActive(index);
          });
        }

        if (list._mydDotsScroll) {
          list.removeEventListener('scroll', list._mydDotsScroll);
        }
        let ticking = false;
        list._mydDotsScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            setActive(activeIndex());
          });
        };
        list.addEventListener('scroll', list._mydDotsScroll, { passive: true });
        setActive(activeIndex());
        this.startCarouselAutoplay(root, list);
      },
      ensureViewAllButton(root) {
        if (!root) return null;
        let btn = root.querySelector(':scope > .jdgm-myd-view-all');
        if (!btn) {
          btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'jdgm-myd-view-all';
          btn.textContent = 'View all';
          const body = root.querySelector('.jdgm-rev-widg__body');
          if (body) body.after(btn);
          else root.appendChild(btn);
        }
        const count =
          Number.parseInt(root.getAttribute('data-number-of-reviews') || '', 10) ||
          Number.parseInt(
            document
              .querySelector('.jdgm-prev-badge[data-number-of-reviews]')
              ?.getAttribute('data-number-of-reviews') || '',
            10
          ) ||
          Number.parseInt(
            (root.querySelector('.jdgm-rev-widg__summary-text')?.textContent || '').replace(/\D/g, ''),
            10
          ) ||
          root.querySelectorAll('.jdgm-rev-widg__reviews > .jdgm-rev').length ||
          0;
        btn.hidden = count < 2;
        btn.textContent = count > 5 ? `View all (${count})` : 'View all';
        return btn;
      },
      getReviewsDrawerProductTitle() {
        // Prefer the real H1 only — .product__title wraps type, wishlist app blocks, etc.
        const heading = document.querySelector(
          '.product__info-container h1.product__heading, .product__title-main h1.product__heading, h1.product__heading'
        );
        if (heading) {
          const clone = heading.cloneNode(true);
          clone
            .querySelectorAll('script, style, noscript, .product__wishlist, .product__title-wishlist')
            .forEach((el) => el.remove());
          const text = (clone.textContent || '').replace(/\s+/g, ' ').trim();
          if (text && text.length <= 140) {
            return text;
          }
        }

        const og = document.querySelector('meta[property="og:title"]')?.getAttribute('content');
        if (og) {
          return og.replace(/\s*[|\-–—].*$/, '').replace(/\s+/g, ' ').trim();
        }

        try {
          const scripts = document.querySelectorAll('script[type="application/ld+json"]');
          for (const script of scripts) {
            const raw = script.textContent || '';
            if (!raw.includes('Product')) continue;
            const data = JSON.parse(raw);
            const list = Array.isArray(data) ? data : data['@graph'] || [data];
            for (const item of list) {
              if (item && (item['@type'] === 'Product' || item['@type']?.includes?.('Product')) && item.name) {
                return String(item.name).replace(/\s+/g, ' ').trim();
              }
            }
          }
        } catch (e) {
          /* ignore */
        }

        const fromMeta = window.meta?.product?.title || window.ShopifyAnalytics?.meta?.product?.title;
        return fromMeta ? String(fromMeta).replace(/\s+/g, ' ').trim() : '';
      },
      ensureReviewsDrawer() {
        let drawer = document.getElementById('myd-jdgm-reviews-drawer');
        if (drawer) return drawer;

        drawer = document.createElement('div');
        drawer.id = 'myd-jdgm-reviews-drawer';
        drawer.className = 'jdgm-myd-reviews-drawer';
        drawer.hidden = true;
        drawer.innerHTML = `
          <div class="jdgm-myd-reviews-drawer__overlay" data-myd-reviews-close></div>
          <div class="jdgm-myd-reviews-drawer__sheet" role="dialog" aria-modal="true" aria-labelledby="myd-jdgm-reviews-drawer-title">
            <div class="jdgm-myd-reviews-drawer__grabber" aria-hidden="true">
              <span class="jdgm-myd-reviews-drawer__handle"></span>
            </div>
            <div class="jdgm-myd-reviews-drawer__head">
              <div class="jdgm-myd-reviews-drawer__heading">
                <h3 class="jdgm-myd-reviews-drawer__title" id="myd-jdgm-reviews-drawer-title">Reviews</h3>
                <p class="jdgm-myd-reviews-drawer__product" data-myd-reviews-product hidden></p>
              </div>
              <button type="button" class="jdgm-myd-reviews-drawer__close" data-myd-reviews-close aria-label="Close">${window.MYD_POPUP_CLOSE_ICON}</button>
            </div>
            <div class="jdgm-myd-reviews-drawer__scroll">
              <div class="jdgm-myd-reviews-drawer__loading" aria-live="polite">
                <span class="jdgm-myd-reviews-drawer__loading-spin" aria-hidden="true"></span>
                <span>Loading more reviews…</span>
              </div>
              <div class="jdgm-myd-reviews-drawer__sentinel" aria-hidden="true"></div>
            </div>
          </div>
        `;
        document.body.appendChild(drawer);

        const close = (event) => {
          event?.preventDefault?.();
          this.closeReviewsDrawer();
        };
        drawer.querySelectorAll('[data-myd-reviews-close]').forEach((el) => {
          el.addEventListener('click', close);
        });

        if (!this._mydReviewsEscBound) {
          this._mydReviewsEscBound = true;
          document.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape') return;
            // Lightbox sits above the drawer — let it handle Escape first
            if (document.documentElement.classList.contains('jdgm-myd-lightbox-open')) return;
            const open = document.getElementById('myd-jdgm-reviews-drawer');
            if (open && !open.hidden) this.closeReviewsDrawer();
          });
        }
        return drawer;
      },
      openReviewsDrawer(root) {
        if (!root) return;
        const drawer = this.ensureReviewsDrawer();
        if (!drawer || (!drawer.hidden && this._drawerRoot === root)) return;

        const body = root.querySelector('.jdgm-rev-widg__body');
        const scroll = drawer.querySelector('.jdgm-myd-reviews-drawer__scroll');
        if (!body || !scroll) return;

        if (!this._reviewsBodyHome || !this._reviewsBodyHome.isConnected) {
          const marker = document.createComment('myd-jdgm-reviews-body-home');
          body.parentNode?.insertBefore(marker, body);
          this._reviewsBodyHome = marker;
        }

        let placeholder = root.querySelector('.jdgm-myd-rev-body-ph');
        if (!placeholder) {
          placeholder = document.createElement('div');
          placeholder.className = 'jdgm-myd-rev-body-ph';
          placeholder.setAttribute('aria-hidden', 'true');
          this._reviewsBodyHome.after(placeholder);
        }

        const list = body.querySelector('.jdgm-rev-widg__reviews');
        list?.classList.remove('jdgm-myd-rev-carousel');
        this.stopCarouselAutoplay(list);
        this.clearCarouselReadMore(list || body);
        body.querySelectorAll('.jdgm-rev__body-read-more, .jdgm-myd-read-more').forEach((el) => {
          el.remove();
        });
        // Drawer shows full review text (no carousel clamp / Judge.me truncation)
        body.querySelectorAll('.jdgm-rev').forEach((rev) => {
          rev.classList.remove('is-expanded');
          this.expandReviewBodyFull(rev);
        });

        const loading = scroll.querySelector('.jdgm-myd-reviews-drawer__loading');
        const sentinel = scroll.querySelector('.jdgm-myd-reviews-drawer__sentinel');
        scroll.insertBefore(body, loading || sentinel);
        body.classList.add('jdgm-myd-in-drawer');
        root.classList.add('jdgm-myd-reviews-in-drawer');
        this._drawerRoot = root;

        const count =
          Number.parseInt(root.getAttribute('data-number-of-reviews') || '', 10) ||
          Number.parseInt(
            document
              .querySelector('.jdgm-prev-badge[data-number-of-reviews]')
              ?.getAttribute('data-number-of-reviews') || '',
            10
          ) ||
          root.querySelectorAll('.jdgm-rev').length;
        const title = drawer.querySelector('.jdgm-myd-reviews-drawer__title');
        if (title) {
          title.textContent = count > 0 ? `Reviews (${count})` : 'Reviews';
        }
        const productEl = drawer.querySelector('[data-myd-reviews-product]');
        if (productEl) {
          const productName = this.getReviewsDrawerProductTitle();
          if (productName) {
            productEl.textContent = productName;
            productEl.setAttribute('title', productName);
            productEl.hidden = false;
          } else {
            productEl.textContent = '';
            productEl.removeAttribute('title');
            productEl.hidden = true;
          }
        }

        drawer.classList.remove('is-closing', 'is-loading');
        this._drawerLoading = false;
        this._drawerExhausted = false;
        this._drawerEmptyStreak = 0;

        drawer.hidden = false;
        document.documentElement.classList.add('jdgm-myd-reviews-drawer-open');

        const sheet = drawer.querySelector('.jdgm-myd-reviews-drawer__sheet');
        const overlay = drawer.querySelector('.jdgm-myd-reviews-drawer__overlay');
        if (overlay) {
          overlay.style.opacity = '1';
          overlay.style.display = 'block';
        }
        if (sheet) {
          sheet.style.animation = 'none';
          void sheet.offsetWidth;
          sheet.style.animation = '';
        }

        this.setupPagination(root);
        // Re-bind load-more on the moved body if Judge.me lost handlers
        try {
          const jdgm = window.jdgm;
          if (jdgm?._setupLoadReviewsEventsFor && jdgm.$) {
            jdgm._setupLoadReviewsEventsFor(jdgm.$(body));
          }
        } catch (e) {
          /* optional */
        }

        this.styleReviews(root);
        this.setLoadMoreLabel(root);
        this.bindReviewPhotoLightbox(root);
        this.bindDrawerInfiniteScroll(drawer);
        scroll.scrollTop = 0;
        requestAnimationFrame(() => this.maybeLoadMoreReviews(drawer));
      },
      closeReviewsDrawer() {
        const drawer = document.getElementById('myd-jdgm-reviews-drawer');
        if (!drawer || drawer.hidden) return;
        if (drawer.classList.contains('is-closing')) return;

        const finish = () => {
          const body = drawer.querySelector('.jdgm-rev-widg__body.jdgm-myd-in-drawer');
          const root = this._drawerRoot;
          if (body && this._reviewsBodyHome?.parentNode) {
            this._reviewsBodyHome.parentNode.insertBefore(body, this._reviewsBodyHome.nextSibling);
            body.classList.remove('jdgm-myd-in-drawer');
            const list = body.querySelector('.jdgm-rev-widg__reviews');
            list?.classList.add('jdgm-myd-rev-carousel');
          }
          root?.classList.remove('jdgm-myd-reviews-in-drawer');
          root?.querySelector('.jdgm-myd-rev-body-ph')?.remove();

          this.unbindDrawerInfiniteScroll();
          drawer.classList.remove('is-closing', 'is-loading');
          drawer.hidden = true;
          document.documentElement.classList.remove('jdgm-myd-reviews-drawer-open');
          this._drawerRoot = null;

          if (root) {
            this.styleReviews(root);
            this.setupReviewCarousel(root);
            this.setLoadMoreLabel(root);
          }
        };

        drawer.classList.add('is-closing');
        const sheet = drawer.querySelector('.jdgm-myd-reviews-drawer__sheet');
        if (!sheet) {
          finish();
          return;
        }
        let done = false;
        const onEnd = (event) => {
          if (done) return;
          if (event && event.target !== sheet) return;
          done = true;
          sheet.removeEventListener('animationend', onEnd);
          finish();
        };
        sheet.addEventListener('animationend', onEnd);
        setTimeout(onEnd, 340);
      },
      bindDrawerInfiniteScroll(drawer) {
        this.unbindDrawerInfiniteScroll();
        if (!drawer) return;
        const scroll = drawer.querySelector('.jdgm-myd-reviews-drawer__scroll');
        const sentinel = drawer.querySelector('.jdgm-myd-reviews-drawer__sentinel');
        if (!scroll || !sentinel || typeof IntersectionObserver === 'undefined') return;

        this._drawerIO = new IntersectionObserver(
          (entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            this.maybeLoadMoreReviews(drawer);
          },
          { root: scroll, rootMargin: '120px 0px', threshold: 0 }
        );
        this._drawerIO.observe(sentinel);

        this._drawerScrollHandler = () => this.maybeLoadMoreReviews(drawer);
        scroll.addEventListener('scroll', this._drawerScrollHandler, { passive: true });
      },
      unbindDrawerInfiniteScroll() {
        if (this._drawerIO) {
          this._drawerIO.disconnect();
          this._drawerIO = null;
        }
        if (this._drawerScrollHandler) {
          const scroll = document.querySelector('#myd-jdgm-reviews-drawer .jdgm-myd-reviews-drawer__scroll');
          scroll?.removeEventListener('scroll', this._drawerScrollHandler);
          this._drawerScrollHandler = null;
        }
        this._drawerLoading = false;
      },
      getShopDomain() {
        return (
          window.jdgm?.SHOP_DOMAIN ||
          window.jdgmSettings?.shop_domain ||
          window.Shopify?.shop ||
          window.location.hostname.replace(/^www\./, '') ||
          ''
        );
      },
      getWidgetProductId(root) {
        return (
          root?.getAttribute?.('data-id') ||
          root?.getAttribute?.('data-product-id') ||
          root?.closest?.('[data-id]')?.getAttribute('data-id') ||
          root?.closest?.('[data-product-id]')?.getAttribute('data-product-id') ||
          document.querySelector('#judgeme_product_reviews')?.getAttribute('data-id') ||
          document.querySelector('#judgeme_product_reviews')?.getAttribute('data-product-id') ||
          document.querySelector('.jdgm-preview-badge')?.getAttribute('data-id') ||
          document.querySelector('.jdgm-preview-badge')?.getAttribute('data-product-id') ||
          ''
        );
      },
      async fetchReviewsWidgetPage(productId, page, perPage = 5) {
        const shop = this.getShopDomain();
        if (!shop || !productId) return null;
        const params = new URLSearchParams({
          url: shop,
          shop_domain: shop,
          platform: 'shopify',
          page: String(page),
          per_page: String(perPage),
          product_id: String(productId)
        });
        const res = await fetch(`https://cdn.judge.me/reviews/reviews_for_widget?${params}`, {
          credentials: 'omit'
        });
        if (!res.ok) return null;
        return res.json();
      },
      appendFetchedReviews(body, data) {
        if (!body || !data) return { added: 0, hasMore: false };
        const list = body.querySelector('.jdgm-rev-widg__reviews');
        if (!list) return { added: 0, hasMore: false };

        const probe = document.createElement('div');
        probe.innerHTML = data.html || '';
        const incoming = probe.querySelector('.jdgm-rev-widg__reviews') || probe;
        const existing = new Set(
          [...list.querySelectorAll(':scope > .jdgm-rev')]
            .map((rev) => rev.getAttribute('data-review-id') || '')
            .filter(Boolean)
        );

        let added = 0;
        [...incoming.querySelectorAll(':scope > .jdgm-rev')].forEach((rev) => {
          const id = rev.getAttribute('data-review-id') || '';
          if (id && existing.has(id)) return;
          if (id) existing.add(id);
          delete rev.dataset.mydStyled;
          list.appendChild(rev);
          this.styleReview(rev);
          if (rev.closest('.jdgm-myd-reviews-drawer, .jdgm-myd-in-drawer')) {
            this.expandReviewBodyFull(rev);
          }
          added += 1;
        });

        const newPaginate = probe.querySelector('.jdgm-paginate');
        const oldPaginate = body.querySelector('.jdgm-paginate');
        if (newPaginate) {
          if (oldPaginate) oldPaginate.replaceWith(newPaginate);
          else list.after(newPaginate);
        } else if (oldPaginate && !added) {
          oldPaginate.remove();
        }

        const hasMore = Boolean(body.querySelector('.jdgm-paginate__load-more'));
        const total = Number(data.total_count) || 0;
        const count = list.querySelectorAll(':scope > .jdgm-rev').length;
        return {
          added,
          hasMore: hasMore && (!total || count < total)
        };
      },
      maybeLoadMoreReviews(drawer) {
        if (!drawer || drawer.hidden || this._drawerLoading || this._drawerExhausted) return;
        const body = drawer.querySelector('.jdgm-rev-widg__body.jdgm-myd-in-drawer');
        const list = body?.querySelector('.jdgm-rev-widg__reviews');
        if (!body || !list) return;

        const btn = body.querySelector('.jdgm-paginate__load-more');
        const paginate = body.querySelector('.jdgm-paginate');
        const perPage =
          Number.parseInt(paginate?.getAttribute('data-per-page') || '', 10) || 5;
        const count = list.querySelectorAll(':scope > .jdgm-rev').length;
        const root = this._drawerRoot;
        const total =
          Number.parseInt(
            root?.getAttribute?.('data-number-of-reviews') ||
              document
                .querySelector('.jdgm-prev-badge[data-number-of-reviews]')
                ?.getAttribute('data-number-of-reviews') ||
              '',
            10
          ) || 0;

        if (!btn || (total > 0 && count >= total)) {
          this._drawerExhausted = true;
          drawer.classList.remove('is-loading');
          return;
        }

        const productId = this.getWidgetProductId(root);
        let nextPage = Number.parseInt(btn.getAttribute('data-page') || '', 10);
        if (!(nextPage > 1)) {
          nextPage = Math.floor(count / perPage) + 1;
          if (nextPage < 2) nextPage = 2;
        }

        this._drawerLoading = true;
        drawer.classList.add('is-loading');

        const finish = (added, hasMore) => {
          this._drawerLoading = false;
          drawer.classList.remove('is-loading');
          if (this._drawerRoot) {
            this.styleReviews(this._drawerRoot);
            this.setLoadMoreLabel(this._drawerRoot);
          }
          if (!hasMore) {
            this._drawerExhausted = true;
            this._drawerEmptyStreak = 0;
            if (!body.querySelector('.jdgm-paginate__load-more')) {
              body.querySelector('.jdgm-paginate')?.remove();
            }
            return;
          }
          if (added > 0) {
            this._drawerEmptyStreak = 0;
            requestAnimationFrame(() => this.maybeLoadMoreReviews(drawer));
            return;
          }
          // Page already present (e.g. carousel prefetch) — paginate was advanced; try next
          this._drawerEmptyStreak = (this._drawerEmptyStreak || 0) + 1;
          if (this._drawerEmptyStreak >= 3) {
            this._drawerExhausted = true;
            return;
          }
          requestAnimationFrame(() => this.maybeLoadMoreReviews(drawer));
        };

        // Prefer CDN fetch — Judge.me click handlers break after body is teleported into the drawer
        if (productId) {
          this.fetchReviewsWidgetPage(productId, nextPage, perPage)
            .then((data) => {
              if (!data) {
                finish(0, false);
                return;
              }
              const { added, hasMore } = this.appendFetchedReviews(body, data);
              finish(added, hasMore);
            })
            .catch(() => finish(0, false));
          return;
        }

        // Fallback: try Judge.me's button click
        const before = count;
        try {
          btn.click();
        } catch (e) {
          finish(0, false);
          return;
        }

        let tries = 0;
        const poll = () => {
          tries += 1;
          const after = list.querySelectorAll(':scope > .jdgm-rev').length;
          const stillBtn = body.querySelector('.jdgm-paginate__load-more');
          if (after > before || !stillBtn || tries > 40) {
            finish(after - before, Boolean(stillBtn && after > before));
            return;
          }
          setTimeout(poll, 120);
        };
        setTimeout(poll, 180);
      },
      bindShowAllReviews(root) {
        if (!root || root.dataset.mydShowAllBound === '1') return;
        root.dataset.mydShowAllBound = '1';
        root.addEventListener(
          'click',
          (e) => {
            const readMore = e.target.closest('.jdgm-myd-read-more');
            if (readMore && root.contains(readMore)) {
              e.preventDefault();
              e.stopPropagation();
              this.toggleReviewExpand(readMore.closest('.jdgm-rev'), readMore);
              return;
            }

            const viewAll = e.target.closest('.jdgm-myd-view-all');
            if (viewAll && root.contains(viewAll)) {
              e.preventDefault();
              e.stopPropagation();
              this.openReviewsDrawer(root);
              return;
            }

            const btn = e.target.closest('.jdgm-paginate__load-more');
            if (!btn || !root.contains(btn)) return;
            // Quiet prefetch for carousel (need 5 cards) — let Judge.me load
            if (this._carouselPrefetching || root.dataset.mydCarouselPrefetch === '1') return;
            // Inside drawer: let Judge.me / infinite scroll handle load-more
            if (btn.closest('.jdgm-myd-in-drawer, .jdgm-myd-reviews-drawer')) return;
            e.preventDefault();
            e.stopPropagation();
            this.openReviewsDrawer(root);
          },
          true
        );
      },
      expandReviewBodyFull(rev) {
        if (!rev) return;
        const body = rev.querySelector('.jdgm-rev__body');
        if (!body) return;
        const reviewId = rev.getAttribute('data-review-id') || '';

        body
          .querySelectorAll(
            '.jdgm-truncate-text, .jdgm-rev__body-full, .jdgm-rev__content-hidden, [hidden]'
          )
          .forEach((el) => {
            el.removeAttribute('hidden');
            el.style.removeProperty('display');
            el.style.removeProperty('visibility');
            el.style.removeProperty('max-height');
            el.style.removeProperty('-webkit-line-clamp');
            el.style.removeProperty('line-clamp');
          });

        body.classList.remove('jdgm-truncate-ellipsis', 'is-truncated');
        body.style.removeProperty('max-height');
        body.style.removeProperty('-webkit-line-clamp');
        body.style.removeProperty('line-clamp');
        body.style.removeProperty('overflow');
        body.style.removeProperty('display');
        body.querySelectorAll('p').forEach((p) => {
          p.style.removeProperty('max-height');
          p.style.removeProperty('-webkit-line-clamp');
          p.style.removeProperty('line-clamp');
          p.style.removeProperty('overflow');
          p.style.removeProperty('display');
        });

        rev.querySelectorAll('.jdgm-rev__body-read-more, .jdgm-myd-read-more').forEach((el) => {
          el.remove();
        });

        const full =
          rev.dataset.mydFullText ||
          body.dataset.mydFullText ||
          (reviewId && this._reviewBodies[reviewId]) ||
          '';
        const target = body.querySelector('p') || body;
        const current = (target.textContent || '').replace(/\s+/g, ' ').trim();
        const fullNorm = String(full).replace(/\s+/g, ' ').trim();
        if (fullNorm && fullNorm.length > current.length + 2) {
          target.textContent = fullNorm;
        }
      },
      toggleReviewExpand(rev, readMore) {
        if (!rev || !readMore) return;
        const expanded = rev.classList.toggle('is-expanded');
        const body = rev.querySelector('.jdgm-rev__body');
        const reviewId = rev.getAttribute('data-review-id') || '';

        if (expanded) {
          // Reveal any Judge.me truncated / full siblings still hidden
          body
            ?.querySelectorAll(
              '.jdgm-truncate-text, .jdgm-rev__body-full, .jdgm-rev__content-hidden, [hidden]'
            )
            .forEach((el) => {
              el.removeAttribute('hidden');
              el.style.removeProperty('display');
              el.style.removeProperty('visibility');
              el.style.removeProperty('max-height');
              el.style.removeProperty('-webkit-line-clamp');
              el.style.removeProperty('line-clamp');
            });
          if (body) {
            body.classList.remove('jdgm-truncate-ellipsis', 'is-truncated');
            body.style.removeProperty('max-height');
            body.style.removeProperty('-webkit-line-clamp');
            body.style.removeProperty('line-clamp');
            body.style.setProperty('overflow', 'visible', 'important');
            body.style.setProperty('display', 'block', 'important');
            body.style.setProperty('-webkit-line-clamp', 'unset', 'important');
          }

          const full =
            rev.dataset.mydFullText ||
            body?.dataset?.mydFullText ||
            (reviewId && this._reviewBodies[reviewId]) ||
            '';
          const target = body?.querySelector('p') || body;
          const current = (target?.textContent || '').replace(/\s+/g, ' ').trim();
          const fullNorm = String(full).replace(/\s+/g, ' ').trim();
          if (target && fullNorm && fullNorm.length > current.length + 2) {
            target.textContent = fullNorm;
          }
        } else if (body) {
          body.style.removeProperty('overflow');
          body.style.removeProperty('display');
          body.style.removeProperty('-webkit-line-clamp');
          body.style.removeProperty('line-clamp');
        }

        readMore.textContent = expanded ? 'Read less' : 'Read more';
        readMore.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      },
      _runWithoutObs(root, fn) {
        if (!root || typeof fn !== 'function') return;
        root.dataset.mydStyling = '1';
        try {
          fn();
        } finally {
          requestAnimationFrame(() => {
            delete root.dataset.mydStyling;
          });
        }
      },
      bindCollapse(root) {
        if (!root) return;
        // Ensure we render in the expanded state on first paint.
        // Some Judge.me renders may leave `.is-collapsed` on the widget root.
        root.classList.remove('is-collapsed');
        const header = root.querySelector('.jdgm-rev-widg__header');
        const title = root.querySelector('.jdgm-rev-widg__title');
        if (!header || !title) return;

        let titleRow = header.querySelector('.jdgm-rev-widg__title-row');
        if (!titleRow) {
          titleRow = document.createElement('div');
          titleRow.className = 'jdgm-rev-widg__title-row';
          title.before(titleRow);
          titleRow.appendChild(title);
        }

        let btn = titleRow.querySelector('.jdgm-myd-collapse');
        if (!btn) {
          btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'jdgm-myd-collapse';
          btn.setAttribute('aria-label', 'Collapse ratings and reviews');
          btn.setAttribute('aria-expanded', 'true');
          btn.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 15l6-6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          titleRow.appendChild(btn);
          btn.addEventListener('click', () => {
            const collapsed = root.classList.toggle('is-collapsed');
            btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
            btn.setAttribute(
              'aria-label',
              collapsed ? 'Expand ratings and reviews' : 'Collapse ratings and reviews'
            );
          });
        }

        // Mark collapsible regions (gallery + reviews + paginate)
        const body = root.querySelector('.jdgm-rev-widg__body');
        const rowStars = root.querySelector('.jdgm-row-stars');
        [body, rowStars].forEach((el) => {
          if (!el) return;
          // Keep summary visible; collapse gallery siblings + body
          if (!el.classList.contains('jdgm-myd-collapse-body')) {
            el.classList.add('jdgm-myd-collapse-body');
          }
        });
        // Summary stays open — remove collapse class from summary only
        root.querySelector('.jdgm-rev-widg__summary')?.classList.remove('jdgm-myd-collapse-body');

        // More precise: wrap gallery+keywords in collapse, keep summary out
        if (rowStars) {
          rowStars.classList.remove('jdgm-myd-collapse-body');
          [...rowStars.children].forEach((child) => {
            if (child.classList.contains('jdgm-rev-widg__summary')) {
              child.classList.remove('jdgm-myd-collapse-body');
            } else if (!child.classList.contains('jdgm-myd-collapse-body')) {
              child.classList.add('jdgm-myd-collapse-body');
            }
          });
        }
      },
      _appendInOrder(parent, nodes) {
        if (!parent) return;
        const items = nodes.filter(Boolean);
        items.forEach((node, index) => {
          const next = items[index + 1] || null;
          if (node.parentElement === parent && node.nextElementSibling === next) return;
          parent.insertBefore(node, next);
        });
      },
      consolidateStarsRow(root) {
        if (!root) return null;
        const header = root.querySelector('.jdgm-rev-widg__header');
        const rows = [...root.querySelectorAll('.jdgm-row-stars')];
        if (!rows.length) return null;
        const primary = rows[0];
        rows.slice(1).forEach((row) => {
          [...row.children].forEach((child) => {
            if (child.classList.contains('jdgm-widget-actions-wrapper')) {
              // Keep Judge.me's "Write a review" CTA wrapper if present.
              const write = child.querySelector('a.jdgm-write-rev-link, button.jdgm-write-rev-link');
              if (!write) child.remove();
              // If it contains the write CTA, keep it and let the normal append logic below place it.
              if (!write) return;
            }
            if (child.classList.contains('jdgm-rev-widg__summary') && primary.querySelector('.jdgm-rev-widg__summary')) {
              child.remove();
              return;
            }
            primary.appendChild(child);
          });
          row.remove();
        });
        const summary = primary.querySelector('.jdgm-rev-widg__summary');
        const gallery = primary.querySelector('.jdgm-myd-gallery, .jdgm-gallery');
        const keywords = primary.querySelector('.jdgm-keywords');
        this._appendInOrder(primary, [summary, gallery, keywords]);
        // Drop any leftover histograms from prior skin
        primary.querySelectorAll('.jdgm-myd-histogram, .jdgm-histogram').forEach((n) => n.remove());
        return primary;
      },
      styleSummary(root) {
        if (!root) return;
        this.consolidateStarsRow(root);
        const title = root.querySelector('.jdgm-rev-widg__title');
        if (title) title.textContent = 'Ratings and reviews';

        // Lift write CTA out of Judge.me's hidden actions wrapper; placement
        // into the score row happens after the score row is built below.
        const write = root.querySelector('a.jdgm-write-rev-link, button.jdgm-write-rev-link');
        if (write) {
          const actionsWrap = write.closest('.jdgm-widget-actions-wrapper');
          if (actionsWrap?.parentElement) {
            actionsWrap.parentElement.insertBefore(write, actionsWrap);
          }
          write.removeAttribute('hidden');
          write.style.removeProperty('display');
        }
        root.querySelectorAll('.jdgm-widget-actions-wrapper').forEach((n) => n.remove());

        const avgElExisting = root.querySelector('.jdgm-rev-widg__summary-average');
        const avg =
          Number.parseFloat(root.getAttribute('data-average-rating') || '') ||
          Number.parseFloat((avgElExisting?.textContent || '').match(/[\d.]+/)?.[0] || '0');

        let summary = root.querySelector('.jdgm-rev-widg__summary');
        if (!summary) {
          const host = root.querySelector('.jdgm-row-stars') || root.querySelector('.jdgm-rev-widg__header');
          if (host) {
            summary = document.createElement('div');
            summary.className = 'jdgm-rev-widg__summary';
            host.insertBefore(summary, host.firstChild);
          }
        }
        if (!summary) return;

        let inner = summary.querySelector('.jdgm-rev-widg__summary-inner');
        if (!inner) {
          inner = document.createElement('div');
          inner.className = 'jdgm-rev-widg__summary-inner';
          while (summary.firstChild) inner.appendChild(summary.firstChild);
          summary.appendChild(inner);
        }

        let avgEl = inner.querySelector('.jdgm-rev-widg__summary-average');
        if (!avgEl && avg > 0) {
          avgEl = document.createElement('div');
          avgEl.className = 'jdgm-rev-widg__summary-average';
          inner.insertBefore(avgEl, inner.firstChild);
        }
        if (avgEl && avg > 0) {
          avgEl.textContent = (Math.round(avg * 10) / 10).toFixed(1);
        }

        if (inner) {
          let scoreRow = inner.querySelector('.jdgm-myd-score-row');
          if (!scoreRow) {
            scoreRow = document.createElement('div');
            scoreRow.className = 'jdgm-myd-score-row';
            inner.insertBefore(scoreRow, inner.firstChild);
          }
          if (avgEl && avgEl.parentElement !== scoreRow) {
            scoreRow.appendChild(avgEl);
          }
          let scoreStar = scoreRow.querySelector('.jdgm-myd-score-star');
          // Decorative ★ is only for products that already have a rating
          if (avg > 0) {
            if (!scoreStar) {
              scoreStar = document.createElement('span');
              scoreStar.className = 'jdgm-myd-score-star';
              scoreStar.setAttribute('aria-hidden', 'true');
              scoreStar.textContent = '★';
              scoreRow.appendChild(scoreStar);
            }
          } else if (scoreStar) {
            scoreStar.remove();
          }
          const sentiment = this.sentimentLabel(avg);
          let pill = scoreRow.querySelector('.jdgm-myd-sentiment');
          if (sentiment) {
            if (!pill) {
              pill = document.createElement('span');
              pill.className = 'jdgm-myd-sentiment';
              scoreRow.appendChild(pill);
            }
            pill.textContent = sentiment;
          } else if (pill) {
            pill.remove();
          }
        }

        const count =
          Number.parseInt(root.getAttribute('data-number-of-reviews') || '0', 10) ||
          Number.parseInt(
            (root.querySelector('.jdgm-rev-widg__summary-text')?.textContent || '').replace(/\D/g, ''),
            10
          );
        let summaryText = root.querySelector('.jdgm-rev-widg__summary-text');
        if (!summaryText && inner && count > 0) {
          summaryText = document.createElement('div');
          summaryText.className = 'jdgm-rev-widg__summary-text';
          inner.appendChild(summaryText);
        }
        const scoreRowForSummary = inner?.querySelector('.jdgm-myd-score-row');
        if (summaryText && count > 0) {
          if (summaryText.parentElement !== inner && inner) inner.appendChild(summaryText);
          const summaryHtml = `based on ${count.toLocaleString('en-IN')} ratings by <span class="jdgm-myd-verified-inline">Verified Buyers${window.MYD_VERIFIED_BADGE_ICON}</span>`;
          if (summaryText.innerHTML !== summaryHtml) summaryText.innerHTML = summaryHtml;
        } else if (summaryText && count === 0 && scoreRowForSummary) {
          // Empty state: keep prompt + Write CTA on one full-width row
          if (summaryText.parentElement !== scoreRowForSummary) {
            scoreRowForSummary.insertBefore(summaryText, scoreRowForSummary.firstChild);
          }
        }

        this.bindCollapse(root);

        // Far-right of score row: 4.8 ★ Excellent …… [Write a review]
        const scoreRow = root.querySelector('.jdgm-myd-score-row');
        const writeBtn = root.querySelector('a.jdgm-write-rev-link, button.jdgm-write-rev-link');
        if (scoreRow && writeBtn) {
          writeBtn.classList.remove('jdgm-myd-collapse-body');
          writeBtn.classList.add('jdgm-myd-write-review');
          if (writeBtn.parentElement !== scoreRow || scoreRow.lastElementChild !== writeBtn) {
            scoreRow.appendChild(writeBtn);
          }
          if (!/cancel/i.test(writeBtn.textContent || '')) {
            writeBtn.textContent = 'Write a review';
          }
        }

        this.setLoadMoreLabel(root);

        const host = root.querySelector('.jdgm-row-stars');
        if (host && !host.querySelector('.jdgm-myd-gallery')) {
          host.dataset.mydMedia = '';
          this.styleMedia(root);
        }
      },
      openWriteReview(root, score) {
        if (!root) return;
        const write =
          root.querySelector('a.jdgm-write-rev-link, button.jdgm-write-rev-link') ||
          document.querySelector('a.jdgm-write-rev-link, button.jdgm-write-rev-link');

        const mount = () => {
          const wrap = this.dedupeReviewForms(root);
          if (!wrap) return false;
          this.mountReviewFormInModal(wrap, score);
          return true;
        };

        if (mount()) return;

        // Ask Judge.me to build the form once, then lift it into the modal
        if (write) {
          this._mydAllowNativeWrite = true;
          try {
            write.dispatchEvent(
              new MouseEvent('click', { bubbles: true, cancelable: true, view: window })
            );
          } catch (err) {
            /* ignore */
          }
          this._mydAllowNativeWrite = false;
        }

        if (!mount()) {
          setTimeout(() => mount(), 50);
          setTimeout(() => mount(), 200);
          setTimeout(() => mount(), 500);
        }
      },
      ensureReviewModalChrome(modal) {
        if (!modal) return;
        const sheet = modal.querySelector('.jdgm-myd-review-modal__sheet');
        if (!sheet) return;
        if (!sheet.querySelector('.jdgm-myd-review-modal__grabber')) {
          const grabber = document.createElement('div');
          grabber.className = 'jdgm-myd-review-modal__grabber';
          grabber.setAttribute('data-myd-sheet-handle', '');
          grabber.setAttribute('aria-hidden', 'true');
          grabber.innerHTML = '<span class="jdgm-myd-review-modal__handle"></span>';
          sheet.insertBefore(grabber, sheet.firstChild);
        }
        const head = sheet.querySelector('.jdgm-myd-review-modal__head');
        if (head) head.setAttribute('data-myd-sheet-header', '');
        this.bindReviewModalDrag(modal);
      },
      bindReviewModalDrag(modal) {
        const sheet = modal?.querySelector('.jdgm-myd-review-modal__sheet');
        const overlay = modal?.querySelector('.jdgm-myd-review-modal__overlay');
        if (!sheet || sheet.__mydDragBound) return;
        if (!window.MYDSheetDrag || typeof window.MYDSheetDrag.bind !== 'function') {
          setTimeout(() => this.bindReviewModalDrag(modal), 200);
          return;
        }
        window.MYDSheetDrag.bind(sheet, {
          onClose: () => this.closeWriteReview(),
          overlay,
          handleSelector: '.jdgm-myd-review-modal__grabber, .jdgm-myd-review-modal__handle',
          headerSelector: '.jdgm-myd-review-modal__head, [data-myd-sheet-header]',
          scrollSelector: '.jdgm-myd-review-modal__body',
        });
      },
      ensureReviewModal() {
        let modal = document.getElementById('myd-jdgm-review-modal');
        if (modal) {
          this.ensureReviewModalChrome(modal);
          return modal;
        }

        modal = document.createElement('div');
        modal.id = 'myd-jdgm-review-modal';
        modal.className = 'jdgm-myd-review-modal';
        modal.hidden = true;
        modal.innerHTML = `
          <div class="jdgm-myd-review-modal__overlay" data-myd-review-close></div>
          <div class="jdgm-myd-review-modal__sheet" role="dialog" aria-modal="true" aria-label="Write a review">
            <div class="jdgm-myd-review-modal__grabber" data-myd-sheet-handle aria-hidden="true">
              <span class="jdgm-myd-review-modal__handle"></span>
            </div>
            <div class="jdgm-myd-review-modal__head" data-myd-sheet-header>
              <h3 class="jdgm-myd-review-modal__title">Write a review</h3>
              <button type="button" class="jdgm-myd-review-modal__close myd-popup-close" data-myd-review-close aria-label="Close">${window.MYD_POPUP_CLOSE_ICON}</button>
            </div>
            <div class="jdgm-myd-review-modal__body"></div>
          </div>
        `;
        document.body.appendChild(modal);

        const close = (event) => {
          event?.preventDefault?.();
          this.closeWriteReview();
        };
        modal.querySelectorAll('[data-myd-review-close]').forEach((el) => {
          el.addEventListener('click', close);
        });
        if (!this._mydReviewEscBound) {
          this._mydReviewEscBound = true;
          document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && !modal.hidden) this.closeWriteReview();
          });
          document.addEventListener(
            'click',
            (event) => {
              const cancel = event.target.closest?.(
                '#myd-jdgm-review-modal .jdgm-cancel-rev, #myd-jdgm-review-modal a.jdgm-cancel-rev, #myd-jdgm-review-modal button.jdgm-cancel-rev'
              );
              if (!cancel) return;
              event.preventDefault();
              event.stopPropagation();
              this.closeWriteReview();
            },
            true
          );
        }
        this.bindReviewModalDrag(modal);
        return modal;
      },
      dedupeReviewForms(root) {
        const wrappers = [...document.querySelectorAll('.jdgm-form-wrapper')];
        const forms = [
          ...document.querySelectorAll('form.jdgm-form:not(.jdgm-question-form)'),
        ];

        let keep =
          (root && root.querySelector('.jdgm-form-wrapper')) ||
          wrappers.find((w) => w.querySelector('form.jdgm-form:not(.jdgm-question-form)')) ||
          null;

        if (!keep && forms[0]) {
          keep = forms[0].closest('.jdgm-form-wrapper') || forms[0];
        }

        wrappers.forEach((wrap) => {
          if (wrap === keep || keep?.contains?.(wrap)) return;
          if (wrap.closest('#myd-jdgm-review-modal')) return;
          wrap.setAttribute('data-myd-dup-form', '1');
          wrap.setAttribute('hidden', '');
          wrap.style.setProperty('display', 'none', 'important');
        });

        forms.forEach((form) => {
          if (keep && (keep === form || keep.contains(form))) return;
          if (form.closest('#myd-jdgm-review-modal')) return;
          form.setAttribute('data-myd-dup-form', '1');
          form.setAttribute('hidden', '');
          form.style.setProperty('display', 'none', 'important');
        });

        // Hide ask-a-question forms while writing a review
        document.querySelectorAll('.jdgm-question-form').forEach((question) => {
          question.setAttribute('hidden', '');
          question.style.setProperty('display', 'none', 'important');
        });

        return keep;
      },
      mountReviewFormInModal(wrap, score) {
        if (!wrap) return;
        const modal = this.ensureReviewModal();
        const body = modal.querySelector('.jdgm-myd-review-modal__body');
        if (!body) return;

        if (!this._reviewFormHome || !this._reviewFormHome.isConnected) {
          const marker = document.createComment('myd-jdgm-form-home');
          wrap.parentNode?.insertBefore(marker, wrap);
          this._reviewFormHome = marker;
        }

        wrap.classList.add('jdgm-myd-in-modal');
        wrap.removeAttribute('data-myd-dup-form');
        wrap.removeAttribute('hidden');
        wrap.style.setProperty('display', 'block', 'important');
        body.appendChild(wrap);

        // Only one form node inside the modal
        const forms = [
          ...wrap.querySelectorAll('form.jdgm-form:not(.jdgm-question-form)'),
          ...(wrap.matches?.('form.jdgm-form') ? [wrap] : []),
        ];
        forms.forEach((form, index) => {
          if (index === 0) {
            form.removeAttribute('data-myd-dup-form');
            form.removeAttribute('hidden');
            form.style.removeProperty('display');
            return;
          }
          form.setAttribute('data-myd-dup-form', '1');
          form.setAttribute('hidden', '');
          form.style.setProperty('display', 'none', 'important');
        });

        const form = forms[0] || wrap.querySelector('form.jdgm-form:not(.jdgm-question-form)');
        if (score && form) {
          const star = form.querySelector(`.jdgm-form__rating [data-alt="${score}"]`);
          if (star) {
            star.click();
          } else {
            const scoreInput = form.querySelector('input[name="score"]');
            if (scoreInput) scoreInput.value = String(score);
            form.querySelectorAll('.jdgm-form__rating .jdgm-star').forEach((el, i) => {
              el.classList.toggle('jdgm--on', i < score);
              el.classList.toggle('jdgm--off', i >= score);
            });
          }
        }

        // Hide Judge.me's own form title — modal header already says Write a review
        wrap.querySelectorAll('.jdgm-form__title').forEach((title) => {
          title.setAttribute('data-myd-modal-hidden-title', '1');
          title.style.setProperty('display', 'none', 'important');
        });

        this.ensureAiReviewDraft(wrap);

        // Mark empty upload tiles only — never touch attached preview boxes
        wrap.querySelectorAll('.jdgm-picture-fieldset__box').forEach((box) => {
          const hasFileInput = !!box.querySelector('input[type="file"], .jdgm-picture-fieldset__input');
          const hasDelete = !!box.querySelector('.jdgm-picture-fieldset__delete');
          if (hasFileInput && !hasDelete) {
            box.classList.add('jdgm-picture-fieldset__box--input');
            const wrapper = box.querySelector('.jdgm-picture-fieldset__box-wrapper');
            if (wrapper) {
              wrapper.style.setProperty('background-image', 'none', 'important');
              wrapper.style.removeProperty('background');
            }
            // Hide Judge.me empty-state glyph / broken <img> — keep file input
            box.querySelectorAll('img, svg, i, .jdgm-picture-fieldset__icon, [class*="icon"]').forEach((el) => {
              if (el.matches?.('input')) return;
              el.setAttribute('hidden', '');
              el.setAttribute('aria-hidden', 'true');
              if (el.tagName === 'IMG') {
                el.removeAttribute('src');
                el.removeAttribute('srcset');
              }
              el.style.setProperty('display', 'none', 'important');
            });
          } else {
            box.classList.remove('jdgm-picture-fieldset__box--input');
          }
        });

        // Show first, then replay sheet animation (overlay stays solid — no opacity anim)
        modal.classList.remove('is-closing');
        modal.hidden = false;
        document.documentElement.classList.add('jdgm-myd-review-modal-open');

        const sheet = modal.querySelector('.jdgm-myd-review-modal__sheet');
        const overlay = modal.querySelector('.jdgm-myd-review-modal__overlay');
        if (overlay) {
          overlay.style.opacity = '1';
          overlay.style.display = 'block';
        }
        if (sheet) {
          sheet.style.animation = 'none';
          void sheet.offsetWidth;
          sheet.style.animation = '';
        }

        document
          .querySelectorAll('a.jdgm-write-rev-link, button.jdgm-write-rev-link')
          .forEach((btn) => {
            if (!/cancel/i.test(btn.dataset.mydWriteLabel || '')) {
              btn.dataset.mydWriteLabel = (btn.textContent || 'Write a review').trim();
            }
            btn.textContent = 'Write a review';
            btn.setAttribute('aria-expanded', 'true');
          });
      },
      getReviewProductContext() {
        const el = document.getElementById('myd-review-product-context');
        if (el) {
          try {
            const data = JSON.parse(el.textContent || '{}');
            if (data && data.title) return data;
          } catch (err) {
            /* ignore */
          }
        }
        const title =
          document.querySelector('.product__title')?.textContent?.trim() ||
          document.querySelector('meta[property="og:title"]')?.content ||
          document.title ||
          '';
        const type = document.querySelector('[data-product-type]')?.textContent?.trim() || '';
        const description =
          document.querySelector('.product__description')?.textContent?.trim() ||
          document.querySelector('meta[name="description"]')?.content ||
          '';
        return {
          title,
          type,
          description: description.slice(0, 1200),
          fit: '',
          handle: '',
        };
      },
      getAiReviewClientKey() {
        const storageKey = 'myd_ai_review_client_key';
        try {
          let key = localStorage.getItem(storageKey);
          if (!key || !/^[a-zA-Z0-9_-]{8,64}$/.test(key)) {
            key =
              (crypto.randomUUID && crypto.randomUUID().replace(/-/g, '')) ||
              `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
            localStorage.setItem(storageKey, key);
          }
          return key;
        } catch (err) {
          return `s${Date.now().toString(36)}`;
        }
      },
      getAiReviewUsageStore() {
        const storageKey = 'myd_ai_review_usage_v1';
        const empty = () => ({ day: '', dayCount: 0, stamps: [] });
        try {
          const raw = localStorage.getItem(storageKey);
          if (!raw) return empty();
          const parsed = JSON.parse(raw);
          if (!parsed || typeof parsed !== 'object') return empty();
          return {
            day: String(parsed.day || ''),
            dayCount: Number(parsed.dayCount) || 0,
            stamps: Array.isArray(parsed.stamps) ? parsed.stamps.map(Number).filter(Boolean) : [],
          };
        } catch (err) {
          return empty();
        }
      },
      saveAiReviewUsageStore(store) {
        try {
          localStorage.setItem('myd_ai_review_usage_v1', JSON.stringify(store));
        } catch (err) {
          /* ignore quota / private mode */
        }
      },
      checkAiReviewClientLimits() {
        const now = Date.now();
        const day = new Date().toISOString().slice(0, 10);
        const store = this.getAiReviewUsageStore();
        if (store.day !== day) {
          store.day = day;
          store.dayCount = 0;
          store.stamps = [];
        }
        store.stamps = store.stamps.filter((ts) => now - ts < 60 * 1000);
        if (store.stamps.length >= 2) {
          return {
            ok: false,
            message: 'You can only generate 2 AI drafts per minute. Please wait a moment and try again.',
            error: 'rate_limit_minute',
          };
        }
        if (store.dayCount >= 5) {
          return {
            ok: false,
            message: 'Daily limit reached — you can generate up to 5 AI drafts per day. Try again tomorrow.',
            error: 'rate_limit_day',
          };
        }
        return { ok: true, store };
      },
      recordAiReviewClientUse() {
        const now = Date.now();
        const day = new Date().toISOString().slice(0, 10);
        const store = this.getAiReviewUsageStore();
        if (store.day !== day) {
          store.day = day;
          store.dayCount = 0;
          store.stamps = [];
        }
        store.stamps = store.stamps.filter((ts) => now - ts < 60 * 1000);
        store.stamps.push(now);
        store.dayCount += 1;
        this.saveAiReviewUsageStore(store);
      },
      ensureAiReviewGeneratingOverlay() {
        let overlay = document.getElementById('myd-ai-review-generating');
        if (overlay) return overlay;
        overlay = document.createElement('div');
        overlay.id = 'myd-ai-review-generating';
        overlay.className = 'jdgm-myd-ai-generating';
        overlay.hidden = true;
        overlay.setAttribute('role', 'status');
        overlay.setAttribute('aria-live', 'polite');
        overlay.innerHTML = `
          <div class="jdgm-myd-ai-generating__card">
            <div class="jdgm-myd-ai-generating__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" focusable="false">
                <path d="M12 3l1.2 4.2L17.5 8.5 13.2 9.8 12 14l-1.2-4.2L6.5 8.5l4.3-1.3L12 3z" fill="#ffcc00"/>
                <path d="M18.5 13.5l.7 2.3 2.3.7-2.3.7-.7 2.3-.7-2.3-2.3-.7 2.3-.7.7-2.3z" fill="#ffcc00"/>
              </svg>
            </div>
            <p class="jdgm-myd-ai-generating__title">Generating draft…</p>
            <p class="jdgm-myd-ai-generating__sub">Preparing a professional review for this product</p>
          </div>
        `;
        document.body.appendChild(overlay);
        return overlay;
      },
      showAiReviewGenerating(show) {
        const overlay = this.ensureAiReviewGeneratingOverlay();
        overlay.hidden = !show;
        overlay.classList.toggle('is-open', Boolean(show));
        document.documentElement.classList.toggle('jdgm-myd-ai-generating-open', Boolean(show));
      },
      aiReviewDraftErrorMessage(data, fallbackCode) {
        if (data?.message) return String(data.message);
        const code = data?.error || fallbackCode || '';
        if (code === 'rate_limit_minute') {
          return 'You can only generate 2 AI drafts per minute. Please wait a moment and try again.';
        }
        if (code === 'rate_limit_day') {
          return 'Daily limit reached — you can generate up to 5 AI drafts per day. Try again tomorrow.';
        }
        if (code === 'openai_not_configured' || code === 'ai_review_disabled') {
          return 'AI drafting is turned off right now.';
        }
        if (code === 'missing_product_title') {
          return 'Could not read product details for this review.';
        }
        return 'Could not draft a review. Try again.';
      },
      ensureAiReviewDraft(wrap) {
        if (!wrap || wrap.querySelector('[data-myd-ai-review-draft]')) return;
        if (window.MYD_AI_REVIEW_DRAFT_ENABLED === false) return;

        const form = wrap.querySelector('form.jdgm-form:not(.jdgm-question-form)') || wrap;
        if (!form) return;

        const rating = form.querySelector('.jdgm-form__rating');
        if (!rating) return;

        let row = form.querySelector('.jdgm-myd-rating-row');
        if (!row) {
          row = document.createElement('div');
          row.className = 'jdgm-myd-rating-row';
          rating.parentNode?.insertBefore(row, rating);
          row.appendChild(rating);
        }

        const bar = document.createElement('div');
        bar.className = 'jdgm-myd-ai-draft';
        bar.setAttribute('data-myd-ai-review-draft', '1');
        bar.hidden = true;
        bar.innerHTML = `
          <button type="button" class="jdgm-myd-ai-draft__btn" data-myd-ai-draft-btn aria-label="Draft review with AI">
            <span class="jdgm-myd-ai-draft__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" focusable="false">
                <path d="M12 3l1.2 4.2L17.5 8.5 13.2 9.8 12 14l-1.2-4.2L6.5 8.5l4.3-1.3L12 3z" fill="#ffcc00"/>
                <path d="M18.5 13.5l.7 2.3 2.3.7-2.3.7-.7 2.3-.7-2.3-2.3-.7 2.3-.7.7-2.3z" fill="#ffcc00"/>
              </svg>
            </span>
            <span class="jdgm-myd-ai-draft__label">Draft with AI</span>
          </button>
        `;

        const status = document.createElement('p');
        status.className = 'jdgm-myd-ai-draft__status';
        status.setAttribute('data-myd-ai-draft-status', '');
        status.setAttribute('role', 'alert');
        status.hidden = true;

        row.appendChild(bar);
        row.parentNode?.insertBefore(status, row.nextSibling);

        const btn = bar.querySelector('[data-myd-ai-draft-btn]');
        btn?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.runAiReviewDraft(form, btn, status);
        });

        this.resolveAiReviewDraftVisibility(bar, status);
      },
      async resolveAiReviewDraftVisibility(bar, status) {
        if (!bar) return;
        const api =
          window.MYD_REVIEW_DRAFT_API ||
          'https://z01mbz6pjg.execute-api.ap-south-1.amazonaws.com/review-draft';
        try {
          const res = await fetch(api, { method: 'GET', headers: { accept: 'application/json' } });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data.enabled) {
            bar.hidden = false;
            return;
          }
        } catch (err) {
          /* hide when status check fails */
        }
        bar.remove();
        status?.remove();
      },
      async runAiReviewDraft(form, btn, status) {
        if (!form || !btn || btn.disabled) return;
        const api =
          window.MYD_REVIEW_DRAFT_API ||
          'https://z01mbz6pjg.execute-api.ap-south-1.amazonaws.com/review-draft';
        const product = this.getReviewProductContext();
        if (!product.title) {
          if (status) {
            status.hidden = false;
            status.textContent = 'Could not read product details for this review.';
            status.classList.add('is-error');
          }
          return;
        }

        const clientLimit = this.checkAiReviewClientLimits();
        if (!clientLimit.ok) {
          if (status) {
            status.hidden = false;
            status.classList.add('is-error');
            status.textContent = clientLimit.message;
          }
          return;
        }

        const scoreInput = form.querySelector('input[name="score"]');
        const rating = scoreInput ? Number(scoreInput.value) : 0;
        if (rating >= 1 && rating <= 5) product.rating = rating;

        const customerId = String(window.MYD_REVIEW_CUSTOMER_ID || '').trim();
        if (/^\d{5,}$/.test(customerId)) product.customer_id = customerId;
        product.client_key = this.getAiReviewClientKey();

        const label = btn.querySelector('.jdgm-myd-ai-draft__label');
        const prev = label?.textContent || 'Draft with AI';
        btn.disabled = true;
        btn.classList.add('is-loading');
        if (label) label.textContent = 'Drafting…';
        if (status) {
          status.hidden = true;
          status.classList.remove('is-error');
          status.textContent = '';
        }
        this.showAiReviewGenerating(true);

        try {
          const res = await fetch(api, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(product),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data?.title || !data?.body) {
            const fail = new Error(data?.error || 'draft_failed');
            fail.payload = data;
            throw fail;
          }

          this.recordAiReviewClientUse();

          const titleInput =
            form.querySelector('input[name="review_title"]') ||
            form.querySelector('input[name="title"]');
          const bodyInput =
            form.querySelector('textarea[name="review_body"]') ||
            form.querySelector('textarea[name="body"]') ||
            form.querySelector('textarea');

          if (titleInput) {
            titleInput.value = data.title;
            titleInput.dispatchEvent(new Event('input', { bubbles: true }));
            titleInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          if (bodyInput) {
            bodyInput.value = data.body;
            bodyInput.dispatchEvent(new Event('input', { bubbles: true }));
            bodyInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          if (status) {
            status.hidden = false;
            status.classList.remove('is-error');
            status.textContent = 'Draft ready — edit anything before you submit.';
          }
        } catch (err) {
          if (status) {
            status.hidden = false;
            status.classList.add('is-error');
            status.textContent = this.aiReviewDraftErrorMessage(err?.payload, err?.message);
          }
        } finally {
          this.showAiReviewGenerating(false);
          btn.disabled = false;
          btn.classList.remove('is-loading');
          if (label) label.textContent = prev;
        }
      },
      closeWriteReview() {
        const modal = document.getElementById('myd-jdgm-review-modal');
        if (!modal || modal.hidden) return;
        if (modal.classList.contains('is-closing')) return;

        const finish = () => {
          const body = modal.querySelector('.jdgm-myd-review-modal__body');
          const wrap =
            body?.querySelector('.jdgm-form-wrapper, form.jdgm-form:not(.jdgm-question-form)') ||
            null;

          if (wrap) {
            wrap.classList.remove('jdgm-myd-in-modal');
            wrap.querySelectorAll('[data-myd-modal-hidden-title]').forEach((title) => {
              title.removeAttribute('data-myd-modal-hidden-title');
              title.style.removeProperty('display');
            });
            if (this._reviewFormHome?.parentNode) {
              this._reviewFormHome.parentNode.insertBefore(wrap, this._reviewFormHome);
            }
            wrap.setAttribute('hidden', '');
            wrap.style.setProperty('display', 'none', 'important');
          }

          modal.classList.remove('is-closing');
          modal.hidden = true;
          document.documentElement.classList.remove('jdgm-myd-review-modal-open');

          document
            .querySelectorAll('a.jdgm-write-rev-link, button.jdgm-write-rev-link')
            .forEach((btn) => {
              btn.textContent = btn.dataset.mydWriteLabel || 'Write a review';
              btn.setAttribute('aria-expanded', 'false');
            });
        };

        modal.classList.add('is-closing');
        const sheet = modal.querySelector('.jdgm-myd-review-modal__sheet');
        if (!sheet) {
          finish();
          return;
        }

        let done = false;
        const onEnd = (event) => {
          if (done) return;
          if (event && event.target !== sheet) return;
          done = true;
          sheet.removeEventListener('animationend', onEnd);
          finish();
        };
        sheet.addEventListener('animationend', onEnd);
        setTimeout(onEnd, 320);
      },
      bindWriteReviewFix(root) {
        if (!root || root.dataset.mydWriteFix === '1') return;
        root.dataset.mydWriteFix = '1';

        root.addEventListener(
          'click',
          (event) => {
            const write = event.target.closest?.(
              'a.jdgm-write-rev-link, button.jdgm-write-rev-link'
            );
            const cancel = event.target.closest?.(
              '.jdgm-cancel-rev, a.jdgm-cancel-rev, button.jdgm-cancel-rev'
            );

            if (cancel) {
              event.preventDefault();
              event.stopPropagation();
              this.closeWriteReview();
              return;
            }

            if (!write || !root.contains(write)) return;
            if (this._mydAllowNativeWrite) return;

            event.preventDefault();
            event.stopPropagation();

            const modal = document.getElementById('myd-jdgm-review-modal');
            if (modal && !modal.hidden) {
              this.closeWriteReview();
              return;
            }
            this.openWriteReview(root);
          },
          true
        );
      },
      bindSummaryStarWrite(root) {
        // Flipkart summary uses score + sentiment, not interactive stars
        if (!root) return;
        this.bindWriteReviewFix(root);
      },
      reviewsScope(root) {
        if (!root) return document;
        if (root.classList?.contains('jdgm-myd-reviews-in-drawer')) {
          return (
            document.querySelector('#myd-jdgm-reviews-drawer .jdgm-rev-widg__body.jdgm-myd-in-drawer') ||
            root
          );
        }
        return root;
      },
      styleReview(rev) {
        if (!rev) return;
        const inDrawer = Boolean(rev.closest('.jdgm-myd-reviews-drawer, .jdgm-myd-in-drawer'));
        // Always strip native/custom read-more in full-list contexts
        rev.querySelectorAll('.jdgm-rev__body-read-more').forEach((el) => el.remove());
        // Name/location belong in footer only — never beside the star badge
        rev
          .querySelectorAll(
            '.jdgm-rev__author-wrapper, .jdgm-rev__location, .jdgm-rev__buyer-badge-wrapper'
          )
          .forEach((el) => {
            el.style.setProperty('display', 'none', 'important');
          });
        if (inDrawer) {
          rev.querySelectorAll('.jdgm-myd-read-more').forEach((el) => el.remove());
          this.expandReviewBodyFull(rev);
        }
        if (rev.dataset.mydStyled === '1') {
          const footer = rev.querySelector('.jdgm-myd-rev-footer');
          this.fixReviewDate(rev, null, footer);
          this.attachReviewCardPics(rev);
          return;
        }
        const reviewId = rev.getAttribute('data-review-id');
        const body = rev.querySelector('.jdgm-rev__body');
        const readMore = body?.querySelector('.jdgm-rev__body-read-more');
        // Don't click Judge.me's Read more — it can re-render mid-enhance and leave
        // the widget stuck at opacity:0 (missing .jdgm-myd-ready). Hide the link and
        // reveal any truncated/full siblings instead.
        if (readMore) {
          body
            ?.querySelectorAll(
              '.jdgm-truncate-text, .jdgm-rev__body-full, .jdgm-rev__content-hidden, [hidden]'
            )
            .forEach((el) => {
              el.removeAttribute('hidden');
              el.style.removeProperty('display');
              el.style.removeProperty('visibility');
              el.style.removeProperty('max-height');
              el.style.removeProperty('-webkit-line-clamp');
            });
          if (body) {
            body.style.removeProperty('max-height');
            body.style.removeProperty('-webkit-line-clamp');
            body.classList.remove('jdgm-truncate-ellipsis', 'is-truncated');
          }
          readMore.remove();
        }
        const visibleBody = (body?.textContent || '').trim();
        const excerpt = reviewId ? this._reviewExcerpts[reviewId] : '';
        if (body && !visibleBody && excerpt) {
          const paragraph = body.querySelector('p') || body;
          paragraph.insertBefore(document.createTextNode(excerpt), paragraph.firstChild);
        }
        // Keep full copy for carousel Read more expand (Judge.me may re-truncate)
        const fullBody = (body?.textContent || '').replace(/\s+/g, ' ').trim();
        if (fullBody) {
          if (reviewId) this._reviewBodies[reviewId] = fullBody;
          if (body) body.dataset.mydFullText = fullBody;
          rev.dataset.mydFullText = fullBody;
        } else if (reviewId && this._reviewBodies[reviewId] && body) {
          const paragraph = body.querySelector('p') || body;
          if (!(paragraph.textContent || '').trim()) {
            paragraph.textContent = this._reviewBodies[reviewId];
          }
          body.dataset.mydFullText = this._reviewBodies[reviewId];
          rev.dataset.mydFullText = this._reviewBodies[reviewId];
        }
        if (inDrawer) this.expandReviewBodyFull(rev);

        const header = rev.querySelector('.jdgm-rev__header');
        const content = rev.querySelector('.jdgm-rev__content');
        const title = rev.querySelector('.jdgm-rev__title');
        const author =
          rev.querySelector('.jdgm-rev__author') ||
          rev.querySelector('.jdgm-rev__author-wrapper .jdgm-rev__author');
        const timestamp = rev.querySelector('.jdgm-rev__timestamp');
        const actions = rev.querySelector('.jdgm-rev__actions');
        const score = this.reviewScore(rev);

        if (header) {
          let badge = header.querySelector('.jdgm-myd-rev-badge');
          if (!badge && score > 0) {
            badge = document.createElement('span');
            badge.className = 'jdgm-myd-rev-badge';
            badge.innerHTML = `${score}<span class="jdgm-myd-rev-badge__star" aria-hidden="true">★</span>`;
            header.insertBefore(badge, header.firstChild);
          } else if (badge && score > 0) {
            badge.innerHTML = `${score}<span class="jdgm-myd-rev-badge__star" aria-hidden="true">★</span>`;
          }

          if (title) {
            if (title.parentElement !== header) header.appendChild(title);
            const titleText = (title.textContent || '').trim();
            if (titleText) {
              // Judge.me can return titles in ALL CAPS; convert that to sentence case
              // e.g. "LOVED IT WITH ZERO REGRETS" -> "Loved it with zero regrets"
              if (titleText === titleText.toUpperCase()) {
                title.textContent = titleText.charAt(0).toUpperCase() + titleText.slice(1).toLowerCase();
              } else {
                title.textContent = titleText;
              }
            }
          }
        }

        let footer = rev.querySelector('.jdgm-myd-rev-footer');
        if (!footer) {
          footer = document.createElement('div');
          footer.className = 'jdgm-myd-rev-footer';
          (content || header || rev).after(footer);
        }

        if (timestamp) {
          this.fixReviewDate(rev, timestamp, footer);
        }

        let authorBlock = footer.querySelector('.jdgm-myd-rev-author');
        if (!authorBlock) {
          authorBlock = document.createElement('div');
          authorBlock.className = 'jdgm-myd-rev-author';
          footer.appendChild(authorBlock);
        } else if (authorBlock.parentElement === footer) {
          footer.appendChild(authorBlock);
        }
        const authorName = (author?.textContent || '').trim() || 'Customer';
        const verified =
          rev.getAttribute('data-verified-buyer') === 'true' ||
          !!rev.querySelector('.jdgm-rev__buyer-badge');
        const escape = (s) =>
          String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
        authorBlock.innerHTML = `
          <span class="jdgm-myd-rev-author__name">${escape(authorName)}</span>
          ${verified ? `<span class="jdgm-myd-rev-author__verified">Verified customer${window.MYD_VERIFIED_BADGE_ICON}</span>` : ''}
        `;

        if (actions && actions.parentElement !== footer) {
          footer.insertBefore(actions, authorBlock);
        }

        this.attachReviewCardPics(rev);
        rev.dataset.mydStyled = '1';
      },
      styleReviews(root) {
        const scope = this.reviewsScope(root);
        scope?.querySelectorAll?.('.jdgm-rev').forEach((rev) => this.styleReview(rev));
        this.hydrateReviewPics(scope);
      },
      indexReviewPhotos(photos) {
        if (!this._photosByReviewId) this._photosByReviewId = Object.create(null);
        (photos || []).forEach((item) => {
          const photo = this.normalizePhotoItem(item);
          if (!photo?.reviewId || !photo.full) return;
          const list =
            this._photosByReviewId[photo.reviewId] ||
            (this._photosByReviewId[photo.reviewId] = []);
          if (list.some((existing) => existing.full === photo.full)) return;
          list.push(photo);
        });
      },
      photosForReview(reviewId) {
        if (!reviewId) return [];
        const indexed = this._photosByReviewId?.[reviewId];
        if (indexed?.length) return indexed;
        Object.values(this._mediaCache || {}).forEach((summary) => {
          this.indexReviewPhotos(summary?.photos);
        });
        return this._photosByReviewId?.[reviewId] || [];
      },
      /** Judge.me default sort leaves .jdgm-rev__pics empty — fill from with_media cache. */
      attachReviewCardPics(rev) {
        if (!rev) return;
        const picsEl = rev.querySelector('.jdgm-rev__pics');
        const hasLink = !!picsEl?.querySelector('a.jdgm-rev__pic-link, img.jdgm-rev__pic-img');
        if (hasLink) {
          this.hydrateReviewPics(rev);
          return;
        }
        const reviewId = rev.getAttribute('data-review-id');
        const photos = this.photosForReview(reviewId);
        if (!photos.length) return;

        let host = picsEl;
        if (!host) {
          host = document.createElement('div');
          host.className = 'jdgm-rev__pics';
          const body = rev.querySelector('.jdgm-rev__body');
          const content = rev.querySelector('.jdgm-rev__content');
          const footer = rev.querySelector('.jdgm-myd-rev-footer');
          if (body) body.after(host);
          else if (footer) footer.before(host);
          else if (content) content.appendChild(host);
          else rev.appendChild(host);
        }

        const escape = (s) =>
          String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
        const author = escape(
          (rev.querySelector('.jdgm-rev__author')?.textContent || 'Customer').trim() || 'Customer'
        );
        host.innerHTML = photos
          .map((photo, i) => {
            const thumb = escape(this.withJudgeMeWidth(photo.url || photo.full, 160));
            const full = escape(this.withJudgeMeWidth(photo.full || photo.url, 800));
            return `<a class="jdgm-rev__pic-link" href="${full}" data-mfp-src="${full}" target="_blank" rel="nofollow" aria-label="${author} review photo ${i + 1}"><img class="jdgm-rev__pic-img" alt="${author} review photo" src="${thumb}" loading="lazy"></a>`;
          })
          .join('');
      },
      /** Judge.me leaves pics on data-src until its lazy loader runs — promote early. */
      hydrateReviewPics(root) {
        const scope = root || document;
        scope.querySelectorAll?.('.jdgm-rev__pic-img[data-src], a.jdgm-rev__pic-link img[data-src]').forEach((img, i) => {
          const dataSrc = img.getAttribute('data-src');
          if (!dataSrc) return;
          const cur = img.getAttribute('src') || '';
          if (!cur || cur.startsWith('data:') || img.classList.contains('jdgm--loading')) {
            img.src = dataSrc;
          }
          img.loading = i < 6 ? 'eager' : 'lazy';
          img.classList.remove('jdgm--loading');
          img.closest('a')?.classList.remove('jdgm--loading');
        });
      },
      withJudgeMeWidth(url, width) {
        const u = String(url || '').replace(/&amp;/g, '&');
        if (!u) return '';
        if (/width=\d+/.test(u)) return u.replace(/width=\d+/, `width=${width}`);
        return `${u}${u.includes('?') ? '&' : '?'}width=${width}`;
      },
      renderHistogram() {
        // Flipkart mock 1 — no histogram
      },
      normalizePhotoItem(item) {
        if (!item) return null;
        // Lightbox uses 800px (not 1024) — sharper than thumbs, ~40% fewer bytes
        const LB = 800;
        if (typeof item === 'string') {
          const url = item.replace(/&amp;/g, '&');
          return {
            url: this.withJudgeMeWidth(url, 160),
            full: this.withJudgeMeWidth(url, LB),
            score: 0,
            author: '',
            body: '',
            title: '',
            datetime: '',
            verified: false,
            helpful: 0
          };
        }
        const url = String(item.url || item.full || '').replace(/&amp;/g, '&');
        if (!url) return null;
        return {
          url: this.withJudgeMeWidth(url, 160),
          full: this.withJudgeMeWidth(String(item.full || url), LB),
          reviewId: item.reviewId || '',
          score: Number(item.score) || 0,
          author: item.author || '',
          body: item.body || '',
          title: item.title || '',
          datetime: item.datetime || '',
          verified: !!item.verified,
          helpful: Number(item.helpful) || 0
        };
      },
      formatLightboxDate(raw) {
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      },
      extractPhotoFromReview(rev, link) {
        if (!rev) return null;
        const href =
          link?.getAttribute('data-mfp-src') ||
          link?.getAttribute('href') ||
          link?.querySelector?.('img')?.getAttribute('data-src') ||
          link?.querySelector?.('img')?.getAttribute('src') ||
          '';
        const full = String(href).replace(/&amp;/g, '&');
        if (!full) return null;
        const bodyEl = rev.querySelector('.jdgm-rev__body');
        const readMore = bodyEl?.querySelector('.jdgm-rev__body-read-more, .jdgm-myd-read-more');
        const body = (
          rev.dataset.mydFullText ||
          bodyEl?.dataset?.mydFullText ||
          (bodyEl?.textContent || '')
            .replace(readMore?.textContent || '', '')
            .replace(/\s+/g, ' ')
            .trim()
        )
          .replace(/\s+/g, ' ')
          .trim();
        const ts =
          rev.querySelector('.jdgm-myd-rev-date, .jdgm-rev__timestamp') ||
          null;
        return this.normalizePhotoItem({
          url: this.withJudgeMeWidth(full, 160),
          full: this.withJudgeMeWidth(full, 800),
          reviewId: rev.getAttribute('data-review-id') || '',
          score: this.reviewScore(rev),
          author: (
            rev.querySelector('.jdgm-myd-rev-author__name')?.textContent ||
            rev.querySelector('.jdgm-rev__author')?.textContent ||
            ''
          ).trim(),
          body,
          title: (rev.querySelector('.jdgm-rev__title')?.textContent || '').trim(),
          datetime:
            ts?.getAttribute('datetime') ||
            ts?.getAttribute('data-content') ||
            '',
          verified:
            rev.getAttribute('data-verified-buyer') === 'true' ||
            !!rev.querySelector('.jdgm-rev__buyer-badge'),
          helpful: Number(rev.getAttribute('data-thumb-up-count') || 0)
        });
      },
      collectDomPhotos(root) {
        const photos = [];
        const seen = new Set();
        const pushFrom = (rev, link) => {
          const item = rev ? this.extractPhotoFromReview(rev, link) : null;
          if (!item?.full || seen.has(item.full)) {
            // Native Judge.me media grid thumbs (no parent .jdgm-rev)
            if (!rev && link) {
              const href =
                link.getAttribute('data-mfp-src') ||
                link.getAttribute('href') ||
                link.querySelector?.('img')?.getAttribute('data-src') ||
                link.querySelector?.('img')?.getAttribute('src') ||
                '';
              const full = this.withJudgeMeWidth(String(href).replace(/&amp;/g, '&'), 800);
              if (!full || !/review-images\.judgeme\.com/.test(full) || seen.has(full)) return;
              seen.add(full);
              photos.push(
                this.normalizePhotoItem({
                  url: this.withJudgeMeWidth(full, 160),
                  full
                })
              );
            }
            return;
          }
          seen.add(item.full);
          photos.push(item);
        };
        (root || document).querySelectorAll?.('.jdgm-rev[data-review-id]').forEach((rev) => {
          rev.querySelectorAll('a.jdgm-rev__pic-link').forEach((link) => pushFrom(rev, link));
        });
        (root || document)
          .querySelectorAll?.('.jdgm-gallery__thumbnail-link, .jdgm-row-media a[href*="review-images"]')
          .forEach((link) => pushFrom(null, link));
        return photos;
      },
      getPhotoAlbum(root) {
        const productId =
          root?.getAttribute?.('data-id') ||
          root?.getAttribute?.('data-product-id') ||
          root?.closest?.('[data-id]')?.getAttribute('data-id') ||
          document.querySelector('.jdgm-preview-badge')?.getAttribute('data-id') ||
          '';
        const cached = productId ? this._mediaCache[productId]?.photos : null;
        if (cached?.length) return cached.map((p) => this.normalizePhotoItem(p)).filter(Boolean);
        const domPhotos = this.collectDomPhotos(root);
        if (domPhotos.length) return domPhotos;
        return [];
      },
      renderGallery(host, urls, photos) {
        const photoItems = (photos?.length ? photos : urls || [])
          .map((item) => this.normalizePhotoItem(item))
          .filter(Boolean);
        if (!host || !photoItems.length) {
          host?.querySelectorAll('.jdgm-myd-gallery').forEach((n) => n.remove());
          return;
        }
        host.querySelectorAll('.jdgm-myd-gallery').forEach((n, i) => {
          if (i > 0) n.remove();
        });
        let el = host.querySelector('.jdgm-myd-gallery');
        if (!el) {
          el = document.createElement('div');
          el.className = 'jdgm-myd-gallery';
          el.setAttribute('role', 'list');
          host.appendChild(el);
        }

        const total = photoItems.length;
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        // Mobile Flipkart mosaic: 1 large + 2×2 (5 cells) when we have 4+ photos
        const cellCount = Math.min(total, isMobile ? 5 : 8);
        const shown = photoItems.slice(0, cellCount);
        const useMosaic = isMobile && cellCount >= 4;
        el.classList.toggle('is-mosaic-5', useMosaic);
        el.classList.toggle('is-mosaic-3', isMobile && cellCount === 3);
        el.classList.toggle('jdgm-myd-collapse-body', true);
        el.dataset.photoCount = String(total);
        el.style.setProperty('--jdgm-myd-gallery-cols', String(cellCount));
        // Clear any prior inline layout locks so mobile mosaic CSS can win
        el.style.removeProperty('display');
        el.style.removeProperty('flex-direction');
        el.style.removeProperty('flex-wrap');
        el.style.removeProperty('width');

        el.innerHTML = shown
          .map((photo, i) => {
            const thumb = this.withJudgeMeWidth(photo.url || photo.full, 320);
            const isLast = i === shown.length - 1;
            // Flipkart: last cell shows +N remaining beyond the clear thumbs
            const extra = total > cellCount && isLast ? total - (cellCount - 1) : 0;
            const overlay =
              extra > 0
                ? `<span class="jdgm-myd-gallery__more" aria-hidden="true">+${extra}</span>`
                : '';
            const eager = i < 2;
            return `<button type="button" class="jdgm-myd-gallery__item" role="listitem" aria-label="Customer photo ${i + 1}${extra ? `, ${extra} more` : ''}" data-full="${photo.full}" data-index="${i}">
                <img src="${thumb}" alt="" loading="${eager ? 'eager' : 'lazy'}"${eager && i === 0 ? ' fetchpriority="high"' : ''} width="320" height="320" decoding="async">
                ${overlay}
              </button>`;
          })
          .join('');
        el.dataset.photoList = JSON.stringify(photoItems);
        // Warm lightbox sizes for the first couple of mosaic cells
        shown.slice(0, 2).forEach((photo) => {
          const u = photo.full;
          if (!u) return;
          if (!this._lbPrefetch) this._lbPrefetch = new Set();
          if (this._lbPrefetch.has(u)) return;
          this._lbPrefetch.add(u);
          const warm = new Image();
          warm.decoding = 'async';
          warm.src = u;
        });
        el.onclick = (e) => {
          const item = e.target.closest('.jdgm-myd-gallery__item');
          if (!item) return;
          e.preventDefault();
          el.querySelectorAll('.jdgm-myd-gallery__item').forEach((n) => n.classList.remove('is-active'));
          item.classList.add('is-active');
          let list = [];
          try {
            list = JSON.parse(el.dataset.photoList || '[]');
          } catch (err) {
            list = photoItems;
          }
          this.openLightbox(list, Number(item.dataset.index) || 0);
        };
      },
      openLightbox(images, startIndex) {
        const photos = (images || []).map((item) => this.normalizePhotoItem(item)).filter(Boolean);
        if (!photos.length) return;
        let index = Math.max(0, Math.min(startIndex || 0, photos.length - 1));
        let fullscreen = false;

        let box = document.querySelector('.jdgm-myd-lightbox');
        if (box && (!box.querySelector('[data-myd-lb-fs]') || !box.querySelector('.jdgm-myd-lightbox__stage'))) {
          box.remove();
          box = null;
        }
        if (!box) {
          box = document.createElement('div');
          box.className = 'jdgm-myd-lightbox';
          box.setAttribute('role', 'dialog');
          box.setAttribute('aria-modal', 'true');
          box.setAttribute('aria-label', 'Customer review photo');
          box.innerHTML = `
            <div class="jdgm-myd-lightbox__backdrop" data-myd-lb-close></div>
            <div class="jdgm-myd-lightbox__stage">
              <button type="button" class="jdgm-myd-lightbox__close myd-popup-close" data-myd-lb-close aria-label="Close">${window.MYD_POPUP_CLOSE_ICON}</button>
              <div class="jdgm-myd-lightbox__card">
                <div class="jdgm-myd-lightbox__media">
                  <span class="jdgm-myd-lightbox__counter" aria-live="polite"></span>
                  <button type="button" class="jdgm-myd-lightbox__fs" data-myd-lb-fs aria-label="View fullscreen">
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm0-4h2V7h3V5H5v5h2zm10 7h-3v2h5v-5h-2v3zm-3-9V5h5v5h-2V7h-3z"/></svg>
                  </button>
                  <button type="button" class="jdgm-myd-lightbox__nav jdgm-myd-lightbox__nav--prev" aria-label="Previous">&#8249;</button>
                  <button type="button" class="jdgm-myd-lightbox__img-btn" aria-label="View image fullscreen">
                    <img class="jdgm-myd-lightbox__img" alt="Customer photo">
                  </button>
                  <button type="button" class="jdgm-myd-lightbox__nav jdgm-myd-lightbox__nav--next" aria-label="Next">&#8250;</button>
                </div>
                <div class="jdgm-myd-lightbox__details">
                  <div class="jdgm-myd-lightbox__rating-row">
                    <div class="jdgm-myd-lightbox__stars" aria-hidden="true"></div>
                    <span class="jdgm-myd-lightbox__verified">
                      ${window.MYD_VERIFIED_BADGE_ICON}
                      Verified customer
                    </span>
                  </div>
                  <p class="jdgm-myd-lightbox__body"></p>
                  <div class="jdgm-myd-lightbox__footer">
                    <div class="jdgm-myd-lightbox__author-block">
                      <strong class="jdgm-myd-lightbox__author"></strong>
                      <span class="jdgm-myd-lightbox__date"></span>
                    </div>
                    <div class="jdgm-myd-lightbox__helpful">
                      <span class="jdgm-myd-lightbox__helpful-icon" aria-hidden="true">👍</span>
                      <span class="jdgm-myd-lightbox__helpful-count"></span>
                      <span class="jdgm-myd-lightbox__helpful-label">People found this helpful</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>`;
          document.body.appendChild(box);
        }

        const img = box.querySelector('.jdgm-myd-lightbox__img');
        const counter = box.querySelector('.jdgm-myd-lightbox__counter');
        const stars = box.querySelector('.jdgm-myd-lightbox__stars');
        const verified = box.querySelector('.jdgm-myd-lightbox__verified');
        const body = box.querySelector('.jdgm-myd-lightbox__body');
        const author = box.querySelector('.jdgm-myd-lightbox__author');
        const dateEl = box.querySelector('.jdgm-myd-lightbox__date');
        const helpful = box.querySelector('.jdgm-myd-lightbox__helpful');
        const helpfulCount = box.querySelector('.jdgm-myd-lightbox__helpful-count');
        const prevBtn = box.querySelector('.jdgm-myd-lightbox__nav--prev');
        const nextBtn = box.querySelector('.jdgm-myd-lightbox__nav--next');
        const fsBtn = box.querySelector('[data-myd-lb-fs]');
        const imgBtn = box.querySelector('.jdgm-myd-lightbox__img-btn');

        const setFullscreen = (on) => {
          fullscreen = !!on;
          box.classList.toggle('is-fullscreen', fullscreen);
          if (fsBtn) {
            fsBtn.setAttribute('aria-label', fullscreen ? 'Exit fullscreen' : 'View fullscreen');
          }
        };

        const render = () => {
          const photo = photos[index];
          const full = photo.full || photo.url;
          const preview = this.withJudgeMeWidth(photo.url || photo.full, 320);
          const token = `${index}:${full}`;
          img.dataset.mydLbToken = token;
          img.alt = photo.author ? `${photo.author} review photo` : 'Customer photo';
          // Show mosaic/thumb size immediately, then upgrade to lightbox size
          if (preview && preview !== full) {
            img.src = preview;
          }
          const hi = new Image();
          hi.decoding = 'async';
          hi.onload = () => {
            if (img.dataset.mydLbToken === token) img.src = full;
          };
          hi.onerror = () => {
            if (img.dataset.mydLbToken === token && img.src !== full) img.src = full;
          };
          hi.src = full;
          // Prefetch neighbors so swipe feels instant
          if (!this._lbPrefetch) this._lbPrefetch = new Set();
          [index - 1, index + 1].forEach((ni) => {
            if (ni < 0 || ni >= photos.length) return;
            const u = photos[ni].full || photos[ni].url;
            if (!u || this._lbPrefetch.has(u)) return;
            this._lbPrefetch.add(u);
            const pre = new Image();
            pre.decoding = 'async';
            pre.src = u;
          });
          counter.textContent = `${index + 1} / ${photos.length}`;
          const score = Math.max(0, Math.min(5, photo.score || 0));
          stars.innerHTML = Array.from({ length: 5 }, (_, i) =>
            `<span class="jdgm-myd-lightbox__star${i < score ? ' is-on' : ''}">★</span>`
          ).join('');
          stars.setAttribute('aria-label', score ? `${score} star review` : 'Review rating');
          verified.hidden = !photo.verified;
          if (verified && !verified.hidden) {
            const label = verified.childNodes[verified.childNodes.length - 1];
            if (label && label.nodeType === Node.TEXT_NODE) {
              label.textContent = ' Verified customer';
            } else if (!/verified customer/i.test(verified.textContent || '')) {
              verified.insertAdjacentText('beforeend', ' Verified customer');
            }
          }
          const text = [photo.title, photo.body].filter(Boolean).join(' — ');
          body.textContent = text || 'Customer photo';
          author.textContent = photo.author || 'Customer';
          dateEl.textContent = this.formatLightboxDate(photo.datetime) || '';
          if (photo.helpful > 0) {
            helpful.hidden = false;
            helpfulCount.textContent = `(${photo.helpful})`;
          } else {
            helpful.hidden = true;
          }
          const multi = photos.length > 1;
          prevBtn.hidden = !multi;
          nextBtn.hidden = !multi;
        };
        const close = () => {
          setFullscreen(false);
          box.classList.remove('is-open');
          document.documentElement.classList.remove('jdgm-myd-lightbox-open');
          document.removeEventListener('keydown', onKey);
        };
        const onKey = (ev) => {
          if (ev.key === 'Escape') {
            if (fullscreen) setFullscreen(false);
            else close();
          } else if (ev.key === 'ArrowLeft' && photos.length > 1) {
            index = (index - 1 + photos.length) % photos.length;
            render();
          } else if (ev.key === 'ArrowRight' && photos.length > 1) {
            index = (index + 1) % photos.length;
            render();
          }
        };

        prevBtn.onclick = (ev) => {
          ev.stopPropagation();
          index = (index - 1 + photos.length) % photos.length;
          render();
        };
        nextBtn.onclick = (ev) => {
          ev.stopPropagation();
          index = (index + 1) % photos.length;
          render();
        };
        if (fsBtn) {
          fsBtn.onclick = (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            setFullscreen(!fullscreen);
          };
        }
        if (imgBtn) {
          imgBtn.onclick = (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            setFullscreen(!fullscreen);
          };
        }
        box.onclick = (ev) => {
          if (ev.target.closest('[data-myd-lb-close]')) {
            ev.preventDefault();
            ev.stopPropagation();
            // Fullscreen → back to card lightbox; card lightbox → dismiss
            if (fullscreen) setFullscreen(false);
            else close();
            return;
          }
          // Outside the card (backdrop / empty area)
          if (!ev.target.closest('.jdgm-myd-lightbox__card')) {
            if (fullscreen) setFullscreen(false);
            else close();
          }
        };

        setFullscreen(false);
        render();
        box.classList.add('is-open');
        document.documentElement.classList.add('jdgm-myd-lightbox-open');
        document.addEventListener('keydown', onKey);
      },
      bindReviewPhotoLightbox(root) {
        if (root) root.dataset.mydPhotoLb = '1';
        if (this._reviewPhotoLbBound) return;
        this._reviewPhotoLbBound = true;
        document.addEventListener(
          'click',
          (event) => {
            const link = event.target.closest?.(
              'a.jdgm-rev__pic-link, .jdgm-rev__pics a[href], .jdgm-rev__pics a[data-mfp-src]'
            );
            if (!link) return;
            const rev = link.closest('.jdgm-rev');
            if (!rev) return;
            if (
              !rev.closest(
                '#myd-jdgm-reviews-drawer, .jdgm-myd-in-drawer, .jdgm-rev-widg, #judgeme_product_reviews'
              )
            ) {
              return;
            }

            event.preventDefault();
            event.stopPropagation();

            const current = this.extractPhotoFromReview(rev, link);
            if (!current) return;

            const widget =
              this._drawerRoot ||
              rev.closest('.jdgm-rev-widg') ||
              document.querySelector('#judgeme_product_reviews .jdgm-rev-widg, .jdgm-rev-widg');

            let album = this.getPhotoAlbum(widget);
            if (!album.length) album = this.collectDomPhotos(widget) || [];
            if (!album.length) album = [current];

            let index = album.findIndex(
              (p) =>
                p.full === current.full ||
                (p.url && current.url && p.url.split('?')[0] === current.url.split('?')[0])
            );
            if (index < 0 && current.reviewId) {
              index = album.findIndex((p) => p.reviewId === current.reviewId);
            }
            if (index < 0) {
              album = [current, ...album];
              index = 0;
            } else {
              // Prefer live review details (title/body/author) from the clicked card
              album[index] = { ...album[index], ...current, full: album[index].full || current.full };
            }
            this.openLightbox(album, index);
          },
          true
        );
      },
      async fetchMediaSummary(productId, { onProgress, force } = {}) {
        const id = String(productId || '');
        if (!id) return null;
        const cachedMem = this._mediaCache[id];
        // Sparse caches (<5 photos) are not final — Flipkart mosaic needs 4+ for grid + count
        const cacheComplete =
          cachedMem && !cachedMem.partial && (cachedMem.photos?.length || 0) >= 5;
        if (!force && cacheComplete) {
          this.indexReviewPhotos(cachedMem.photos);
          onProgress?.(cachedMem);
          return cachedMem;
        }
        if (this._mediaFetchPromises[id]) return this._mediaFetchPromises[id];

        const shop =
          window.Shopify?.shop ||
          window.jdgm?.SHOP_DOMAIN ||
          window.jdgmSettings?.shop_domain ||
          window.location.hostname.replace(/^www\./, '');
        if (!shop) return null;

        const cacheKey = `myd-jdgm-media:v9:${id}`;
        if (!force) {
          try {
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
              const parsed = JSON.parse(cached);
              const n = parsed?.photos?.length || parsed?.pics?.length || 0;
              // Ignore thin caches left by early DOM/API paints
              if (n >= 5) {
                Object.assign(this._reviewExcerpts, parsed.excerpts || {});
                this._mediaCache[id] = parsed;
                this.indexReviewPhotos(parsed.photos);
                onProgress?.(parsed);
                return parsed;
              }
            }
          } catch (e) {
            /* ignore */
          }
        }

        const hist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        const pics = [];
        const photos = [];
        const seenPhotos = new Set();
        const excerpts = {};
        let total = 0;

        const emit = (partial) => {
          const counted = Object.values(hist).reduce((a, b) => a + b, 0);
          const snapshot = {
            hist: { ...hist },
            pics: pics.slice(),
            photos: photos.slice(),
            excerpts: { ...excerpts },
            total: total || counted,
            partial: !!partial
          };
          Object.assign(this._reviewExcerpts, excerpts);
          this._mediaCache[id] = snapshot;
          this.indexReviewPhotos(snapshot.photos);
          onProgress?.(snapshot);
          return snapshot;
        };

        const ingestHtml = (html) => {
          const probe = document.createElement('div');
          probe.innerHTML = html;
          probe.querySelectorAll('.jdgm-rev[data-review-id]').forEach((review) => {
            const reviewId = review.getAttribute('data-review-id');
            const fullText = (review.querySelector('.jdgm-rev__body')?.textContent || '')
              .replace(/\s+/g, ' ')
              .trim();
            if (reviewId && fullText) {
              this._reviewBodies[reviewId] = fullText;
              const sentence = fullText.match(/^.*?[.!?]+(?=\s|$)/)?.[0] || fullText;
              excerpts[reviewId] = sentence.slice(0, 180).trim();
            }

            const score =
              Number.parseInt(review.querySelector('.jdgm-rev__rating')?.getAttribute('data-score') || '', 10) ||
              0;
            const author = (review.querySelector('.jdgm-rev__author')?.textContent || '').trim();
            const title = (review.querySelector('.jdgm-rev__title')?.textContent || '').trim();
            const datetime =
              review.querySelector('.jdgm-rev__timestamp')?.getAttribute('datetime') ||
              review.querySelector('.jdgm-rev__timestamp')?.getAttribute('data-content') ||
              '';
            const verified =
              review.getAttribute('data-verified-buyer') === 'true' ||
              !!review.querySelector('.jdgm-rev__buyer-badge');
            const helpful = Number(review.getAttribute('data-thumb-up-count') || 0);

            review.querySelectorAll('a.jdgm-rev__pic-link').forEach((link) => {
              const fullRaw =
                link.getAttribute('data-mfp-src') ||
                link.getAttribute('href') ||
                link.querySelector('img')?.getAttribute('data-src') ||
                link.querySelector('img')?.getAttribute('src') ||
                '';
              let full = String(fullRaw).replace(/&amp;/g, '&');
              if (!full || !/review-images\.judgeme\.com/.test(full)) return;
              full = this.withJudgeMeWidth(full, 800);
              if (seenPhotos.has(full)) return;
              seenPhotos.add(full);
              const thumb = this.withJudgeMeWidth(full, 160);
              pics.push(thumb);
              photos.push({
                url: thumb,
                full,
                reviewId: reviewId || '',
                score,
                author,
                body: fullText,
                title,
                datetime,
                verified,
                helpful
              });
            });
          });

          let pageScores = 0;
          for (const m of html.matchAll(/data-score=['"](\d)['"]/g)) {
            const star = Number(m[1]);
            if (hist[star] != null) hist[star] += 1;
            pageScores += 1;
          }

          for (const m of html.matchAll(/https:\/\/review-images\.judgeme\.com\/[^"'\\\s>]+/g)) {
            const img = m[0].replace(/&amp;/g, '&');
            const full = this.withJudgeMeWidth(img, 800);
            if (seenPhotos.has(full)) continue;
            const thumb = this.withJudgeMeWidth(img, 160);
            seenPhotos.add(full);
            pics.push(thumb);
            photos.push({
              url: thumb,
              full,
              reviewId: '',
              score: 0,
              author: '',
              body: '',
              title: '',
              datetime: '',
              verified: false,
              helpful: 0
            });
          }
          return pageScores;
        };

        const run = async () => {
          try {
            // Prefer CDN (same host as badge hydrate). sort_by=with_media is required —
            // default sort returns empty .jdgm-rev__pics, so the mosaic stayed at 3 DOM thumbs.
            const maxPages = 4;
            for (let page = 1; page <= maxPages; page += 1) {
              const params = new URLSearchParams({
                url: shop,
                shop_domain: shop,
                platform: 'shopify',
                page: String(page),
                per_page: '50',
                product_id: id,
                sort_by: 'with_media',
                review_type: 'product-reviews'
              });
              const url = `https://cdn.judge.me/reviews/reviews_for_widget?${params}`;
              const res = await fetch(url, { credentials: 'omit' });
              if (!res.ok) break;
              const data = await res.json();
              const html = data.html || '';
              if (!html) break;
              // with_media total_count = reviews-with-photos; keep for pagination only
              if (page === 1 && data.total_count) total = Number(data.total_count) || 0;

              const pageScores = ingestHtml(html);
              const countedSoFar = Object.values(hist).reduce((a, b) => a + b, 0);
              // Paint after every page so mosaic upgrades 3 → 5+ with +N as soon as we have enough
              emit(true);
              if (pageScores === 0) break;
              if (total && countedSoFar >= total) break;
              if (photos.length >= 40) break;
            }
          } catch (e) {
            if (!photos.length) return null;
          }

          const summary = emit(false);
          delete summary.partial;
          this._mediaCache[id] = summary;
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(summary));
          } catch (e) {
            /* ignore */
          }
          return summary;
        };

        this._mediaFetchPromises[id] = run().finally(() => {
          delete this._mediaFetchPromises[id];
        });
        return this._mediaFetchPromises[id];
      },
      async styleMedia(root) {
        if (!root) return;
        const header = root.querySelector('.jdgm-rev-widg__header');
        let host = this.consolidateStarsRow(root) || root.querySelector('.jdgm-row-stars');
        if (!host && header) {
          host = document.createElement('div');
          host.className = 'jdgm-row-stars';
          header.appendChild(host);
        }
        if (!host) return;

        header?.querySelectorAll(':scope > .jdgm-myd-gallery').forEach((n) => {
          host.appendChild(n);
        });
        // Remove legacy histograms from Flipkart skin
        host.querySelectorAll('.jdgm-myd-histogram, .jdgm-histogram').forEach((n) => n.remove());

        if (host.dataset.mydMedia === 'loading') return;

        const productId =
          root.getAttribute('data-id') ||
          root.getAttribute('data-product-id') ||
          root.closest('[data-id]')?.getAttribute('data-id') ||
          document.querySelector('.jdgm-preview-badge')?.getAttribute('data-id');

        const paint = (summary) => {
          if (!summary?.photos?.length && !summary?.pics?.length) return;
          this.indexReviewPhotos(summary.photos);
          this.renderGallery(host, summary.pics, summary.photos);
          host.dataset.mydMedia = '1';
          this.styleReviews(root);
        };

        const existingGallery = host.querySelector('.jdgm-myd-gallery');
        const galleryCount = existingGallery?.querySelectorAll('.jdgm-myd-gallery__item').length || 0;
        const cachedCount = this._mediaCache[productId]?.photos?.length || 0;
        const fetchDone =
          !!this._mediaCache[productId] && !this._mediaCache[productId].partial && cachedCount >= 5;

        if (existingGallery && galleryCount) {
          this.styleReviews(root);
          this.bindCollapse(root);
          this.bindReviewPhotoLightbox(root);
          host.dataset.mydMedia = '1';

          // Upgrade sparse 3-cell gallery → Flipkart 5-cell + count once API returns more
          if (cachedCount > galleryCount) {
            paint(this._mediaCache[productId]);
          }
          if (productId && !fetchDone && !this._mediaFetchPromises[productId]) {
            this.fetchMediaSummary(productId, {
              force: galleryCount < 5,
              onProgress: paint
            }).then((summary) => paint(summary));
          }
          return;
        }

        host.dataset.mydMedia = 'loading';
        this.hydrateReviewPics(root);

        // Instant mosaic from already-rendered review cards while API pages load
        const domPhotos = this.collectDomPhotos(root);
        if (domPhotos.length) {
          this.renderGallery(host, null, domPhotos);
          host.dataset.mydMedia = '1';
        }

        const summary = await this.fetchMediaSummary(productId, {
          force: (domPhotos.length || 0) < 5,
          onProgress: paint
        });
        if (!summary && !host.querySelector('.jdgm-myd-gallery__item')) {
          host.dataset.mydMedia = '';
          return;
        }
        // Merge any DOM-only pics the API might have missed on early pages
        if (summary?.photos?.length && domPhotos.length) {
          const seen = new Set(summary.photos.map((p) => p.full));
          domPhotos.forEach((p) => {
            if (p.full && !seen.has(p.full)) {
              summary.photos.push(p);
              summary.pics.push(p.url || p.full);
            }
          });
        }
        paint(summary);
        this.styleReviews(root);
        this.bindCollapse(root);
        this.bindReviewPhotoLightbox(root);
        host.dataset.mydMedia = '1';
      },
      observeReviews(root) {
        if (!root || root.dataset.mydObs === '1') return;
        const body =
          root.querySelector('.jdgm-rev-widg__reviews') ||
          document.querySelector('#myd-jdgm-reviews-drawer .jdgm-rev-widg__reviews');
        if (!body) return;
        root.dataset.mydObs = '1';
        let timer = null;
        const mo = new MutationObserver((mutations) => {
          if (root.dataset.mydStyling === '1') return;
          const hasNewReviews = mutations.some(
            (mutation) =>
              mutation.type === 'childList' &&
              [...mutation.addedNodes].some(
                (node) =>
                  node.nodeType === 1 &&
                  (node.matches?.('.jdgm-rev') || node.querySelector?.('.jdgm-rev'))
              )
          );
          if (!hasNewReviews) return;
          clearTimeout(timer);
          timer = setTimeout(() => {
            this._runWithoutObs(root, () => {
              this.styleReviews(root);
              this.setLoadMoreLabel(root);
              const drawer = document.getElementById('myd-jdgm-reviews-drawer');
              if (drawer && !drawer.hidden) {
                this._drawerLoading = false;
                drawer.classList.remove('is-loading');
              }
            });
          }, 50);
        });
        mo.observe(body, { childList: true, subtree: true });
        this._reviewsMO = mo;
      },
      styleWidget(widget) {
        if (!widget) return;
        const root = widget.classList?.contains('jdgm-rev-widg')
          ? widget
          : widget.querySelector('.jdgm-rev-widg') || widget;
        if (!root) return;
        // Judge.me ships display:none via .jdgm-temp-hiding-style — strip ASAP
        root.querySelectorAll('.jdgm-temp-hiding-style').forEach((el) => el.remove());
        widget.querySelectorAll?.('.jdgm-temp-hiding-style')?.forEach((el) => el.remove());
        root.classList.remove('is-collapsed');
        root.classList.add('jdgm-myd-ready');
        this._runWithoutObs(root, () => {
          try {
            this.styleSummary(root);
            this.styleReviews(root);
            this.setLoadMoreLabel(root);
            this.dedupeReviewForms(root);
            this.bindReviewPhotoLightbox(root);
          } finally {
            root.classList.add('jdgm-myd-ready');
            root.querySelectorAll('.jdgm-temp-hiding-style').forEach((el) => el.remove());
          }
        });
        this.observeReviews(root);
        this.styleMedia(root);
        this.bindWriteReviewFix(root);
        if (!root.dataset.mydRetry) {
          root.dataset.mydRetry = '1';
          setTimeout(() => {
            this._runWithoutObs(root, () => {
              try {
                this.styleSummary(root);
                this.styleReviews(root);
                this.setLoadMoreLabel(root);
              } finally {
                root.classList.add('jdgm-myd-ready');
              }
            });
            this.styleMedia(root);
          }, 800);
          setTimeout(() => {
            this._runWithoutObs(root, () => {
              try {
                this.styleSummary(root);
                this.styleReviews(root);
                this.setLoadMoreLabel(root);
              } finally {
                root.classList.add('jdgm-myd-ready');
              }
            });
            this.styleMedia(root);
          }, 2200);
        }
      },
      enhancePaginationOnly() {
        const seen = new Set();
        document
          .querySelectorAll('#judgeme_product_reviews, .jdgm-review-widget, .jdgm-rev-widg')
          .forEach((widget) => {
            const root = widget.classList?.contains('jdgm-rev-widg')
              ? widget
              : widget.querySelector('.jdgm-rev-widg') || widget;
            if (!root || seen.has(root)) return;
            seen.add(root);
            this.setupPagination(widget);
            this.styleWidget(widget);
          });
      },
      enhance() {
        this.placeBadgeUnderTitle();
        document.querySelectorAll('.jdgm-preview-badge').forEach((badge) => {
          this.syncPreviewBadge(badge);
          const id = badge.getAttribute('data-id') || badge.getAttribute('data-product-id');
          if (id) this.setProductId(badge, id);
        });
        const seen = new Set();
        document
          .querySelectorAll('#judgeme_product_reviews, .jdgm-review-widget, .jdgm-rev-widg')
          .forEach((widget) => {
            const root = widget.classList?.contains('jdgm-rev-widg')
              ? widget
              : widget.querySelector('.jdgm-rev-widg') || widget;
            if (!root || seen.has(root)) return;
            seen.add(root);
            const id = root.getAttribute('data-id') || root.getAttribute('data-product-id') ||
              widget.getAttribute('data-id') || widget.getAttribute('data-product-id');
            if (id) this.setProductId(root, id);
            root.querySelectorAll('.jdgm-temp-hiding-style').forEach((el) => el.remove());
            this.setupPagination(widget);
            this.styleWidget(root);
          });
      },
      scheduleEnhance() {
        if (this._enhanceScheduled) return;
        this._enhanceScheduled = true;
        const run = () => {
          this._enhanceScheduled = false;
          // Always clear Judge.me's FOUC hide, even before their JS is ready
          document.querySelectorAll('.jdgm-temp-hiding-style').forEach((el) => el.remove());
          if (!window.jdgm?._setupLoadReviewsEventsFor) {
            this.placeBadgeUnderTitle();
            document.querySelectorAll('.jdgm-preview-badge').forEach((b) => this.syncPreviewBadge(b));
            document.querySelectorAll('.jdgm-rev-widg').forEach((w) => this.styleWidget(w));
            return;
          }
          this.enhance();
        };
        // Run ASAP — idle delay caused a visible stock Judge.me flash
        if (typeof queueMicrotask === 'function') queueMicrotask(run);
        else setTimeout(run, 0);
      },
      watchForWidget() {
        if (this._widgetWatcher) return;
        this._widgetWatcher = true;
        const kick = () => {
          const pending = [...document.querySelectorAll('.jdgm-rev-widg')].some(
            (el) => !el.classList.contains('jdgm-myd-ready')
          );
          const host =
            document.querySelector('#judgeme_product_reviews, .jdgm-review-widget') &&
            !document.querySelector('.jdgm-rev-widg');
          if (pending || host) this.scheduleEnhance();
        };
        kick();
        const mo = new MutationObserver(() => kick());
        mo.observe(document.documentElement, { childList: true, subtree: true });
        // Safety: never leave reviews permanently invisible; stop watching
        setTimeout(() => {
          try {
            mo.disconnect();
          } catch (err) {
            /* ignore */
          }
          document.querySelectorAll('.jdgm-rev-widg:not(.jdgm-myd-ready)').forEach((el) => {
            el.classList.add('jdgm-myd-ready');
          });
        }, 4000);
      }
    };

    customElements.define('variant-selects', VariantSelects);

    const bootJudgeMeEnhance = () => window.MYDJudgeMe?.watchForWidget?.() || window.MYDJudgeMe?.scheduleEnhance();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bootJudgeMeEnhance, { once: true });
    } else {
      bootJudgeMeEnhance();
    }
    setTimeout(bootJudgeMeEnhance, 400);
    setTimeout(bootJudgeMeEnhance, 1500);
  
    class VariantRadios extends VariantSelects {
      constructor() {
        super();
      }
  
      updateOptions() {
        const fieldsets = Array.from(this.querySelectorAll('fieldset'));
        this.options = fieldsets.map((fieldset) => {
          return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked)?.value;
        });
      }
  
      enableVariantOption(group, obj) {
        if (this.isLookOptionGroup(group)) {
          this.enableLookOptionGroups();
          return;
        }

        const value = obj.value.replace(/([ #;&,.+*~\':"!^$[\]()=>|\/@])/g,'\\$1');
        const input = group.querySelector('input[data-option-value="'+ value +'"]');
        if (!input) return;

        // Variant exists - enable & show variant
        input.removeAttribute('disabled');
        input.classList.remove('disabled');
  
        // Variant sold out — keep selectable so Notify Me can appear, but style as OOS
        if (obj.soldOut) {
          input.classList.add('disabled');
        }
      }
  
      disableVariantGroup(group) {
        if (this.isLookOptionGroup(group)) {
          this.enableLookOptionGroups();
          return;
        }

        group.querySelectorAll('input').forEach((input) => {
          input.setAttribute('disabled', '');
        });
      }
    }
    customElements.define('variant-radios', VariantRadios);
  
    class ProductForm extends HTMLElement {
      constructor() {
        super();   
  
        this.miniCart = document.querySelector('mini-cart');
        this.form = this.querySelector('form');
        const idInput = this.form.querySelector('[name=id]');
        if (idInput) {
          const sizeMissing = window.MYDSizeGate
            ? !window.MYDSizeGate.isSizeSelected()
            : !document.querySelector(
                'variant-radios fieldset.variant-input-wrapper:not([data-combined-listing-option="true"]) input[type="radio"]:checked'
              );
          if (sizeMissing && document.querySelector('variant-radios fieldset[data-option-slug="size"], variant-radios fieldset.variant-input-wrapper')) {
            idInput.value = '';
            idInput.disabled = true;
          } else {
            idInput.disabled = false;
          }
        }
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }

      isVariantSelectionComplete() {
        const variantRadios = document.querySelector('variant-radios');
        if (!variantRadios) return true;

        const fieldsets = Array.from(variantRadios.querySelectorAll('fieldset')).filter((fs) => {
          if (fs.dataset.combinedListingOption === 'true') return false;
          const slug = (fs.dataset.optionSlug || '').toLowerCase();
          if (slug.includes('look') || slug.includes('shop-by')) return false;
          return true;
        });
        if (fieldsets.length === 0) return true;

        return fieldsets.every((fs) => !!fs.querySelector('input:checked'));
      }

      showVariantSelectionError() {
        const message = 'Please select a size before adding to cart.';
        const fromSticky = !!this.closest?.('.sticky-cart-wrapper');
        if (window.MYDSizeGate?.showSizeWarning) {
          window.MYDSizeGate.showSizeWarning(message, {
            fromSticky: fromSticky,
            pending: fromSticky
              ? {
                  type: 'atc',
                  el: this.querySelector('[type="submit"], .product-form__submit, [name="add"]')
                }
              : null
          });
          return;
        }

        const alertDiv = document.getElementById('alert_message_product');
        if (alertDiv) {
          alertDiv.textContent = message;
          alertDiv.classList.remove('hidden');
          alertDiv.classList.add('is-visible');
        }

        this.handleErrorMessage(message);
      }
  
      onSubmitHandler(evt) {
        if (!this.isVariantSelectionComplete()) {
          evt.preventDefault();
          this.showVariantSelectionError();
          return;
        }

        if (document.body.classList.contains('template-cart') || !window.shopSettings.cartDrawer) {
          return;
        }
        
        evt.preventDefault();
        const submitButton = this.querySelector('[type="submit"]');
        if (submitButton.classList.contains('loading')) {
          return;
        }
  
        this.handleErrorMessage();
  
        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');
  
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
  
        const formData = new FormData(this.form);
  
        if (this.miniCart) {
          formData.append('sections', this.miniCart.getSectionsToRender().map((section) => section.section || section.id));
        }
        formData.append('sections_url', window.location.pathname);
        config.body = formData;
  
        fetch(`${window.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description);
              return;
            }
  
            if (this.miniCart) {
              this.miniCart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
          });
      }
  
      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        
        if (this.errorMessageWrapper) {
          this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
          this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
  
          if (errorMessage) {
            this.errorMessage.textContent = errorMessage;
          }
        }
        else {
          if (errorMessage) {
            alert(errorMessage);
          }
        }
      }
    }
    customElements.define('product-form', ProductForm);
    class AnimateSticky extends HTMLElement {
      constructor() {
        super();
      }
  
      connectedCallback() {
        if (this.classList.contains('sticky-cart-wrapper')) {
          this.initStickyCartObserver();
          return;
        }

        this.onScrollHandler = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScrollHandler, false);
        this.onScrollHandler();
      }
  
      disconnectedCallback() {
        if (this._stickyTriggerRetry) {
          clearInterval(this._stickyTriggerRetry);
          this._stickyTriggerRetry = null;
        }
        if (this._stickyAtcObserver) {
          this._stickyAtcObserver.disconnect();
          this._stickyAtcObserver = null;
        }
        if (this._stickyDomObserver) {
          this._stickyDomObserver.disconnect();
          this._stickyDomObserver = null;
        }
        if (this.onScrollHandler) {
          window.removeEventListener('scroll', this.onScrollHandler);
        }
        if (this._stickyResizeHandler) {
          window.removeEventListener('resize', this._stickyResizeHandler);
          this._stickyResizeHandler = null;
        }
      }

      initStickyCartObserver() {
        // Reveal only after the last *visible* inline CTA leaves the viewport.
        // Hidden Buy it now / empty payment wrappers report a 0-height box and
        // would keep the sticky bar on screen while Sold out + Notify Me are still in view.
        const findButtons = () =>
          Array.from(document.querySelectorAll('.product-form__buttons')).find(
            (el) => !el.closest('.sticky-cart-wrapper')
          );

        const isVisibleCta = (el) => {
          if (!(el instanceof HTMLElement)) return false;
          if (el.closest('.sticky-cart-wrapper')) return false;
          if (el.classList.contains('product-form__quantity')) return false;
          if (el.closest('.product-form__quantity, .product__wishlist, quantity-input')) return false;
          const cs = window.getComputedStyle(el);
          if (cs.display === 'none' || cs.visibility === 'hidden') return false;
          const rect = el.getBoundingClientRect();
          return rect.height > 2 && rect.width > 2;
        };

        const findTrigger = () => {
          const mainButtons = findButtons();
          if (!mainButtons) return null;

          const notify =
            mainButtons.querySelector('[data-myd-notify-cta="true"]') ||
            mainButtons.querySelector('.restock-alerts-notify-button, .PRODUCT_PAGE-notify-button') ||
            Array.from(mainButtons.querySelectorAll('button, a, [role="button"]')).find((el) =>
              /notify\s*me/i.test((el.textContent || '').replace(/\s+/g, ' '))
            );
          if (notify && isVisibleCta(notify)) return notify;

          const candidates = Array.from(
            mainButtons.querySelectorAll(
              '.shopify-payment-button, .product-form__submit[name="add"], .product-form__submit, button, a.button'
            )
          ).filter(isVisibleCta);
          return candidates[candidates.length - 1] || (isVisibleCta(mainButtons) ? mainButtons : null);
        };

        const isMobileSticky = () => window.matchMedia('(max-width: 749px)').matches;

        const update = () => {
          if (!isMobileSticky()) {
            this.reset();
            return;
          }
          const trigger = findTrigger();
          if (!trigger) {
            this.reset();
            return;
          }
          const rect = trigger.getBoundingClientRect();
          if (rect.height > 2 && rect.bottom <= 0) this.reveal();
          else this.reset();
        };

        const attach = (host) => {
          if (!host || this.onScrollHandler) return;

          if ('IntersectionObserver' in window) {
            this._stickyAtcObserver = new IntersectionObserver(() => update(), {
              threshold: [0, 0.01, 1],
              rootMargin: '0px',
            });
            this._stickyAtcObserver.observe(host);
          }

          this.onScrollHandler = update;
          window.addEventListener('scroll', this.onScrollHandler, { passive: true });
          this._stickyResizeHandler = update;
          window.addEventListener('resize', this._stickyResizeHandler, { passive: true });
          this._stickyDomObserver = new MutationObserver(() => update());
          this._stickyDomObserver.observe(host, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class', 'hidden'],
          });
          update();
        };

        const host = findButtons();
        if (host) {
          attach(host);
          return;
        }

        let attempts = 0;
        this._stickyTriggerRetry = setInterval(() => {
          attempts += 1;
          const t = findButtons();
          if (t || attempts > 40) {
            clearInterval(this._stickyTriggerRetry);
            this._stickyTriggerRetry = null;
            if (t) attach(t);
          }
        }, 250);
      }
  
      onScroll() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  
        if (scrollTop > this.getOffsetTop(this)) {
          window.requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.requestAnimationFrame(this.reset.bind(this));
        }
      }
  
      reveal() {
        this.setAttribute('animate', '');
        document.body.classList.add('myd-sticky-atc-visible');
      }
  
      reset() {
        this.removeAttribute('animate');
        document.body.classList.remove('myd-sticky-atc-visible');
      }
  
      getOffsetTop(element) {
        let offsetTop = 0;
        while(element) {
          offsetTop += element.offsetTop;
          element = element.offsetParent;
        }
        return offsetTop;
      }
    }
    customElements.define('animate-sticky', AnimateSticky);
